{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2024 - 2025                               }
{            Email : info@tmssoftware.com                            }
{            Web : https://www.tmssoftware.com                       }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.TMSFNCDataGrid;

interface

{$I WEBLib.TMSFNCDefines.inc}

{$IFDEF FMXLIB}
{$DEFINE SUPPORTABSTRACT}
{$ENDIF}
{$IFDEF VCLLIB}
{$DEFINE SUPPORTABSTRACT}
{$ENDIF}

uses
  {$IFDEF VCLLIB}
  Windows, Messages,
  {$ENDIF}
  Classes, SysUtils, Types, TMS.TMSFNCFilterBuilder, WEBLib.TMSFNCPersistence, WEBLib.TMSFNCJSONReader, WEBLib.TMSFNCJSONWriter, WEBLib.TMSFNCDataBinding, WEBLib.TMSFNCToolBar, WEBLib.TMSFNCRadioGroup,
  WEBLib.TMSFNCPopup, WEBLib.TMSFNCCustomControl, WEBLib.TMSFNCDataGridCell, WEBLib.TMSFNCDataGridRenderer, WEBLib.TMSFNCDataGridData, WEBLib.TMSFNCDataGridColumnsEditor, WEBLib.Graphics,
  WEBLib.TMSFNCBitmapContainer, WEBLib.TMSFNCGraphics, WEBLib.StdCtrls, WEBLib.TMSFNCScrollBar, WEBLib.TMSFNCDataGridCore, WEBLib.TMSFNCDataGridBase, WEBLib.ExtCtrls, WEBLib.TMSFNCButton,
  WEBLib.Menus, WEBLib.Controls, WEBLib.Forms, WEBLib.TMSFNCTypes, WEBLib.TMSFNCCustomComponent, WEBLib.TMSFNCGraphicsTypes
  ,Generics.Collections, Rtti, Generics.Defaults
  {$IFNDEF WEBLIB}
  ,UITypes
  {$ENDIF}
  {$IFDEF FMXLIB}
  ,FMX.Types, FMX.ListBox
  {$ENDIF}
  {$IFDEF SUPPORTABSTRACT}
  ,TMS.TMSFNCDataSet, Data.DB
  {$ENDIF}
  ;

const
  TTMSFNCDataGridDocURL = TTMSFNCBaseDocURL + 'tmsfncuipack/components/ttmsfncdatagrid/';
  {$IFDEF ANDROID}
  SCROLLINGDELAY = 40;
  {$ELSE}
  SCROLLINGDELAY = 0;
  {$ENDIF}

const
  MAJ_VER = 1; // Major version nr.
  MIN_VER = 5; // Minor version nr.
  REL_VER = 0; // Release nr.
  BLD_VER = 4; // Build nr.

  // version history
  // v0.9.0.0 : first beta release
  // v0.9.0.1 : Fixed: Access violation while expanding in connection with TTMSFNCDataGridDatabaseAdapter
  //          : Improved: Added GridColumn & ColumnAtField properties to TTMSFNCDataGridDatabaseAdapter to get easier access to grid column properties
  // v0.9.0.2 : New: gsmDisjunctCellRange mode to select items like in Windows Explorer (selection types & mode might change in future BETA updates)
  //          : Improved: Ability to mark filter items with TStringList objects in OnNeedFilterDropDown event (filter items might change in future BETA updates)
  //          :         : - Sample: AValues.AddObject(">= 10", TMSFNCDataGrid1.Filter.CommonDoubleQuotedItem);
  //          :         : - 1) CommonItem (counts towards global check all item)
  //          :         : - 2) CommonDoubleQuotedItem (counts towards global check all item, double quoted)
  //          :         : - 3) UniqueItem (does not count towards global check all item)
  //          :         : - 4) UniqueDoubleQuotedItem (does not count towards global check all item, double quoted)
  //          : Improved: Removed DataGrid property to avoid circular references in TTMSFNCDataGridDatabaseAdapter
  //          : Fixed: Issue with mouse wheel scrolling in combination with sub grid in VCL
  //          : Fixed: Issue updating sub-grid in VCL
  // v0.9.1.0 : New: Sides property at cell Layout
  //          : New: Options.URL.AutoDetect (automatically detects HTTP, HTTPS, FTP, FILE, WWW, MAILTO as links)
  //          : New: Options.URL.AutoOpen (automatically opens URLs in the default application)
  //          : Fixed: Issue with progressbar rendering in VCL
  //          : Fixed: Issue toggling Booleans property in OnCellCheckChanged event
  //          : Fixed: Issue handling mouse wheel in detail grid in a master-detail relationship
  // v1.0.0.0 : first release
  //          : Fixed: Issue with DisplayToRealCell function not returning a value
  //          : Fixed: Issue with TAB key causing exception in TMS WEB Core
  // v1.0.0.1 : Fixed: Issue with modal dialogs in combination with double-click events
  // v1.0.0.2 : Fixed: Issue with column settings related to cell controls not being transfered properly
  //          : Fixed: Issue focusing filter edit on iOS & Android
  //          : Improved: Strings[Col, Row] property is writable now
  // v1.0.0.3 : Fixed: Issue updating grid through editable cell controls
  //          : Fixed: Issue with selection count when there is no selection
  //          : Fixed: Issue calculating stretch in VCL with indexes
  // v1.0.0.4 : Fixed: Issue with stretching index based & scrolling
  //          : Improved: Cell merging able to overwrite and extend existing merges
  // v1.1.0.0 : New: Options.Keyboard.HomeKeyHandling, Options.Keyboard.EndKeyHandling
  //          : New: Options.Filtering.HideEmptyGroups
  //          : Fixed: Issue with disappearing cell controls when updating grid in VCL
  // v1.2.0.0 : New: Filter Row (Options.Filtering.Controls := [gfcEditor]);
  //          : New: OnBeforeCreateCustomFilterEditor, OnCreateCustomFilterEditor, OnAfterCreateCustomFilterEditor events
  //          : New: FirstPage, LastPage, NextPage, PreviousPage, PageIndex and PageCount
  //          : Improved: Performance
  //          : Improved: Filtering algorithm and combinations of AND / OR
  //          : Improved: Exposed EditingCell, IsEditing
  //          : Improved: Layouts[] priority only for normal cells by default overridable with OnGetCellLayoutHasPriority
  //          : Improved: Options.IO.TrimSpaces to trim spaces around text while importing from CSV
  //          : Fixed: Issue with mouse wheel scrolling selecting last row
  //          : Fixed: consistent prefix for TTMSFNCDataGridRowType values
  //          : Fixed: Issue with text clipping
  //          : Fixed: respect spaces around text while importing from CSV
  //          : Fixed: Rounding issue stretching Columns and Rows on VCL
  // v1.2.0.1 : Improved: Loading performance in TMS WEB Core (related to storing icons)
  //          : Fixed: Breaking change: CellAppearance icons are now stored under the property Icons, click ignore when opening your project
  //          : Fixed: Issue with high DPI calculations
  //          : Fixed: Issue with PasteFromClipboard missing characters
  //          : Fixed: Issue in TMS WEB Core web designer
  // v1.2.0.2 : Improved: added DefaultFilterType property at column level
  // v1.2.0.3 : Improved: filter behavior mixing list & edit in dropdown filter
  //          : Fixed: ACanShow changed to ACanExecute for clipboard events
  //          : Fixed: OnLoadCellData & OnSaveCellData not called for clipboard actions
  //          : Fixed: Size of bitmaps in combination with Control settings
  // v1.2.0.4 : New: OnRemoveFilter event
  //          : Improved: Filter.Clear now also effectively clears filter status
  //          : Fixed: Issue selecting (All) clears all filters when Options.Filtering.MultiColumn true
  // v1.2.0.5 : Improved: Exposed and improved row & column hidden, displayed and visible functions
  //          :         : Displayed: Is being rendered, ignores boundaries of the visual range
  //          :         : Visible: Is being rendered, inside the boundaries of the visual range
  //          :         : Hidden: Is not being rendered (hidden or filtered rows, hidden columns)
  //          : Fixed: Issue with Excel Export clearing Layout settings
  //          : Fixed: Issue with checkbox and radiobutton control size on mobile
  // v1.2.0.6 : Fixed: Issue setting width at column level when connected with a database adapter
  //          : Fixed: Issue persisting width at column level when changing DPI
  //          : Fixed: Issue with OnColumnSized and OnRowSized not called when double-clicking to auto-size columns
  // v1.2.0.7 : Improved: OnCustomizeFilterPopupEditor
  // v1.3.0.0 : New : AcceptDropFiles & OnDropFiles event
  //          : New : Columns Editor
  //          : Fixed: Issue sorting order with multiple columns
  //          : Fixed: Issue applying default filter type
  //          : Fixed: Properly rooting OnRemoveFilter event
  //          : Fixed: Incorrect timing of calling OnRemoveFilter event
  //          : Fixed: Issue updating focused cell when swapping cells
  // v1.3.0.1 : Fixed: Issue managing controls when scrolling
  // v1.3.1.0 : New: RowViewMode, ColumnViewMode to configure where fixed rows and columns are positioned.
  //          : Fixed: Issue with formatting TDate & TTime values when exporting to CSV
  // v1.4.0.0 : New: Events to further customize column and row calculations
  //          : New: FilterBuilder support
  //          : Improved: scrolling performance
  //          : Fixed : Invalid pointer updating cells from control events
  // v1.4.0.1 : Improved : Banding now properly applied when filtering
  //          : Fixed : Typo in event parameter OnRowDropped
  //          : Fixed : Issue persisting width when swapping columns
  //          : Fixed : Issue with font scaling at column level
  //          : Fixed : Issue setting comments
  // v1.4.1.0 : New : Filter actions
  //          : New : Appearance based filtering
  //          : Improved : support for moving columns at column collection
  //          : Fixed : Issue with mouse wheel & keyboard scrolling when filtering
  // v1.5.0.0 : New : Header visual grouping
  //          : Improved : SelectAllCells parameter to include visible cells, only applicable for disjunct selection modes
  //          : Improved : Entering date/time when pressing keys if the inplace editor is a date picker
  //          : Improved : (Empty) option in filter drop down list
  //          : Fixed : OnSelectCell triggered only when selection is effectively changed
  //          : Fixed : Issue selecting empty / not empty in drop down does not apply filter
  // v1.5.0.1 : Fixed : Issue with scroll position when scroll mode is cell based
  //          : Fixed : Issue with OnCellDblClick eating focus
  //          : Fixed : Issue with SelectAllCells, now including all rows outside of the view
  //          : Fixed : Issue with font color not being applied when adding cell layouts
  // v1.5.0.2 : Fixed : Regression with mouse handling in combination with column sizing
  // v1.5.0.3 : Improved : Calculation format now has a type for column and row calculations
  //          : Fixed : Cell Layout did not take over global layout at the time of creation
  //          : Fixed : Issue with Control not being properly cleaned up when deleting rows
  // v1.5.0.4 : Improved : mouse wheel scrolling stops editing
  //          : Fixed : Issue with properly moving dictionary data when filtering causing unwanted side-effects on TMS WEB Core
  //          : Fixed : Issue with mouse up and touch-scrolling in TMS WEB Core
  //          : Fixed : Issue parsing filter in TMS WEB Core

type
  TTMSFNCCustomDataGrid = class;

  TTMSFNCDataGridKeyboardMode = (gkmDefault, gkmGrid);

  TTMSFNCDataGridHeaderFooter = class(TPersistent)
  private
    FOwner: TPersistent;
    FVisible: Boolean;
    FOnChangeRebuild: TNotifyEvent;
    FSize: Integer;
    FFill: TTMSFNCGraphicsFill;
    FStroke: TTMSFNCGraphicsStroke;
    FOnChange: TNotifyEvent;
    FAutoSize: Boolean;
    procedure SetSize(const Value: Integer);
    procedure SetVisible(const Value: Boolean);
    procedure SetFill(const Value: TTMSFNCGraphicsFill);
    procedure SetStroke(const Value: TTMSFNCGraphicsStroke);
    procedure SetAutoSize(const Value: Boolean);
  protected
    function GetOwner: TPersistent; override;
    procedure DoFillChanged(Sender: TObject);
    procedure DoStrokeChanged(Sender: TObject);
    property Fill: TTMSFNCGraphicsFill read FFill write SetFill;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
    property OnChangeRebuild: TNotifyEvent read FOnChangeRebuild write FOnChangeRebuild;
    property Size: Integer read FSize write SetSize default 30;
    property Stroke: TTMSFNCGraphicsStroke read FStroke write SetStroke;
    property Visible: Boolean read FVisible write SetVisible default False;
    property AutoSize: Boolean read FAutoSize write SetAutoSize default True;
  public
    constructor Create(AOwner: TPersistent); virtual;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
  end;

  TTMSFNCDataGridHeaderVisualGroupingLayout = class(TTMSFNCDataGridCellLayout)
  private
    function GetTextAlign: TTMSFNCGraphicsTextAlign;
    procedure SetTextAlign(const Value: TTMSFNCGraphicsTextAlign);
  public
    constructor Create(ADefaultFillColor: TTMSFNCGraphicsColor = gcWhite); override;
  published
    property TextAlign: TTMSFNCGraphicsTextAlign read GetTextAlign write SetTextAlign default gtaCenter;
  end;

  TTMSFNCDataGridHeaderVisualGrouping = class(TPersistent)
  private
    FVisible: Boolean;
    FOnChange: TNotifyEvent;
    FOnChangeRebuild: TNotifyEvent;
    FSortOnClick: Boolean;
    FCloseIndicator: Boolean;
    FConnectionLines: Boolean;
    FLayout: TTMSFNCDataGridHeaderVisualGroupingLayout;
    FConnectionStroke: TTMSFNCGraphicsStroke;
    FAutoSize: Boolean;
    FSize: Integer;
    FLevelIndicationFill: TTMSFNCGraphicsFill;
    FLevelActiveIndicationStroke: TTMSFNCGraphicsStroke;
    FLevelIndicationStroke: TTMSFNCGraphicsStroke;
    FLevelActiveIndicationFill: TTMSFNCGraphicsFill;
    FDragging: Boolean;
    procedure SetVisible(const Value: Boolean);
    procedure SetCloseIndicator(const Value: Boolean);
    procedure SetConnectionLines(const Value: Boolean);
    procedure SetLayout(const Value: TTMSFNCDataGridHeaderVisualGroupingLayout);
    procedure SetConnectionStroke(const Value: TTMSFNCGraphicsStroke);
    procedure SetAutoSize(const Value: Boolean);
    procedure SetSize(const Value: Integer);
    procedure SetLevelActiveIndicationFill(const Value: TTMSFNCGraphicsFill);
    procedure SetLevelActiveIndicationStroke(
      const Value: TTMSFNCGraphicsStroke);
    procedure SetLevelIndicationFill(const Value: TTMSFNCGraphicsFill);
    procedure SetLevelIndicationStroke(const Value: TTMSFNCGraphicsStroke);
  protected
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
    property OnChangeRebuild: TNotifyEvent read FOnChangeRebuild write FOnChangeRebuild;
    procedure DoLayoutChanged(Sender: TObject);
    procedure DoConnectionStrokeChanged(Sender: TObject);
    procedure DoFillChanged(Sender: TObject);
    procedure DoStrokeChanged(Sender: TObject);
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
  published
    property AutoSize: Boolean read FAutoSize write SetAutoSize default True;
    property Dragging: Boolean read FDragging write FDragging default True;
    property Size: Integer read FSize write SetSize default 30;
    property Visible: Boolean read FVisible write SetVisible default True;
    property SortOnClick: Boolean read FSortOnClick write FSortOnClick default True;
    property CloseIndicator: Boolean read FCloseIndicator write SetCloseIndicator default True;
    property ConnectionLines: Boolean read FConnectionLines write SetConnectionLines default True;
    property ConnectionStroke: TTMSFNCGraphicsStroke read FConnectionStroke write SetConnectionStroke;
    property Layout: TTMSFNCDataGridHeaderVisualGroupingLayout read FLayout write SetLayout;
    property LevelIndicationFill: TTMSFNCGraphicsFill read FLevelIndicationFill write SetLevelIndicationFill;
    property LevelActiveIndicationFill: TTMSFNCGraphicsFill read FLevelActiveIndicationFill write SetLevelActiveIndicationFill;
    property LevelIndicationStroke: TTMSFNCGraphicsStroke read FLevelIndicationStroke write SetLevelIndicationStroke;
    property LevelActiveIndicationStroke: TTMSFNCGraphicsStroke read FLevelActiveIndicationStroke write SetLevelActiveIndicationStroke;
  end;

  TTMSFNCDataGridButton = class(TCollectionItem)
  private
    FName: string;
    FOnClick: TNotifyEvent;
    FHint: string;
    FWidth: Single;
    FButton: TTMSFNCToolBarButton;
    FBitmap: TTMSFNCBitmap;
    FText: string;
    FEnabled: Boolean;
    FVisible: Boolean;
    FControl: TTMSFNCDataGridCellControl;
    FDataPointer: Pointer;
    FDataBoolean: Boolean;
    FDataString: String;
    FDataObject: TObject;
    FDataInteger: NativeInt;
    FCallback: TProc;
    function IsHintStored: Boolean;
    function IsTextStored: Boolean;
    function IsWidthStored: Boolean;
    procedure SetBitmap(const Value: TTMSFNCBitmap);
    procedure SetWidth(const Value: Single);
    procedure SetHint(const Value: string);
    procedure SetText(const Value: string);
    function GetButton: TTMSFNCToolBarButton;
    procedure SetEnabled(const Value: Boolean);
    procedure SetVisible(const Value: Boolean);
    procedure SetControl(const Value: TTMSFNCDataGridCellControl);
  protected
    procedure DoChanged;
    procedure DoBitmapChanged(Sender: TObject);
  public
    constructor Create(ACollection: TCollection); override;
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
    function GetDisplayName: string; override;
    property Button: TTMSFNCToolBarButton read GetButton;
    property DataBoolean: Boolean read FDataBoolean write FDataBoolean;
    property DataInteger: NativeInt read FDataInteger write FDataInteger;
    property DataObject: TObject read FDataObject write FDataObject;
    property DataPointer: Pointer read FDataPointer write FDataPointer;
    property DataString: String read FDataString write FDataString;
    property Callback: TProc read FCallback write FCallback;
  published
    property Name: string read FName write FName;
    property Hint: string read FHint write SetHint stored IsHintStored nodefault;
    property Text: string read FText write SetText stored IsTextStored nodefault;
    property Bitmap: TTMSFNCBitmap read FBitmap write SetBitmap;
    property Width: Single read FWidth write SetWidth stored IsWidthStored nodefault;
    property Enabled: Boolean read FEnabled write SetEnabled default True;
    property Visible: Boolean read FVisible write SetVisible default True;
    property OnClick: TNotifyEvent read FOnClick write FOnClick;
    property Control: TTMSFNCDataGridCellControl read FControl write SetControl;
  end;

  TTMSFNCDataGridButtons = class(TTMSFNCOwnedCollection{$IFNDEF WEBLIB}<TTMSFNCDataGridButton>{$ENDIF})
  private
    FOnChanged: TNotifyEvent;
    function GetItemEx(Index: Integer): TTMSFNCDataGridButton;
    procedure SetItemEx(Index: Integer; const Value: TTMSFNCDataGridButton);
  protected
    procedure DoChanged;
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
  public
    function Add: TTMSFNCDataGridButton;
    function Insert(index: Integer): TTMSFNCDataGridButton;
    property Items[Index: Integer]: TTMSFNCDataGridButton read GetItemEx write SetItemEx; default;
  end;

  TTMSFNCDataGridHeaderBar = class(TPersistent)
  private
    FOwner: TPersistent;
    FVisible: Boolean;
    FOnChange: TNotifyEvent;
    FOnChangeRebuild: TNotifyEvent;
    FSize: Integer;
    FFill: TTMSFNCGraphicsFill;
    FStroke: TTMSFNCGraphicsStroke;
    FButtons: TTMSFNCDataGridButtons;
    procedure SetVisible(const Value: Boolean);
    procedure SetSize(const Value: Integer);
    procedure SetFill(const Value: TTMSFNCGraphicsFill);
    procedure SetStroke(const Value: TTMSFNCGraphicsStroke);
    procedure SetButtons(const Value: TTMSFNCDataGridButtons);
  protected
    function GetOwner: TPersistent; override;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
    property OnChangeRebuild: TNotifyEvent read FOnChangeRebuild write FOnChangeRebuild;
    procedure DoFillChanged(Sender: TObject);
    procedure DoStrokeChanged(Sender: TObject);
    procedure DoButtonsChanged(Sender: TObject);
  public
    constructor Create(AOwner: TPersistent);
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
  published
    property Visible: Boolean read FVisible write SetVisible default False;
    property Size: Integer read FSize write SetSize default 26;
    property Fill: TTMSFNCGraphicsFill read FFill write SetFill;
    property Stroke: TTMSFNCGraphicsStroke read FStroke write SetStroke;
    property Buttons: TTMSFNCDataGridButtons read FButtons write SetButtons;
  end;

  TTMSFNCDataGridHeader = class(TTMSFNCDataGridHeaderFooter)
  private
    FVisualGrouping: TTMSFNCDataGridHeaderVisualGrouping;
    FBar: TTMSFNCDataGridHeaderBar;
    procedure SetVisualGrouping(const Value: TTMSFNCDataGridHeaderVisualGrouping);
    procedure SetBar(const Value: TTMSFNCDataGridHeaderBar);
  protected
    procedure DoVisualGroupingChanged(Sender: TObject);
    procedure DoVisualGroupingChangedRebuild(Sender: TObject);
    procedure DoBarChanged(Sender: TObject);
    procedure DoBarChangedRebuild(Sender: TObject);
  public
    constructor Create(AOwner: TPersistent); override;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
  published
    property VisualGrouping: TTMSFNCDataGridHeaderVisualGrouping read FVisualGrouping write SetVisualGrouping;
    property AutoSize;
    property Size;
    property Visible;
    property Fill;
    property Stroke;
    property Bar: TTMSFNCDataGridHeaderBar read FBar write SetBar;
  end;

  TTMSFNCDataGridFooter = class(TTMSFNCDataGridHeaderFooter)
  published
    property AutoSize;
    property Size;
    property Visible;
    property Fill;
    property Stroke;
  end;

  {$IFNDEF WEBLIB}
  TTMSFNCDataGridDesigner = class(TTMSFNCCustomControl)
  private
    {$IFDEF FMXLIB}
    FFirstMouseDown, FFirstMouseUp: Boolean;
    {$ENDIF}
    FLoadSampleBitmap, FCloseBitmap: TTMSFNCBitmap;
    FSortBitmap: TTMSFNCBitmap;
    FEditBitmap: TTMSFNCBitmap;
    FFilterBitmap: TTMSFNCBitmap;
    FGrid: TTMSFNCCustomDataGrid;
    FClose: TTMSFNCButton;
    FLoadSample: TButton;
    FSort, FFilter, FEdit: TCheckBox;
    FRecreate: Boolean;
    FCreateTimer: TTimer;
  protected
    function HandleDesignHitTest(const APoint: TPoint): Boolean; override;
    function GetControlBitmap(AControl: TControl): TTMSFNCBitmap;
    procedure ApplyStyle; override;
    procedure ResetToDefaultStyle; override;
    procedure AddControls;
    procedure RemoveControls;
    procedure RecreateControls;
    procedure UpdateControlAfterResize; override;
    procedure DoLoadSampleClick(Sender: TObject);
    procedure DoCloseClick(Sender: TObject);
    procedure DoSortClick(Sender: TObject);
    procedure DoFilterClick(Sender: TObject);
    procedure DoEditClick(Sender: TObject);
    procedure DoCreateTimer(Sender: TObject);
    procedure HandleMouseDown(Button: TTMSFNCMouseButton; Shift: TShiftState; X, Y: Single); override;
    procedure HandleMouseUp(Button: TTMSFNCMouseButton; Shift: TShiftState; X, Y: Single); override;
    procedure Draw({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); override;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
  end;
  {$ENDIF}

  TTMSFNCDataGridHeaderGroupBeforeDrawEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; var ACanDraw: Boolean) of object;
  TTMSFNCDataGridHeaderGroupAfterDrawEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics) of object;

  TTMSFNCDataGridHeaderGroup = class
  private
    FNew: Boolean;
    FScale: Single;
    FOrigRect, FRect: TRectF;
    FSortIndicator: TTMSFNCDataGridCellSortIndicator;
    FSortDescendingIcon: TTMSFNCBitmap;
    FSortAscendingIcon: TTMSFNCBitmap;
    FText: string;
    FColumn: Integer;
    FCloseIcon: TTMSFNCBitmap;
    FCloseIndicator: Boolean;
    FLevel: Integer;
    FLayout: TTMSFNCDataGridCellLayout;
    FActive: Boolean;
    FOnAfterDraw: TTMSFNCDataGridHeaderGroupAfterDrawEvent;
    FOnBeforeDraw: TTMSFNCDataGridHeaderGroupBeforeDrawEvent;
    function GetSortIndicatorRect: TRectF;
    function GetTextRect: TRectF;
    function GetCloseIndicatorRect: TRectF;
    function GetLayout: TTMSFNCDataGridCellLayout;
  protected
    function GetRect: TRectF;
    function XYToCloseIndicator(X, Y: Single): Boolean;
    function GetSortIndicatorSize: Single; virtual;
    function GetCloseIndicatorSize: Single; virtual;
    procedure DoAfterDraw(AGraphics: TTMSFNCGraphics); virtual;
    procedure DoBeforeDraw(AGraphics: TTMSFNCGraphics; var ACanDraw: Boolean); virtual;
    procedure DrawContent(AGraphics: TTMSFNCGraphics); virtual;
    procedure DrawBackground(AGraphics: TTMSFNCGraphics); virtual;
    procedure DrawSortIndicator(AGraphics: TTMSFNCGraphics); virtual;
    procedure DrawCloseIndicator(AGraphics: TTMSFNCGraphics); virtual;
    procedure DrawText(AGraphics: TTMSFNCGraphics); virtual;
    procedure Reset;
    property OnAfterDraw: TTMSFNCDataGridHeaderGroupAfterDrawEvent read FOnAfterDraw write FOnAfterDraw;
    property OnBeforeDraw: TTMSFNCDataGridHeaderGroupBeforeDrawEvent read FOnBeforeDraw write FOnBeforeDraw;
    property SortAscendingIcon: TTMSFNCBitmap read FSortAscendingIcon write FSortAscendingIcon;
    property SortDescendingIcon: TTMSFNCBitmap read FSortDescendingIcon write FSortDescendingIcon;
    property CloseIcon: TTMSFNCBitmap read FCloseIcon write FCloseIcon;
    property Active: Boolean read FActive write FActive default False;
    property Rect: TRectF read GetRect;
    property TextRect: TRectF read GetTextRect;
    property CloseIndicatorRect: TRectF read GetCloseIndicatorRect;
    property SortIndicatorRect: TRectF read GetSortIndicatorRect;
    property SortIndicator: TTMSFNCDataGridCellSortIndicator read FSortIndicator default gsiNone;
    property CloseIndicator: Boolean read FCloseIndicator default True;
  public
    constructor Create; virtual;
    destructor Destroy; override;
    property Column: Integer read FColumn;
    property Level: Integer read FLevel;
    property Layout: TTMSFNCDataGridCellLayout read GetLayout;
    procedure Draw(AGraphics: TTMSFNCGraphics);
    property Text: string read FText write FText;
  end;

  TTMSFNCDataGridHeaderGroups = class(TObjectList<TTMSFNCDataGridHeaderGroup>)
  public
    function Find(AColumn: Integer; var AHeaderGroup: TTMSFNCDataGridHeaderGroup): Boolean;
  end;

  TTMSFNCDataGridBeforeDropHeaderGroupEvent = procedure(Sender: TObject; AColumn: Integer; var ACanDrop: Boolean) of object;
  TTMSFNCDataGridCanDragHeaderGroupEvent = procedure(Sender: TObject; AColumn: Integer; var ACanDrag: Boolean) of object;
  TTMSFNCDataGridAfterDropHeaderGroupEvent = procedure(Sender: TObject; AColumn: Integer) of object;
  TTMSFNCDataGridBeforeSortHeaderGroupEvent = procedure(Sender: TObject; AColumn: Integer; var ACanSort: Boolean) of object;
  TTMSFNCDataGridAfterSortHeaderGroupEvent = procedure(Sender: TObject; AColumn: Integer) of object;
  TTMSFNCDataGridBeforeCloseHeaderGroupEvent = procedure(Sender: TObject; AColumn: Integer; var ACanClose: Boolean) of object;
  TTMSFNCDataGridAfterCloseHeaderGroupEvent = procedure(Sender: TObject; AColumn: Integer) of object;
  TTMSFNCDataGridHeaderGroupClickEvent = procedure(Sender: TObject; AColumn: Integer) of object;
  TTMSFNCDataGridAfterDrawHeaderGroupEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; AHeaderGroup: TTMSFNCDataGridHeaderGroup) of object;
  TTMSFNCDataGridBeforeDrawHeaderGroupEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; AHeaderGroup: TTMSFNCDataGridHeaderGroup; var ACanDraw: Boolean) of object;
  TTMSFNCDataGridAfterDrawHeaderGroupLevelEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; ALevel: Integer; AActive: Boolean) of object;
  TTMSFNCDataGridBeforeDrawHeaderGroupLevelEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; ALevel: Integer; AActive: Boolean; var ACanDraw: Boolean) of object;
  TTMSFNCDataGridCustomizeHeaderGroupEvent = procedure(Sender: TObject; AHeaderGroup: TTMSFNCDataGridHeaderGroup) of object;
  TTMSFNCDataGridAfterDrawHeaderEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF) of object;
  TTMSFNCDataGridBeforeDrawHeaderEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; var ACanDraw: Boolean) of object;
  TTMSFNCDataGridAfterDrawFooterEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF) of object;
  TTMSFNCDataGridBeforeDrawFooterEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; var ACanDraw: Boolean) of object;

  TTMSFNCCustomDataGrid = class(TTMSFNCDataGridBase, ITMSFNCAppearanceGlobalFont, ITMSFNCDataGridRendererLink,
    ITMSFNCDataBinderGrid, ITMSFNCCustomReadWriteIO,
    ITMSFNCBitmapContainer{$IFDEF SUPPORTABSTRACT}, ITMSFNCDataObject{$ENDIF})
  private
    FDoubleClicked: Boolean;
    FVisualGroupingSize: Single;
    FDraggedOnHeader, FDraggingOnHeader: Boolean;
    FMoveX, FMoveY, FDownX, FDownY: Single;
    FHeaderLevelRects: TArray<TRectF>;
    FDownHeaderGroup, FDragHeaderGroup: TTMSFNCDataGridHeaderGroup;
    FCalculateGraphics: TTMSFNCGraphics;
    FFocusedRenderer, FDownRenderer: TTMSFNCDataGridRenderer;
    FFooter: TTMSFNCDataGridFooter;
    FHeader: TTMSFNCDataGridHeader;
    FHeaderGroups, FTempHeaderGroups: TTMSFNCDataGridHeaderGroups;
    {$IFNDEF WEBLIB}
    FDesignerControl: TTMSFNCDataGridDesigner;
    {$ENDIF}
    FDesigner: Boolean;
    FOnHeaderGroupClick: TTMSFNCDataGridHeaderGroupClickEvent;
    FOnAfterDropHeaderGroup: TTMSFNCDataGridAfterDropHeaderGroupEvent;
    FOnBeforeSortHeaderGroup: TTMSFNCDataGridBeforeSortHeaderGroupEvent;
    FOnBeforeCloseHeaderGroup: TTMSFNCDataGridBeforeCloseHeaderGroupEvent;
    FOnAfterSortHeaderGroup: TTMSFNCDataGridAfterSortHeaderGroupEvent;
    FOnBeforeDropHeaderGroup: TTMSFNCDataGridBeforeDropHeaderGroupEvent;
    FOnCanDragHeaderGroup: TTMSFNCDataGridCanDragHeaderGroupEvent;
    FOnAfterCloseHeaderGroup: TTMSFNCDataGridAfterCloseHeaderGroupEvent;
    FOnGroup: TNotifyEvent;
    FOnUngroup: TNotifyEvent;
    FOnBeforeDrawHeaderGroup: TTMSFNCDataGridBeforeDrawHeaderGroupEvent;
    FOnAfterDrawHeaderGroup: TTMSFNCDataGridAfterDrawHeaderGroupEvent;
    FOnCustomizeHeaderGroup: TTMSFNCDataGridCustomizeHeaderGroupEvent;
    FOnBeforeDrawHeaderGroupLevel: TTMSFNCDataGridBeforeDrawHeaderGroupLevelEvent;
    FOnAfterDrawHeaderGroupLevel: TTMSFNCDataGridAfterDrawHeaderGroupLevelEvent;
    FOnBeforeDrawHeader: TTMSFNCDataGridBeforeDrawHeaderEvent;
    FOnBeforeDrawFooter: TTMSFNCDataGridBeforeDrawFooterEvent;
    FOnAfterDrawHeader: TTMSFNCDataGridAfterDrawHeaderEvent;
    FOnAfterDrawFooter: TTMSFNCDataGridAfterDrawFooterEvent;
    function GetAdapter: TTMSFNCDataGridDataAdapter;
    function GetBitmapContainer: TTMSFNCBitmapContainer;
    function GetBitmapNames(AColumn, ARow: Integer): string;
    function GetBitmaps(AColumn, ARow: Integer): TTMSFNCBitmap;
    function GetBooleans(AColumn, ARow: Integer): Boolean;
    function GetBottomRow: Integer;
    function GetCellAppearance: TTMSFNCDataGridCellAppearance;
    function GetCellData(AColumn, ARow: Integer): TTMSFNCDataGridCellItem;
    function GetCellPopupMenu: TTMSFNCDataGridPopupMenu;
    function GetCells(AColumn, ARow: Integer): TTMSFNCDataGridCellValue;
    function GetColors(AColumn, ARow: Integer): TTMSFNCGraphicsColor;
    function GetColumnCalculation(AColumn: Integer; AName: string): TTMSFNCDataGridDataColumnCalculationArray;
    function GetColumnCount: Integer;
    function GetColumnOrder(AColumn: Integer): Integer;
    function GetColumnPos(AColumn: Integer): Double;
    function GetColumnReverseOrder(AColumn: Integer): Integer;
    function GetColumns: TTMSFNCDataGridColumns;
    function GetColumnWidths(AColumn: Integer): Double;
    function GetComments(AColumn, ARow: Integer): string;
    function GetControlAligns(AColumn, ARow: Integer): TTMSFNCDataGridCellControlAlign;
    function GetControlHeights(AColumn, ARow: Integer): Integer;
    function GetControlMargins(AColumn, ARow: Integer): TTMSFNCMargins;
    function GetControlPositions(AColumn, ARow: Integer): TTMSFNCDataGridCellControlPosition;
    function GetControls(AColumn, ARow: Integer): TTMSFNCDataGridCellControl;
    function GetControlTexts(AColumn, ARow: Integer): string;
    function GetControlWidths(AColumn, ARow: Integer): Integer;
    function GetCustomHorizontalScrollBar: TTMSFNCScrollBar;
    function GetCustomScrollBars: Boolean;
    function GetCustomVerticalScrollBar: TTMSFNCScrollBar;
    function GetDefaultColumnWidth: Double;
    function GetDefaultRowHeight: Double;
    function GetDragAppearance: TTMSFNCDataGridDragAppearance;
    function GetEditingCell: TTMSFNCDataGridCellCoord;
    function GetFilter: TTMSFNCDataGridDataFilter;
    function GetFilterAppearance: TTMSFNCDataGridFilterAppearance;
    function GetFilterEditors(AColumn, ARow: Integer): TTMSFNCDataGridCellControl;
    function GetFilteredRowCount: Integer;
    function GetFixedBottomRowCount: Integer;
    function GetFixedColumnCount: Integer;
    function GetFixedRightColumnCount: Integer;
    function GetFixedRowCount: Integer;
    function GetFloats(AColumn, ARow: Integer): Double;
    function GetFocusedCell: TTMSFNCDataGridCellCoord;
    function GetFontColors(AColumn, ARow: Integer): TTMSFNCGraphicsColor;
    function GetFontNames(AColumn, ARow: Integer): string;
    function GetFontSizes(AColumn, ARow: Integer): Single;
    function GetFontStyles(AColumn, ARow: Integer): TFontStyles;
    function GetFreezeColumnCount: Integer;
    function GetFreezeRowCount: Integer;
    function GetGlobalFont: TTMSFNCAppearanceGlobalFont;
    function GetGrids(AColumn, ARow: Integer): TTMSFNCDataGridRenderer;
    function GetHasFilterEditor(AColumn, ARow: Integer): Boolean;
    function GetHasLayout(AColumn, ARow: Integer): Boolean;
    function GetHasRowOrder(ARow: Integer): Boolean;
    function GetHeader: TTMSFNCDataGridHeader;
    function GetHiddenColumnCount: Integer;
    function GetHiddenColumns(AColumn: Integer): Boolean;
    function GetHiddenColumnWidths(AColumn: Integer): Double;
    function GetHiddenRowCount: Integer;
    function GetHiddenRowHeights(ARow: Integer): Double;
    function GetHiddenRows(ARow: Integer): Boolean;
    function GetHints(AColumn, ARow: Integer): string;
    function GetHorizontalScrollBar: TScrollBar;
    function GetHorizontalScrollBarVisible: Boolean;
    function GetIcons: TTMSFNCDataGridIcons;
    function GetInts(AColumn, ARow: Integer): Int64;
    function GetLayouts(AColumn, ARow: Integer): TTMSFNCDataGridCellLayout;
    function GetLeftColumn: Integer;
    function GetLink: TTMSFNCDataGridRenderer;
    function GetManagedObjects(AColumn, ARow: Integer): TObject;
    function GetMergedColumns(AColumn: Integer): Boolean;
    function GetMergedRows(ARow: Integer): Boolean;
    function GetObjects(AColumn, ARow: Integer): TObject;
    function GetOnAfterApplyFilter: TTMSFNCDataGridAfterApplyFilterEvent;
    function GetOnAfterCloseInplaceEditor: TTMSFNCDataGridAfterCloseInplaceEditorEvent;
    function GetOnAfterCopyToClipboard: TTMSFNCDataGridAfterClipboardEvent;
    function GetOnAfterCreateFilterEditor: TTMSFNCDataGridAfterCreateFilterEditorEvent;
    function GetOnAfterCutToClipboard: TTMSFNCDataGridAfterClipboardEvent;
    function GetOnAfterDrawCell: TTMSFNCDataGridAfterDrawCellEvent;
    function GetOnAfterDrawEmptySpace: TTMSFNCDataGridAfterDrawEmptySpaceEvent;
    function GetOnAfterDrawGrid: TTMSFNCDataGridAfterDrawGridEvent;
    function GetOnAfterLookup: TTMSFNCDataGridAfterLookupEvent;
    function GetOnAfterOpenInplaceEditor: TTMSFNCDataGridAfterOpenInplaceEditorEvent;
    function GetOnAfterPasteFromClipboard: TTMSFNCDataGridAfterClipboardEvent;
    function GetOnAfterShowFilter: TTMSFNCDataGridAfterShowFilterEvent;
    function GetOnAfterShowFilterDialog: TTMSFNCDataGridAfterShowFilterDialogEvent;
    function GetOnAfterShowFindDialog: TTMSFNCDataGridAfterShowFindDialogEvent;
    function GetOnAfterShowReplaceDialog: TTMSFNCDataGridAfterShowReplaceDialogEvent;
    function GetOnAfterSortColumn: TTMSFNCDataGridAfterSortColumnEvent;
    function GetOnAppendColumn: TTMSFNCDataGridAppendColumnEvent;
    function GetOnAppendRow: TTMSFNCDataGridAppendRowEvent;
    function GetOnBeforeApplyFilter: TTMSFNCDataGridBeforeApplyFilterEvent;
    function GetOnBeforeCloseInplaceEditor: TTMSFNCDataGridBeforeCloseInplaceEditorEvent;
    function GetOnBeforeCopyToClipboard: TTMSFNCDataGridBeforeClipboardEvent;
    function GetOnBeforeCreateFilterEditor: TTMSFNCDataGridBeforeCreateFilterEditorEvent;
    function GetOnBeforeCutToClipboard: TTMSFNCDataGridBeforeClipboardEvent;
    function GetOnBeforeDrawCell: TTMSFNCDataGridBeforeDrawCellEvent;
    function GetOnBeforeDrawEmptySpace: TTMSFNCDataGridBeforeDrawEmptySpaceEvent;
    function GetOnBeforeLookup: TTMSFNCDataGridBeforeLookupEvent;
    function GetOnBeforeOpenInplaceEditor: TTMSFNCDataGridBeforeOpenInplaceEditorEvent;
    function GetOnBeforePasteFromClipboard: TTMSFNCDataGridBeforeClipboardEvent;
    function GetOnBeforeShowFilter: TTMSFNCDataGridBeforeShowFilterEvent;
    function GetOnBeforeShowFindDialog: TTMSFNCDataGridBeforeShowFindDialogEvent;
    function GetOnBeforeShowFilterDialog: TTMSFNCDataGridBeforeShowFilterDialogEvent;
    function GetOnBeforeShowReplaceDialog: TTMSFNCDataGridBeforeShowReplaceDialogEvent;
    function GetOnBeforeSortColumn: TTMSFNCDataGridBeforeSortColumnEvent;
    function GetOnCanAppendColumn: TTMSFNCDataGridCanAppendColumnEvent;
    function GetOnCanAppendRow: TTMSFNCDataGridCanAppendRowEvent;
    function GetOnCanDeleteColumn: TTMSFNCDataGridCanDeleteColumnEvent;
    function GetOnCanDeleteRow: TTMSFNCDataGridCanDeleteRowEvent;
    function GetOnCanDragColumn: TTMSFNCDataGridCanDragColumnEvent;
    function GetOnCanDragRow: TTMSFNCDataGridCanDragRowEvent;
    function GetOnCanDropColumn: TTMSFNCDataGridCanDropColumnEvent;
    function GetOnCanDropRow: TTMSFNCDataGridCanDropRowEvent;
    function GetOnCanEditCell: TTMSFNCDataGridCanEditCellEvent;
    function GetOnCanInsertColumn: TTMSFNCDataGridCanInsertColumnEvent;
    function GetOnCanInsertRow: TTMSFNCDataGridCanInsertRowEvent;
    function GetOnCanSizeColumn: TTMSFNCDataGridCanSizeColumnEvent;
    function GetOnCanSizeRow: TTMSFNCDataGridCanSizeRowEvent;
    function GetOnCellAfterNodeCollapse: TTMSFNCDataGridCellAfterNodeCollapseEvent;
    function GetOnCellAfterNodeExpand: TTMSFNCDataGridCellAfterNodeExpandEvent;
    function GetOnCellAnchorClick: TTMSFNCDataGridCellAnchorClickEvent;
    function GetOnCellBeforeNodeCollapse: TTMSFNCDataGridCellBeforeNodeCollapseEvent;
    function GetOnCellBeforeNodeExpand: TTMSFNCDataGridCellBeforeNodeExpandEvent;
    function GetOnCellBeforeShowPopupMenu: TTMSFNCDataGridCellBeforeShowPopupMenuEvent;
    function GetOnCellButtonClick: TTMSFNCDataGridCellClickEvent;
    function GetOnCellCheckBoxChange: TTMSFNCDataGridCellClickEvent;
    function GetOnCellClick: TTMSFNCDataGridCellClickEvent;
    function GetOnCellDblClick: TTMSFNCDataGridCellClickEvent;
    function GetOnCellEditGetData: TTMSFNCDataGridCellEditGetDataEvent;
    function GetOnCellEditSetData: TTMSFNCDataGridCellEditSetDataEvent;
    function GetOnCellEditValidateData: TTMSFNCDataGridCellEditValidateDataEvent;
    function GetOnCellRadioButtonChange: TTMSFNCDataGridCellClickEvent;
    function GetOnCellRightClick: TTMSFNCDataGridCellClickEvent;
    function GetOnColumnDropped: TTMSFNCDataGridColumnDroppedEvent;
    function GetOnColumnSized: TTMSFNCDataGridColumnSizedEvent;
    function GetOnCreateCustomFilterEditor: TTMSFNCDataGridCreateCustomFilterEditorEvent;
    function GetOnCustomColumnCalculation: TTMSFNCDataGridDataCustomColumnCalculationEvent;
    function GetOnCustomCompare: TTMSFNCDataGridDataCustomCompareEvent;
    function GetOnCustomizeFilterPopupEditor: TTMSFNCDataGridCustomizeFilterPopupEditorEvent;
    function GetOnCustomRowCalculation: TTMSFNCDataGridDataCustomRowCalculationEvent;
    function GetOnDeleteColumn: TTMSFNCDataGridDeleteColumnEvent;
    function GetOnDeleteRow: TTMSFNCDataGridDeleteRowEvent;
    function GetOnDropFiles: TTMSFNCDataGridDropFilesEvent;
    function GetOnGetCellClass: TTMSFNCDataGridGetCellClassEvent;
    function GetOnGetCellData: TTMSFNCDataGridDataGetCellDataEvent;
    function GetOnGetCellDataProperties: TTMSFNCDataGridDataGetCellDataPropertiesEvent;
    function GetOnGetCellDisplayValue: TTMSFNCDataGridDataGetCellDisplayValueEvent;
    function GetOnGetCellFormatting: TTMSFNCDataGridDataGetCellFormattingEvent;
    function GetOnGetCellHint: TTMSFNCDataGridGetCellHintEvent;
    function GetOnGetCellLayout: TTMSFNCDataGridGetCellLayoutEvent;
    function GetOnGetCellLayoutHasPriority: TTMSFNCDataGridGetCellLayoutHasPriorityEvent;
    function GetOnGetCellProperties: TTMSFNCDataGridGetCellPropertiesEvent;
    function GetOnGetColumnCalculationOptions: TTMSFNCDataGridDataGetColumnCalculationOptionsEvent;
    function GetOnGetCustomGroup: TTMSFNCDataGridDataGetCustomGroupEvent;
    function GetOnGetGroupOptions: TTMSFNCDataGridDataGetGroupOptionsEvent;
    function GetOnGetInplaceEditorClass: TTMSFNCDataGridGetInplaceEditorClassEvent;
    function GetOnGetInplaceEditorProperties: TTMSFNCDataGridGetInplaceEditorPropertiesEvent;
    function GetOnGetInplaceEditorRect: TTMSFNCDataGridGetInplaceEditorRectEvent;
    function GetOnGetInplaceEditorTarget: TTMSFNCDataGridGetInplaceEditorTargetEvent;
    function GetOnGetInplaceEditorType: TTMSFNCDataGridGetInplaceEditorTypeEvent;
    function GetOnGetRowCalculationOptions: TTMSFNCDataGridDataGetRowCalculationOptionsEvent;
    function GetOnGetSortOptions: TTMSFNCDataGridDataGetSortOptionsEvent;
    function GetOnHScroll: TTMSFNCDataGridScrollEvent;
    function GetOnInsertColumn: TTMSFNCDataGridInsertColumnEvent;
    function GetOnInsertRow: TTMSFNCDataGridInsertRowEvent;
    function GetOnLoadCellData: TTMSFNCDataGridDataCellDataEvent;
    function GetOnLogMessage: TTMSFNCDataGridLogMessageEvent;
    function GetOnNeedFilterDropDownData: TTMSFNCDataGridNeedFilterDropDownData;
    function GetOnRemoveFilter: TTMSFNCDataGridRemoveFilterEvent;
    function GetOnRowCollapse: TTMSFNCDataGridDataRowCollapseEvent;
    function GetOnRowDropped: TTMSFNCDataGridRowDroppedEvent;
    function GetOnRowExpand: TTMSFNCDataGridDataRowExpandEvent;
    function GetOnRowSized: TTMSFNCDataGridRowSizedEvent;
    function GetOnSaveCellData: TTMSFNCDataGridDataCellDataEvent;
    function GetOnSelectCell: TTMSFNCDataGridSelectCellEvent;
    function GetOnSelectingCell: TTMSFNCDataGridSelectingCellEvent;
    function GetOnSizeColumn: TTMSFNCDataGridSizeColumnEvent;
    function GetOnSizeRow: TTMSFNCDataGridSizeRowEvent;
    function GetOnVScroll: TTMSFNCDataGridScrollEvent;
    function GetOptions: TTMSFNCDataGridOptions;
    function GetPageCount: Integer;
    function GetPageIndex: Integer;
    function GetReadOnlys(AColumn, ARow: Integer): Boolean;
    function GetRightColumn: Integer;
    function GetRowCalculation(ARow: Integer; AName: string): TTMSFNCDataGridDataRowCalculationArray;
    function GetRowCount: Integer;
    function GetRowHeights(ARow: Integer): Double;
    function GetRowNodeChildCount(ARow: Integer): Integer;
    function GetRowNodeLevel(ARow: Integer): Integer;
    function GetRowNodeState(ARow: Integer): TTMSFNCDataGridRowNodeState;
    function GetRowOrder(ARow: Integer): Integer;
    function GetRowPos(ARow: Integer): Double;
    function GetRowReverseOrder(ARow: Integer): Integer;
    function GetRowText(ARow: Integer): string;
    function GetRowTypes(ARow: Integer): TTMSFNCDataGridRowType;
    function GetScrollMode: TTMSFNCDataGridScrollMode;
    function GetScrollUpdate: TTMSFNCDataGridScrollUpdate;
    function GetSelectedCellCount: Integer;
    function GetSelectedCells(AColumn, ARow: Integer): Boolean;
    function GetSelectedColumnCount: Integer;
    function GetSelectedColumns(AColumn: Integer): Boolean;
    function GetSelectedRowCount: Integer;
    function GetSelectedRows(ARow: Integer): Boolean;
    function GetSelection: TTMSFNCDataGridCellCoordRange;
    function GetSortIndexList: TTMSFNCDataGridSortIndexList;
    function GetStartColumn: Integer;
    function GetStartRow: Integer;
    function GetStates(AColumn, ARow: Integer): TTMSFNCDataGridCellState;
    function GetStopColumn: Integer;
    function GetStopRow: Integer;
    function GetStoredCells(AColumn, ARow: Integer): TTMSFNCDataGridCellValue;
    function GetStretchScrollBars: Boolean;
    function GetStrings(AColumn, ARow: Integer): string;
    function GetStrippedCells(AColumn, ARow: Integer): string;
    function GetTextAligns(AColumn, ARow: Integer): TTMSFNCGraphicsTextAlign;
    function GetTextAngles(AColumn, ARow: Integer): Double;
    function GetTopRow: Integer;
    function GetTotalColumnWidth: Double;
    function GetTotalRowHeight: Double;
    function GetVerticalScrollBar: TScrollBar;
    function GetVerticalScrollBarVisible: Boolean;
    function GetVerticalTextAligns(AColumn, ARow: Integer): TTMSFNCGraphicsTextAlign;
    function GetVisibleColumnCount: Integer;
    function GetVisibleRowCount: Integer;
    function GetWordWrapping(AColumn, ARow: Integer): Boolean;
    function GetDragCell: TTMSFNCDataGridCellCoord;
    function GetColumnsEditor: TTMSFNCDataGridColumnsEditor;
    function GetRowViewMode: TTMSFNCDataGridRowViewMode;
    function GetColumnViewMode: TTMSFNCDataGridColumnViewMode;
    function IsDefaultColumnWidthStored: Boolean;
    function IsDefaultRowHeightStored: Boolean;
    procedure SetAdapter(const Value: TTMSFNCDataGridDataAdapter);
    procedure SetBitmapContainer(const Value: TTMSFNCBitmapContainer);
    procedure SetBooleans(AColumn, ARow: Integer; const Value: Boolean);
    procedure SetCellAppearance(const Value: TTMSFNCDataGridCellAppearance);
    procedure SetCellPopupMenu(const Value: TTMSFNCDataGridPopupMenu);
    procedure SetCells(AColumn, ARow: Integer; const Value: TTMSFNCDataGridCellValue);
    procedure SetColors(AColumn, ARow: Integer; const Value: TTMSFNCGraphicsColor);
    procedure SetColumnCalculation(AColumn: Integer; AName: string; const Value: TTMSFNCDataGridDataColumnCalculationArray);
    procedure SetColumnCount(const Value: Integer);
    procedure SetColumns(const Value: TTMSFNCDataGridColumns);
    procedure SetColumnWidths(AColumn: Integer; const Value: Double);
    procedure SetComments(AColumn, ARow: Integer; const Value: string);
    procedure SetControlAligns(AColumn, ARow: Integer; const Value: TTMSFNCDataGridCellControlAlign);
    procedure SetControlHeights(AColumn, ARow: Integer; const Value: Integer);
    procedure SetControlPositions(AColumn, ARow: Integer; const Value: TTMSFNCDataGridCellControlPosition);
    procedure SetControls(AColumn, ARow: Integer; const Value: TTMSFNCDataGridCellControl);
    procedure SetControlTexts(AColumn, ARow: Integer; const Value: string);
    procedure SetControlWidths(AColumn, ARow: Integer; const Value: Integer);
    procedure SetCustomScrollBars(const Value: Boolean);
    procedure SetDefaultColumnWidth(const Value: Double);
    procedure SetDefaultRowHeight(const Value: Double);
    procedure SetDesigner(const Value: Boolean);
    procedure SetDragAppearance(const Value: TTMSFNCDataGridDragAppearance);
    procedure SetFilterAppearance(const Value: TTMSFNCDataGridFilterAppearance);
    procedure SetFixedBottomRowCount(const Value: Integer);
    procedure SetFixedColumnCount(const Value: Integer);
    procedure SetFixedRightColumnCount(const Value: Integer);
    procedure SetFixedRowCount(const Value: Integer);
    procedure SetFloats(AColumn, ARow: Integer; const Value: Double);
    procedure SetFocusedCell(const Value: TTMSFNCDataGridCellCoord);
    procedure SetFontColors(AColumn, ARow: Integer; const Value: TTMSFNCGraphicsColor);
    procedure SetFontNames(AColumn, ARow: Integer; const Value: string);
    procedure SetFontSizes(AColumn, ARow: Integer; const Value: Single);
    procedure SetFontStyles(AColumn, ARow: Integer; const Value: TFontStyles);
    procedure SetFooter(const Value: TTMSFNCDataGridFooter);
    procedure SetFreezeColumnCount(const Value: Integer);
    procedure SetFreezeRowCount(const Value: Integer);
    procedure SetGlobalFont(const Value: TTMSFNCAppearanceGlobalFont);
    procedure SetHeader(const Value: TTMSFNCDataGridHeader);
    procedure SetHiddenColumns(AColumn: Integer; const Value: Boolean);
    procedure SetHiddenRows(ARow: Integer; const Value: Boolean);
    procedure SetHints(AColumn, ARow: Integer; const Value: string);
    procedure SetHorizontalScrollBarVisible(const Value: Boolean);
    procedure SetIcons(const Value: TTMSFNCDataGridIcons);
    procedure SetInts(AColumn, ARow: Integer; const Value: Int64);
    procedure SetLeftColumn(const Value: Integer);
    procedure SetManagedObjects(AColumn, ARow: Integer; const Value: TObject);
    procedure SetMergedColumns(AColumn: Integer; const Value: Boolean);
    procedure SetMergedRows(ARow: Integer; const Value: Boolean);
    procedure SetObjects(AColumn, ARow: Integer; const Value: TObject);
    procedure SetOnAfterApplyFilter(const Value: TTMSFNCDataGridAfterApplyFilterEvent);
    procedure SetOnAfterCloseInplaceEditor(const Value: TTMSFNCDataGridAfterCloseInplaceEditorEvent);
    procedure SetOnAfterCopyToClipboard(const Value: TTMSFNCDataGridAfterClipboardEvent);
    procedure SetOnAfterCreateFilterEditor(const Value: TTMSFNCDataGridAfterCreateFilterEditorEvent);
    procedure SetOnAfterCutToClipboard(const Value: TTMSFNCDataGridAfterClipboardEvent);
    procedure SetOnAfterDrawCell(const Value: TTMSFNCDataGridAfterDrawCellEvent);
    procedure SetOnAfterDrawEmptySpace(const Value: TTMSFNCDataGridAfterDrawEmptySpaceEvent);
    procedure SetOnAfterDrawGrid(const Value: TTMSFNCDataGridAfterDrawGridEvent);
    procedure SetOnAfterLookup(const Value: TTMSFNCDataGridAfterLookupEvent);
    procedure SetOnAfterOpenInplaceEditor(const Value: TTMSFNCDataGridAfterOpenInplaceEditorEvent);
    procedure SetOnAfterPasteFromClipboard(const Value: TTMSFNCDataGridAfterClipboardEvent);
    procedure SetOnAfterShowFilter(const Value: TTMSFNCDataGridAfterShowFilterEvent);
    procedure SetOnAfterShowFilterDialog(const Value: TTMSFNCDataGridAfterShowFilterDialogEvent);
    procedure SetOnAfterShowFindDialog(const Value: TTMSFNCDataGridAfterShowFindDialogEvent);
    procedure SetOnAfterShowReplaceDialog(const Value: TTMSFNCDataGridAfterShowReplaceDialogEvent);
    procedure SetOnAfterSortColumn(const Value: TTMSFNCDataGridAfterSortColumnEvent);
    procedure SetOnAppendColumn(const Value: TTMSFNCDataGridAppendColumnEvent);
    procedure SetOnAppendRow(const Value: TTMSFNCDataGridAppendRowEvent);
    procedure SetOnBeforeApplyFilter(const Value: TTMSFNCDataGridBeforeApplyFilterEvent);
    procedure SetOnBeforeCloseInplaceEditor(const Value: TTMSFNCDataGridBeforeCloseInplaceEditorEvent);
    procedure SetOnBeforeCopyToClipboard(const Value: TTMSFNCDataGridBeforeClipboardEvent);
    procedure SetOnBeforeCreateFilterEditor(const Value: TTMSFNCDataGridBeforeCreateFilterEditorEvent);
    procedure SetOnBeforeCutToClipboard(const Value: TTMSFNCDataGridBeforeClipboardEvent);
    procedure SetOnBeforeDrawCell(const Value: TTMSFNCDataGridBeforeDrawCellEvent);
    procedure SetOnBeforeDrawEmptySpace(const Value: TTMSFNCDataGridBeforeDrawEmptySpaceEvent);
    procedure SetOnBeforeLookup(const Value: TTMSFNCDataGridBeforeLookupEvent);
    procedure SetOnBeforeOpenInplaceEditor(const Value: TTMSFNCDataGridBeforeOpenInplaceEditorEvent);
    procedure SetOnBeforePasteFromClipboard(const Value: TTMSFNCDataGridBeforeClipboardEvent);
    procedure SetOnBeforeShowFilter(const Value: TTMSFNCDataGridBeforeShowFilterEvent);
    procedure SetOnBeforeShowFilterDialog(const Value: TTMSFNCDataGridBeforeShowFilterDialogEvent);
    procedure SetOnBeforeShowFindDialog(const Value: TTMSFNCDataGridBeforeShowFindDialogEvent);
    procedure SetOnBeforeShowReplaceDialog(const Value: TTMSFNCDataGridBeforeShowReplaceDialogEvent);
    procedure SetOnBeforeSortColumn(const Value: TTMSFNCDataGridBeforeSortColumnEvent);
    procedure SetOnCanAppendColumn(const Value: TTMSFNCDataGridCanAppendColumnEvent);
    procedure SetOnCanAppendRow(const Value: TTMSFNCDataGridCanAppendRowEvent);
    procedure SetOnCanDeleteColumn(const Value: TTMSFNCDataGridCanDeleteColumnEvent);
    procedure SetOnCanDeleteRow(const Value: TTMSFNCDataGridCanDeleteRowEvent);
    procedure SetOnCanDragColumn(const Value: TTMSFNCDataGridCanDragColumnEvent);
    procedure SetOnCanDragRow(const Value: TTMSFNCDataGridCanDragRowEvent);
    procedure SetOnCanDropColumn(const Value: TTMSFNCDataGridCanDropColumnEvent);
    procedure SetOnCanDropRow(const Value: TTMSFNCDataGridCanDropRowEvent);
    procedure SetOnCanEditCell(const Value: TTMSFNCDataGridCanEditCellEvent);
    procedure SetOnCanInsertColumn(const Value: TTMSFNCDataGridCanInsertColumnEvent);
    procedure SetOnCanInsertRow(const Value: TTMSFNCDataGridCanInsertRowEvent);
    procedure SetOnCanSizeColumn(const Value: TTMSFNCDataGridCanSizeColumnEvent);
    procedure SetOnCanSizeRow(const Value: TTMSFNCDataGridCanSizeRowEvent);
    procedure SetOnCellAfterNodeCollapse(const Value: TTMSFNCDataGridCellAfterNodeCollapseEvent);
    procedure SetOnCellAfterNodeExpand(const Value: TTMSFNCDataGridCellAfterNodeExpandEvent);
    procedure SetOnCellAnchorClick(const Value: TTMSFNCDataGridCellAnchorClickEvent);
    procedure SetOnCellBeforeNodeCollapse(const Value: TTMSFNCDataGridCellBeforeNodeCollapseEvent);
    procedure SetOnCellBeforeNodeExpand(const Value: TTMSFNCDataGridCellBeforeNodeExpandEvent);
    procedure SetOnCellBeforeShowPopupMenu(const Value: TTMSFNCDataGridCellBeforeShowPopupMenuEvent);
    procedure SetOnCellButtonClick(const Value: TTMSFNCDataGridCellClickEvent);
    procedure SetOnCellCheckBoxChange(const Value: TTMSFNCDataGridCellClickEvent);
    procedure SetOnCellClick(const Value: TTMSFNCDataGridCellClickEvent);
    procedure SetOnCellDblClick(const Value: TTMSFNCDataGridCellClickEvent);
    procedure SetOnCellEditGetData(const Value: TTMSFNCDataGridCellEditGetDataEvent);
    procedure SetOnCellEditSetData(const Value: TTMSFNCDataGridCellEditSetDataEvent);
    procedure SetOnCellEditValidateData(const Value: TTMSFNCDataGridCellEditValidateDataEvent);
    procedure SetOnCellRadioButtonChange(const Value: TTMSFNCDataGridCellClickEvent);
    procedure SetOnCellRightClick(const Value: TTMSFNCDataGridCellClickEvent);
    procedure SetOnColumnDropped(const Value: TTMSFNCDataGridColumnDroppedEvent);
    procedure SetOnColumnSized(const Value: TTMSFNCDataGridColumnSizedEvent);
    procedure SetOnCreateCustomFilterEditor(const Value: TTMSFNCDataGridCreateCustomFilterEditorEvent);
    procedure SetOnCustomColumnCalculation(const Value: TTMSFNCDataGridDataCustomColumnCalculationEvent);
    procedure SetOnCustomCompare(const Value: TTMSFNCDataGridDataCustomCompareEvent);
    procedure SetOnCustomizeFilterPopupEditor(const Value: TTMSFNCDataGridCustomizeFilterPopupEditorEvent);
    procedure SetOnCustomRowCalculation(const Value: TTMSFNCDataGridDataCustomRowCalculationEvent);
    procedure SetOnDeleteColumn(const Value: TTMSFNCDataGridDeleteColumnEvent);
    procedure SetOnDeleteRow(const Value: TTMSFNCDataGridDeleteRowEvent);
    procedure SetOnDropFiles(const Value: TTMSFNCDataGridDropFilesEvent);
    procedure SetOnGetCellClass(const Value: TTMSFNCDataGridGetCellClassEvent);
    procedure SetOnGetCellData(const Value: TTMSFNCDataGridDataGetCellDataEvent);
    procedure SetOnGetCellDataProperties(const Value: TTMSFNCDataGridDataGetCellDataPropertiesEvent);
    procedure SetOnGetCellDisplayValue(const Value: TTMSFNCDataGridDataGetCellDisplayValueEvent);
    procedure SetOnGetCellFormatting(const Value: TTMSFNCDataGridDataGetCellFormattingEvent);
    procedure SetOnGetCellHint(const Value: TTMSFNCDataGridGetCellHintEvent);
    procedure SetOnGetCellLayout(const Value: TTMSFNCDataGridGetCellLayoutEvent);
    procedure SetOnGetCellLayoutHasPriority(const Value: TTMSFNCDataGridGetCellLayoutHasPriorityEvent);
    procedure SetOnGetCellProperties(const Value: TTMSFNCDataGridGetCellPropertiesEvent);
    procedure SetOnGetColumnCalculationOptions(const Value: TTMSFNCDataGridDataGetColumnCalculationOptionsEvent);
    procedure SetOnGetCustomGroup(const Value: TTMSFNCDataGridDataGetCustomGroupEvent);
    procedure SetOnGetGroupOptions(const Value: TTMSFNCDataGridDataGetGroupOptionsEvent);
    procedure SetOnGetInplaceEditorClass(const Value: TTMSFNCDataGridGetInplaceEditorClassEvent);
    procedure SetOnGetInplaceEditorProperties(const Value: TTMSFNCDataGridGetInplaceEditorPropertiesEvent);
    procedure SetOnGetInplaceEditorRect(const Value: TTMSFNCDataGridGetInplaceEditorRectEvent);
    procedure SetOnGetInplaceEditorTarget(const Value: TTMSFNCDataGridGetInplaceEditorTargetEvent);
    procedure SetOnGetInplaceEditorType(const Value: TTMSFNCDataGridGetInplaceEditorTypeEvent);
    procedure SetOnGetRowCalculationOptions(const Value: TTMSFNCDataGridDataGetRowCalculationOptionsEvent);
    procedure SetOnGetSortOptions(const Value: TTMSFNCDataGridDataGetSortOptionsEvent);
    procedure SetOnHScroll(const Value: TTMSFNCDataGridScrollEvent);
    procedure SetOnInsertColumn(const Value: TTMSFNCDataGridInsertColumnEvent);
    procedure SetOnInsertRow(const Value: TTMSFNCDataGridInsertRowEvent);
    procedure SetOnLoadCellData(const Value: TTMSFNCDataGridDataCellDataEvent);
    procedure SetOnLogMessage(const Value: TTMSFNCDataGridLogMessageEvent);
    procedure SetOnNeedFilterDropDownData(const Value: TTMSFNCDataGridNeedFilterDropDownData);
    procedure SetOnRemoveFilter(const Value: TTMSFNCDataGridRemoveFilterEvent);
    procedure SetOnRowCollapse(const Value: TTMSFNCDataGridDataRowCollapseEvent);
    procedure SetOnRowDropped(const Value: TTMSFNCDataGridRowDroppedEvent);
    procedure SetOnRowExpand(const Value: TTMSFNCDataGridDataRowExpandEvent);
    procedure SetOnRowSized(const Value: TTMSFNCDataGridRowSizedEvent);
    procedure SetOnSaveCellData(const Value: TTMSFNCDataGridDataCellDataEvent);
    procedure SetOnSelectCell(const Value: TTMSFNCDataGridSelectCellEvent);
    procedure SetOnSelectingCell(const Value: TTMSFNCDataGridSelectingCellEvent);
    procedure SetOnSizeColumn(const Value: TTMSFNCDataGridSizeColumnEvent);
    procedure SetOnSizeRow(const Value: TTMSFNCDataGridSizeRowEvent);
    procedure SetOnVScroll(const Value: TTMSFNCDataGridScrollEvent);
    procedure SetOptions(const Value: TTMSFNCDataGridOptions);
    procedure SetPageIndex(const Value: Integer);
    procedure SetReadOnlys(AColumn, ARow: Integer; const Value: Boolean);
    procedure SetRowCalculation(ARow: Integer; AName: string; const Value: TTMSFNCDataGridDataRowCalculationArray);
    procedure SetRowCount(const Value: Integer);
    procedure SetRowHeights(ARow: Integer; const Value: Double);
    procedure SetRowNodeChildCount(ARow: Integer; const Value: Integer);
    procedure SetRowNodeLevel(ARow: Integer; const Value: Integer);
    procedure SetRowNodeState(ARow: Integer; const Value: TTMSFNCDataGridRowNodeState);
    procedure SetRowText(ARow: Integer; const Value: string);
    procedure SetRowTypes(ARow: Integer; const Value: TTMSFNCDataGridRowType);
    procedure SetScrollMode(const Value: TTMSFNCDataGridScrollMode);
    procedure SetScrollUpdate(const Value: TTMSFNCDataGridScrollUpdate);
    procedure SetSelectedCells(AColumn, ARow: Integer; const Value: Boolean);
    procedure SetSelectedColumns(AColumn: Integer; const Value: Boolean);
    procedure SetSelectedRows(ARow: Integer; const Value: Boolean);
    procedure SetSelection(const Value: TTMSFNCDataGridCellCoordRange);
    procedure SetStretchScrollBars(const Value: Boolean);
    procedure SetStrings(AColumn, ARow: Integer; const Value: string);
    procedure SetTextAligns(AColumn, ARow: Integer; const Value: TTMSFNCGraphicsTextAlign);
    procedure SetTextAngles(AColumn, ARow: Integer; const Value: Double);
    procedure SetTopRow(const Value: Integer);
    procedure SetVerticalScrollBarVisible(const Value: Boolean);
    procedure SetVerticalTextAligns(AColumn, ARow: Integer; const Value: TTMSFNCGraphicsTextAlign);
    procedure SetWordWrapping(AColumn, ARow: Integer; const Value: Boolean);
    procedure SetRowViewMode(const Value: TTMSFNCDataGridRowViewMode);
    procedure SetColumnViewMode(const Value: TTMSFNCDataGridColumnViewMode);
    function GetFilterBuilder: TTMSFNCFilterBuilder;
    function GetOnAfterSetColumnCalculation: TTMSFNCDataGridDataAfterSetColumnCalculation;
    function GetOnAfterSetRowCalculation: TTMSFNCDataGridDataAfterSetRowCalculation;
    function GetOnBeforeSetColumnCalculation: TTMSFNCDataGridDataBeforeSetColumnCalculation;
    function GetOnBeforeSetRowCalculation: TTMSFNCDataGridDataBeforeSetRowCalculation;
    procedure SetOnAfterSetColumnCalculation(const Value: TTMSFNCDataGridDataAfterSetColumnCalculation);
    procedure SetOnAfterSetRowCalculation(const Value: TTMSFNCDataGridDataAfterSetRowCalculation);
    procedure SetOnBeforeSetColumnCalculation(const Value: TTMSFNCDataGridDataBeforeSetColumnCalculation);
    procedure SetOnBeforeSetRowCalculation(const Value: TTMSFNCDataGridDataBeforeSetRowCalculation);
    function GetFilterActions: TTMSFNCDataGridDataFilterActions;
    procedure SetFilterActions(const Value: TTMSFNCDataGridDataFilterActions);
    function GetHeaderGroups: TTMSFNCDataGridHeaderGroups;
    function GetCalculateGraphics: TTMSFNCGraphics;
    function GetHasFilter: Boolean;
    function GetGroupInfo: TTMSFNCDataGridDataGroupInfo;
  protected
    procedure CustomReadProperty(AObject: TObject; APropertyName: string; APropertyKind: TTypeKind; AReader: TTMSFNCJSONReader; var ACanRead: Boolean); virtual;
    procedure CustomWriteProperty(AObject: TObject; APropertyName: string; APropertyKind: TTypeKind; AWriter: TTMSFNCJSONWriter; var ACanWrite: Boolean); virtual;

    procedure SetDataColumnCount(AValue: Integer);
    procedure SetDataRowCount(AValue: Integer);
    procedure ITMSFNCDataBinderGrid.ClearData = DataClearData;
    procedure DataBeginUpdate;
    procedure DataEndUpdate;
    procedure DataClearData;
    function GetDataRowCount: Integer;
    procedure SetDataValue(AColumn, ARow: Integer; AValue: string);
    procedure SetDataHeader(AColumn: Integer; AValue: string);
    procedure DataInsertRow(AInsertPosition: Integer);
    function DataGetItemIndex: Integer; override;
    procedure DataSetItemIndex(AValue: Integer); override;

    {$IFDEF SUPPORTABSTRACT}
    function Clone: TObject;
    function CompareRecords(AField: string; ARecord1, ARecord2: TValue): Integer;
    function CreateDefaultRecord: TValue;
    function GetRecord(AIndex: Integer): TValue;
    function GetRecordCount: Integer;
    function GetRecordIndex(ARecord: TValue): Integer;
    function GetRecordValue(ARecord: TValue; AField: string; AFieldType: TFieldType): ValueType;
    procedure Activate(AValue: Boolean);
    procedure AddRecord(ARecord: TValue);
    procedure DeleteRecord(AIndex: Integer);
    procedure InitializeFieldDefs(AFieldDefs: TFieldDefs);
    procedure InsertRecord(AIndex: Integer; ARecord: TValue);
    procedure SetNotification(ANotifyObject: ITMSFNCDataObjectNotification);
    procedure SetRecordValue(ARecordIndex: Integer; AField: string; AValue: ValueType);
    {$ENDIF}
    {$IFDEF FMXLIB}
    procedure DoAbsoluteChanged; override;
    {$ENDIF}
    function InternalXYToHeaderGroup(X, Y: Double; AHeaderGroups: TTMSFNCDataGridHeaderGroups; ASkipDragHeaderGroup: Boolean; AUseOriginalRect: Boolean): TTMSFNCDataGridHeaderGroup;
    function CanAddLevel(AHeaderGroups: TTMSFNCDataGridHeaderGroups; ASkipHeaderGroup: TTMSFNCDataGridHeaderGroup; ALevel: Integer; ACheckLevel: Boolean): Boolean;
    function DoRootIsFocused(Sender: TObject): Boolean; override;
    function GetCalculationRect: TRectF; override;
    function GetDesignerBottomSize: Double;
    function GetDesignerLeftSize: Double;
    function GetDesignerRightSize: Double;
    function GetDesignerTopSize: Double;
    function GetDocURL: string; override;
    function GetFooterRect: TRectF;
    function GetFooterSize: Double;
    function GetHeaderBarRect: TRectF;
    function GetHeaderRect: TRectF;
    function GetVisualGroupingRect: TRectF;
    function GetHeaderSize: Double;
    function GetHeaderBarSize: Double;
    function GetHintString: string; override;
    function GetVersion: string; override;
    function HasHint: Boolean; override;
    function HandleDragOnHeader(X, Y: Single): Boolean; virtual;
    function HandleDropOnHeader(X, Y: Single; AShift: TShiftState): Boolean; virtual;
    function StartDragOnHeader(X, Y: Single): Boolean; virtual;
    function PositionToHeaderGroupIndex(AHeaderGroups: TTMSFNCDataGridHeaderGroups; ASkipHeaderGroup: TTMSFNCDataGridHeaderGroup; ALevel: Integer; X: Single): Integer;
    function PositionToHeaderGroupLevel(AHeaderGroups: TTMSFNCDataGridHeaderGroups; ASkipHeaderGroup: TTMSFNCDataGridHeaderGroup; Y: Single): Integer;
    function HeaderGroupsToGroupInfo(AHeaderGroups: TTMSFNCDataGridHeaderGroups; var AGroupInfo: TTMSFNCDataGridDataGroupInfo): Boolean;
    function GroupInfoToHeaderGroups(AIsGrouped: Boolean; AGroupInfo: TTMSFNCDataGridDataGroupInfo; AHeaderGroups: TTMSFNCDataGridHeaderGroups;
      ASkipHeaderGroup: TTMSFNCDataGridHeaderGroup; var AAutoHeaderSize: Single; var AHeaderLevelRects: TArray<TRectF>): Boolean; overload;
    function GroupInfoToHeaderGroups(AIsGrouped: Boolean; AGroupInfo: TTMSFNCDataGridDataGroupInfo; AHeaderGroups: TTMSFNCDataGridHeaderGroups): Boolean; overload;
    procedure DoHeaderButtonClick(Sender: TObject);
    procedure ApplyStyle; override;
    procedure CalculateHeader; virtual;
    procedure CalculateHeaderVisualGrouping; virtual;
    procedure CalculateHeaderBar; virtual;
    procedure ChangeDPIScale(M, D: Integer); override;
    procedure DestroyGraphicElements; override;
    procedure DoCustomizeHeaderGroup(AHeaderGroup: TTMSFNCDataGridHeaderGroup); virtual;
    procedure DoBeforeDrawHeader(AGraphics: TTMSFNCGraphics; ARect: TRectF; var ACanDraw: Boolean); virtual;
    procedure DoAfterDrawHeader(AGraphics: TTMSFNCGraphics; ARect: TRectF); virtual;
    procedure DoBeforeDrawFooter(AGraphics: TTMSFNCGraphics; ARect: TRectF; var ACanDraw: Boolean); virtual;
    procedure DoAfterDrawFooter(AGraphics: TTMSFNCGraphics; ARect: TRectF); virtual;
    procedure DoAfterDrawHeaderGroupLevel(AGraphics: TTMSFNCGraphics; ARect: TRectF; ALevel: Integer; AActive: Boolean); virtual;
    procedure DoBeforeDrawHeaderGroupLevel(AGraphics: TTMSFNCGraphics; ARect: TRectF; ALevel: Integer; AActive: Boolean; var ACanDraw: Boolean); virtual;
    procedure DoAfterDrawHeaderGroup(Sender: TObject; AGraphics: TTMSFNCGraphics); virtual;
    procedure DoBeforeDrawHeaderGroup(Sender: TObject; AGraphics: TTMSFNCGraphics; var ACanDraw: Boolean); virtual;
    procedure DoBeforeDropHeaderGroup(AColumn: Integer; var ACanDrop: Boolean); virtual;
    procedure DoCanDragHeaderGroup(AColumn: Integer; var ACanDrag: Boolean); virtual;
    procedure DoAfterDropHeaderGroup(AColumn: Integer); virtual;
    procedure DoBeforeSortHeaderGroup(AColumn: Integer; var ACanSort: Boolean); virtual;
    procedure DoAfterSortHeaderGroup(AColumn: Integer); virtual;
    procedure DoBeforeCloseHeaderGroup(AColumn: Integer; var ACanClose: Boolean); virtual;
    procedure DoAfterCloseHeaderGroup(AColumn: Integer); virtual;
    procedure DoHeaderGroupClick(AColumn: Integer); virtual;
    procedure DoUngroup; virtual;
    procedure DoGroup; virtual;
    procedure DoEnter; override;
    procedure DoExit; override;
    procedure DoRootFocus(Sender: TObject); override;
    procedure DoRootBeforeCalculate(Sender: TObject); override;
    procedure DoRootUpdateCursor(Sender: TObject; ACursor: TCursor); override;
    procedure DrawFooter(AGraphics: TTMSFNCGraphics);
    procedure DrawFooterBackground(AGraphics: TTMSFNCGraphics);
    procedure DrawHeader(AGraphics: TTMSFNCGraphics);
    procedure DrawHeaderBackground(AGraphics: TTMSFNCGraphics);
    procedure DrawHeaderBar(AGraphics: TTMSFNCGraphics);
    procedure DrawHeaderGroups(AGraphics: TTMSFNCGraphics; AHeaderGroups: TTMSFNCDataGridHeaderGroups);
    procedure FirstPage;
    procedure FooterChangedRebuild(Sender: TObject);
    procedure GoToPage(APageIndex: Integer);
    procedure GroupActiveHeaders;
    procedure HandleDblClick(X, Y: Single); override;
    procedure HandleDialogKey(var Key: Word; Shift: TShiftState); override;
    procedure HandleDragDrop(const Source: TObject; const Point: TPointF); override;
    procedure HandleDragOver(const Source: TObject; const Point: TPointF; var Accept: Boolean); override;
    procedure HandleKeyDown(var Key: Word; Shift: TShiftState); override;
    procedure HandleKeyPress(var Key: Char); override;
    procedure HandleKeyUp(var Key: Word; Shift: TShiftState); override;
    procedure HandleMouseDown(Button: TTMSFNCMouseButton; Shift: TShiftState; X, Y: Single); override;
    procedure HandleMouseEnter; override;
    procedure HandleMouseLeave; override;
    procedure HandleMouseMove(Shift: TShiftState; X, Y: Single); override;
    procedure HandleMouseUp(Button: TTMSFNCMouseButton; Shift: TShiftState; X, Y: Single); override;
    procedure HandleMouseWheel(Shift: TShiftState; WheelDelta: Integer; var Handled: Boolean); override;
    procedure HeaderChangedRebuild(Sender: TObject);
    procedure HandleEndDropOnHeader;
    procedure HandleEndDragOnHeader(X, Y: Single);
    procedure InitializeHeaderGroup(AGraphics: TTMSFNCGraphics; AHeaderGroup: TTMSFNCDataGridHeaderGroup; AColumn: Integer; ALevel: Integer; AX, AY: Single);
    procedure LastPage;
    procedure NextPage;
    procedure Notification(AComponent: TComponent; Operation: TOperation); override;
    procedure PreviousPage;
    procedure ResetToDefaultStyle; override;
    procedure SetAdaptToStyle(const Value: Boolean); override;
    procedure SetFonts(ASetType: TTMSFNCAppearanceGlobalFontType);
    procedure Calculate; virtual;
    property Adapter: TTMSFNCDataGridDataAdapter read GetAdapter write SetAdapter;
    property BitmapContainer: TTMSFNCBitmapContainer read GetBitmapContainer write SetBitmapContainer;
    property BitmapNames[AColumn, ARow: Integer]: string read GetBitmapNames;
    property Bitmaps[AColumn, ARow: Integer]: TTMSFNCBitmap read GetBitmaps;
    property Booleans[AColumn, ARow: Integer]: Boolean read GetBooleans write SetBooleans;
    property BottomRow: Integer read GetBottomRow;
    property CalculateGraphics: TTMSFNCGraphics read GetCalculateGraphics;
    property CellAppearance: TTMSFNCDataGridCellAppearance read GetCellAppearance write SetCellAppearance;
    property CellData[AColumn, ARow: Integer]: TTMSFNCDataGridCellItem read GetCellData;
    property CellPopupMenu: TTMSFNCDataGridPopupMenu read GetCellPopupMenu write SetCellPopupMenu;
    property Cells[AColumn, ARow: Integer]: TTMSFNCDataGridCellValue read GetCells write SetCells;
    property Colors[AColumn, ARow: Integer]: TTMSFNCGraphicsColor read GetColors write SetColors;
    property ColumnCalculations[AColumn: Integer; AName: string]: TTMSFNCDataGridDataColumnCalculationArray read GetColumnCalculation write SetColumnCalculation;
    property ColumnCount: Integer read GetColumnCount write SetColumnCount default 4;
    property ColumnOrders[AColumn: Integer]: Integer read GetColumnOrder;
    property ColumnPositions[AColumn: Integer]: Double read GetColumnPos;
    property ColumnReverseOrders[AColumn: Integer]: Integer read GetColumnReverseOrder;
    property Columns: TTMSFNCDataGridColumns read GetColumns write SetColumns;
    property ColumnsEditor: TTMSFNCDataGridColumnsEditor read GetColumnsEditor;
    property ColumnWidths[AColumn: Integer]: Double read GetColumnWidths write SetColumnWidths;
    property Comments[AColumn, ARow: Integer]: string read GetComments write SetComments;
    property ControlAligns[AColumn, ARow: Integer]: TTMSFNCDataGridCellControlAlign read GetControlAligns write SetControlAligns;
    property ControlHeights[AColumn, ARow: Integer]: Integer read GetControlHeights write SetControlHeights;
    property ControlMargins[AColumn, ARow: Integer]: TTMSFNCMargins read GetControlMargins;
    property ControlPositions[AColumn, ARow: Integer]: TTMSFNCDataGridCellControlPosition read GetControlPositions write SetControlPositions;
    property Controls[AColumn, ARow: Integer]: TTMSFNCDataGridCellControl read GetControls write SetControls;
    property ControlTexts[AColumn, ARow: Integer]: string read GetControlTexts write SetControlTexts;
    property ControlWidths[AColumn, ARow: Integer]: Integer read GetControlWidths write SetControlWidths;
    property RowViewMode: TTMSFNCDataGridRowViewMode read GetRowViewMode write SetRowViewMode default grvmTotalRows;
    property ColumnViewMode: TTMSFNCDataGridColumnViewMode read GetColumnViewMode write SetColumnViewMode default gcvmTotalColumns;
    property CustomHorizontalScrollBar: TTMSFNCScrollBar read GetCustomHorizontalScrollBar;
    property CustomScrollBars: Boolean read GetCustomScrollBars write SetCustomScrollBars;
    property CustomVerticalScrollBar: TTMSFNCScrollBar read GetCustomVerticalScrollBar;
    property DefaultColumnWidth: Double read GetDefaultColumnWidth write SetDefaultColumnWidth stored IsDefaultColumnWidthStored nodefault;
    property DefaultRowHeight: Double read GetDefaultRowHeight write SetDefaultRowHeight stored IsDefaultRowHeightStored nodefault;
    property Designer: Boolean read FDesigner write SetDesigner default True;
    property DragCell: TTMSFNCDataGridCellCoord read GetDragCell;
    property DragAppearance: TTMSFNCDataGridDragAppearance read GetDragAppearance write SetDragAppearance;
    property EditingCell: TTMSFNCDataGridCellCoord read GetEditingCell;
    property Filter: TTMSFNCDataGridDataFilter read GetFilter;
    property FilterActions: TTMSFNCDataGridDataFilterActions read GetFilterActions write SetFilterActions;
    property FilterAppearance: TTMSFNCDataGridFilterAppearance read GetFilterAppearance write SetFilterAppearance;
    property FilterBuilder: TTMSFNCFilterBuilder read GetFilterBuilder;
    property FilterEditors[AColumn, ARow: Integer]: TTMSFNCDataGridCellControl read GetFilterEditors;
    property FilteredRowCount: Integer read GetFilteredRowCount;
    property FixedBottomRowCount: Integer read GetFixedBottomRowCount write SetFixedBottomRowCount default 0;
    property FixedColumnCount: Integer read GetFixedColumnCount write SetFixedColumnCount default 0;
    property FixedRightColumnCount: Integer read GetFixedRightColumnCount write SetFixedRightColumnCount default 0;
    property FixedRowCount: Integer read GetFixedRowCount write SetFixedRowCount default 1;
    property Floats[AColumn, ARow: Integer]: Double read GetFloats write SetFloats;
    property FocusedCell: TTMSFNCDataGridCellCoord read GetFocusedCell write SetFocusedCell;
    property FontColors[AColumn, ARow: Integer]: TTMSFNCGraphicsColor read GetFontColors write SetFontColors;
    property FontNames[AColumn, ARow: Integer]: string read GetFontNames write SetFontNames;
    property FontSizes[AColumn, ARow: Integer]: Single read GetFontSizes write SetFontSizes;
    property FontStyles[AColumn, ARow: Integer]: TFontStyles read GetFontStyles write SetFontStyles;
    property FooterRect: TRectF read GetFooterRect;
    property Footer: TTMSFNCDataGridFooter read FFooter write SetFooter;
    property FreezeColumnCount: Integer read GetFreezeColumnCount write SetFreezeColumnCount default 0;
    property FreezeRowCount: Integer read GetFreezeRowCount write SetFreezeRowCount default 0;
    property GlobalFont: TTMSFNCAppearanceGlobalFont read GetGlobalFont write SetGlobalFont;
    property Grids[AColumn, ARow: Integer]: TTMSFNCDataGridRenderer read GetGrids;
    property GroupInfo: TTMSFNCDataGridDataGroupInfo read GetGroupInfo;
    property HeaderRect: TRectF read GetHeaderRect;
    property HeaderVisualGroupingRect: TRectF read GetHeaderBarRect;
    property HeaderBarRect: TRectF read GetHeaderBarRect;
    property HasFilter: Boolean read GetHasFilter;
    property HasFilterEditor[AColumn, ARow: Integer]: Boolean read GetHasFilterEditor;
    property HasLayout[AColumn, ARow: Integer]: Boolean read GetHasLayout;
    property HasRowOrder[ARow: Integer]: Boolean read GetHasRowOrder;
    property Header: TTMSFNCDataGridHeader read GetHeader write SetHeader;
    property HeaderGroups: TTMSFNCDataGridHeaderGroups read GetHeaderGroups;
    property HiddenColumnCount: Integer read GetHiddenColumnCount;
    property HiddenColumns[AColumn: Integer]: Boolean read GetHiddenColumns write SetHiddenColumns;
    property HiddenColumnWidths[AColumn: Integer]: Double read GetHiddenColumnWidths;
    property HiddenRowCount: Integer read GetHiddenRowCount;
    property HiddenRowHeights[ARow: Integer]: Double read GetHiddenRowHeights;
    property HiddenRows[ARow: Integer]: Boolean read GetHiddenRows write SetHiddenRows;
    property Hints[AColumn, ARow: Integer]: string read GetHints write SetHints;
    property HorizontalScrollBar: TScrollBar read GetHorizontalScrollBar;
    property HorizontalScrollBarVisible: Boolean read GetHorizontalScrollBarVisible write SetHorizontalScrollBarVisible default True;
    property Icons: TTMSFNCDataGridIcons read GetIcons write SetIcons;
    property Ints[AColumn, ARow: Integer]: Int64 read GetInts write SetInts;
    property Layouts[AColumn, ARow: Integer]: TTMSFNCDataGridCellLayout read GetLayouts;
    property LeftColumn: Integer read GetLeftColumn write SetLeftColumn;
    property Link: TTMSFNCDataGridRenderer read GetLink;
    property ManagedObjects[AColumn, ARow: Integer]: TObject read GetManagedObjects write SetManagedObjects;
    property MergedColumns[AColumn: Integer]: Boolean read GetMergedColumns write SetMergedColumns;
    property MergedRows[ARow: Integer]: Boolean read GetMergedRows write SetMergedRows;
    property Objects[AColumn, ARow: Integer]: TObject read GetObjects write SetObjects;
    property OnAfterDrawHeaderGroup: TTMSFNCDataGridAfterDrawHeaderGroupEvent read FOnAfterDrawHeaderGroup write FOnAfterDrawHeaderGroup;
    property OnBeforeDrawHeaderGroup: TTMSFNCDataGridBeforeDrawHeaderGroupEvent read FOnBeforeDrawHeaderGroup write FOnBeforeDrawHeaderGroup;
    property OnAfterDrawHeader: TTMSFNCDataGridAfterDrawHeaderEvent read FOnAfterDrawHeader write FOnAfterDrawHeader;
    property OnBeforeDrawHeader: TTMSFNCDataGridBeforeDrawHeaderEvent read FOnBeforeDrawHeader write FOnBeforeDrawHeader;
    property OnAfterDrawFooter: TTMSFNCDataGridAfterDrawFooterEvent read FOnAfterDrawFooter write FOnAfterDrawFooter;
    property OnBeforeDrawFooter: TTMSFNCDataGridBeforeDrawFooterEvent read FOnBeforeDrawFooter write FOnBeforeDrawFooter;
    property OnAfterDrawHeaderGroupLevel: TTMSFNCDataGridAfterDrawHeaderGroupLevelEvent read FOnAfterDrawHeaderGroupLevel write FOnAfterDrawHeaderGroupLevel;
    property OnBeforeDrawHeaderGroupLevel: TTMSFNCDataGridBeforeDrawHeaderGroupLevelEvent read FOnBeforeDrawHeaderGroupLevel write FOnBeforeDrawHeaderGroupLevel;
    property OnBeforeDropHeaderGroup: TTMSFNCDataGridBeforeDropHeaderGroupEvent read FOnBeforeDropHeaderGroup write FOnBeforeDropHeaderGroup;
    property OnCanDragHeaderGroup: TTMSFNCDataGridCanDragHeaderGroupEvent read FOnCanDragHeaderGroup write FOnCanDragHeaderGroup;
    property OnAfterDropHeaderGroup: TTMSFNCDataGridAfterDropHeaderGroupEvent read FOnAfterDropHeaderGroup write FOnAfterDropHeaderGroup;
    property OnBeforeSortHeaderGroup: TTMSFNCDataGridBeforeSortHeaderGroupEvent read FOnBeforeSortHeaderGroup write FOnBeforeSortHeaderGroup;
    property OnAfterSortHeaderGroup: TTMSFNCDataGridAfterSortHeaderGroupEvent read FOnAfterSortHeaderGroup write FOnAfterSortHeaderGroup;
    property OnBeforeCloseHeaderGroup: TTMSFNCDataGridBeforeCloseHeaderGroupEvent read FOnBeforeCloseHeaderGroup write FOnBeforeCloseHeaderGroup;
    property OnAfterCloseHeaderGroup: TTMSFNCDataGridAfterCloseHeaderGroupEvent read FOnAfterCloseHeaderGroup write FOnAfterCloseHeaderGroup;
    property OnHeaderGroupClick: TTMSFNCDataGridHeaderGroupClickEvent read FOnHeaderGroupClick write FOnHeaderGroupClick;
    property OnAfterApplyFilter: TTMSFNCDataGridAfterApplyFilterEvent read GetOnAfterApplyFilter write SetOnAfterApplyFilter;
    property OnAfterCloseInplaceEditor: TTMSFNCDataGridAfterCloseInplaceEditorEvent read GetOnAfterCloseInplaceEditor write SetOnAfterCloseInplaceEditor;
    property OnAfterCopyToClipboard: TTMSFNCDataGridAfterClipboardEvent read GetOnAfterCopyToClipboard write SetOnAfterCopyToClipboard;
    property OnAfterCreateFilterEditor: TTMSFNCDataGridAfterCreateFilterEditorEvent read GetOnAfterCreateFilterEditor write SetOnAfterCreateFilterEditor;
    property OnAfterCutToClipboard: TTMSFNCDataGridAfterClipboardEvent read GetOnAfterCutToClipboard write SetOnAfterCutToClipboard;
    property OnAfterDrawCell: TTMSFNCDataGridAfterDrawCellEvent read GetOnAfterDrawCell write SetOnAfterDrawCell;
    property OnAfterDrawEmptySpace: TTMSFNCDataGridAfterDrawEmptySpaceEvent read GetOnAfterDrawEmptySpace write SetOnAfterDrawEmptySpace;
    property OnAfterDrawGrid: TTMSFNCDataGridAfterDrawGridEvent read GetOnAfterDrawGrid write SetOnAfterDrawGrid;
    property OnAfterLookup: TTMSFNCDataGridAfterLookupEvent read GetOnAfterLookup write SetOnAfterLookup;
    property OnAfterOpenInplaceEditor: TTMSFNCDataGridAfterOpenInplaceEditorEvent read GetOnAfterOpenInplaceEditor write SetOnAfterOpenInplaceEditor;
    property OnAfterPasteFromClipboard: TTMSFNCDataGridAfterClipboardEvent read GetOnAfterPasteFromClipboard write SetOnAfterPasteFromClipboard;
    property OnAfterSetColumnCalculation: TTMSFNCDataGridDataAfterSetColumnCalculation read GetOnAfterSetColumnCalculation write SetOnAfterSetColumnCalculation;
    property OnAfterSetRowCalculation: TTMSFNCDataGridDataAfterSetRowCalculation read GetOnAfterSetRowCalculation write SetOnAfterSetRowCalculation;
    property OnAfterShowFilter: TTMSFNCDataGridAfterShowFilterEvent read GetOnAfterShowFilter write SetOnAfterShowFilter;
    property OnAfterShowFilterDialog: TTMSFNCDataGridAfterShowFilterDialogEvent read GetOnAfterShowFilterDialog write SetOnAfterShowFilterDialog;
    property OnAfterShowFindDialog: TTMSFNCDataGridAfterShowFindDialogEvent read GetOnAfterShowFindDialog write SetOnAfterShowFindDialog;
    property OnAfterShowReplaceDialog: TTMSFNCDataGridAfterShowReplaceDialogEvent read GetOnAfterShowReplaceDialog write SetOnAfterShowReplaceDialog;
    property OnAfterSortColumn: TTMSFNCDataGridAfterSortColumnEvent read GetOnAfterSortColumn write SetOnAfterSortColumn;
    property OnAppendColumn: TTMSFNCDataGridAppendColumnEvent read GetOnAppendColumn write SetOnAppendColumn;
    property OnAppendRow: TTMSFNCDataGridAppendRowEvent read GetOnAppendRow write SetOnAppendRow;
    property OnBeforeApplyFilter: TTMSFNCDataGridBeforeApplyFilterEvent read GetOnBeforeApplyFilter write SetOnBeforeApplyFilter;
    property OnBeforeCloseInplaceEditor: TTMSFNCDataGridBeforeCloseInplaceEditorEvent read GetOnBeforeCloseInplaceEditor write SetOnBeforeCloseInplaceEditor;
    property OnBeforeCopyToClipboard: TTMSFNCDataGridBeforeClipboardEvent read GetOnBeforeCopyToClipboard write SetOnBeforeCopyToClipboard;
    property OnBeforeCreateFilterEditor: TTMSFNCDataGridBeforeCreateFilterEditorEvent read GetOnBeforeCreateFilterEditor write SetOnBeforeCreateFilterEditor;
    property OnBeforeCutToClipboard: TTMSFNCDataGridBeforeClipboardEvent read GetOnBeforeCutToClipboard write SetOnBeforeCutToClipboard;
    property OnBeforeDrawCell: TTMSFNCDataGridBeforeDrawCellEvent read GetOnBeforeDrawCell write SetOnBeforeDrawCell;
    property OnBeforeDrawEmptySpace: TTMSFNCDataGridBeforeDrawEmptySpaceEvent read GetOnBeforeDrawEmptySpace write SetOnBeforeDrawEmptySpace;
    property OnBeforeLookup: TTMSFNCDataGridBeforeLookupEvent read GetOnBeforeLookup write SetOnBeforeLookup;
    property OnBeforeOpenInplaceEditor: TTMSFNCDataGridBeforeOpenInplaceEditorEvent read GetOnBeforeOpenInplaceEditor write SetOnBeforeOpenInplaceEditor;
    property OnBeforePasteFromClipboard: TTMSFNCDataGridBeforeClipboardEvent read GetOnBeforePasteFromClipboard write SetOnBeforePasteFromClipboard;
    property OnBeforeSetColumnCalculation: TTMSFNCDataGridDataBeforeSetColumnCalculation read GetOnBeforeSetColumnCalculation write SetOnBeforeSetColumnCalculation;
    property OnBeforeSetRowCalculation: TTMSFNCDataGridDataBeforeSetRowCalculation read GetOnBeforeSetRowCalculation write SetOnBeforeSetRowCalculation;
    property OnBeforeShowFilter: TTMSFNCDataGridBeforeShowFilterEvent read GetOnBeforeShowFilter write SetOnBeforeShowFilter;
    property OnBeforeShowFilterDialog: TTMSFNCDataGridBeforeShowFilterDialogEvent read GetOnBeforeShowFilterDialog write SetOnBeforeShowFilterDialog;
    property OnBeforeShowFindDialog: TTMSFNCDataGridBeforeShowFindDialogEvent read GetOnBeforeShowFindDialog write SetOnBeforeShowFindDialog;
    property OnBeforeShowReplaceDialog: TTMSFNCDataGridBeforeShowReplaceDialogEvent read GetOnBeforeShowReplaceDialog write SetOnBeforeShowReplaceDialog;
    property OnBeforeSortColumn: TTMSFNCDataGridBeforeSortColumnEvent read GetOnBeforeSortColumn write SetOnBeforeSortColumn;
    property OnCanAppendColumn: TTMSFNCDataGridCanAppendColumnEvent read GetOnCanAppendColumn write SetOnCanAppendColumn;
    property OnCanAppendRow: TTMSFNCDataGridCanAppendRowEvent read GetOnCanAppendRow write SetOnCanAppendRow;
    property OnCanDeleteColumn: TTMSFNCDataGridCanDeleteColumnEvent read GetOnCanDeleteColumn write SetOnCanDeleteColumn;
    property OnCanDeleteRow: TTMSFNCDataGridCanDeleteRowEvent read GetOnCanDeleteRow write SetOnCanDeleteRow;
    property OnCanDragColumn: TTMSFNCDataGridCanDragColumnEvent read GetOnCanDragColumn write SetOnCanDragColumn;
    property OnCanDragRow: TTMSFNCDataGridCanDragRowEvent read GetOnCanDragRow write SetOnCanDragRow;
    property OnCanDropColumn: TTMSFNCDataGridCanDropColumnEvent read GetOnCanDropColumn write SetOnCanDropColumn;
    property OnCanDropRow: TTMSFNCDataGridCanDropRowEvent read GetOnCanDropRow write SetOnCanDropRow;
    property OnCanEditCell: TTMSFNCDataGridCanEditCellEvent read GetOnCanEditCell write SetOnCanEditCell;
    property OnCanInsertColumn: TTMSFNCDataGridCanInsertColumnEvent read GetOnCanInsertColumn write SetOnCanInsertColumn;
    property OnCanInsertRow: TTMSFNCDataGridCanInsertRowEvent read GetOnCanInsertRow write SetOnCanInsertRow;
    property OnCanSizeColumn: TTMSFNCDataGridCanSizeColumnEvent read GetOnCanSizeColumn write SetOnCanSizeColumn;
    property OnCanSizeRow: TTMSFNCDataGridCanSizeRowEvent read GetOnCanSizeRow write SetOnCanSizeRow;
    property OnCellAfterNodeCollapse: TTMSFNCDataGridCellAfterNodeCollapseEvent read GetOnCellAfterNodeCollapse write SetOnCellAfterNodeCollapse;
    property OnCellAfterNodeExpand: TTMSFNCDataGridCellAfterNodeExpandEvent read GetOnCellAfterNodeExpand write SetOnCellAfterNodeExpand;
    property OnCellAnchorClick: TTMSFNCDataGridCellAnchorClickEvent read GetOnCellAnchorClick write SetOnCellAnchorClick;
    property OnCellBeforeNodeCollapse: TTMSFNCDataGridCellBeforeNodeCollapseEvent read GetOnCellBeforeNodeCollapse write SetOnCellBeforeNodeCollapse;
    property OnCellBeforeNodeExpand: TTMSFNCDataGridCellBeforeNodeExpandEvent read GetOnCellBeforeNodeExpand write SetOnCellBeforeNodeExpand;
    property OnCellBeforeShowPopupMenu: TTMSFNCDataGridCellBeforeShowPopupMenuEvent read GetOnCellBeforeShowPopupMenu write SetOnCellBeforeShowPopupMenu;
    property OnCellButtonClick: TTMSFNCDataGridCellClickEvent read GetOnCellButtonClick write SetOnCellButtonClick;
    property OnCellCheckBoxChange: TTMSFNCDataGridCellClickEvent read GetOnCellCheckBoxChange write SetOnCellCheckBoxChange;
    property OnCellClick: TTMSFNCDataGridCellClickEvent read GetOnCellClick write SetOnCellClick;
    property OnCellDblClick: TTMSFNCDataGridCellClickEvent read GetOnCellDblClick write SetOnCellDblClick;
    property OnCellEditGetData: TTMSFNCDataGridCellEditGetDataEvent read GetOnCellEditGetData write SetOnCellEditGetData;
    property OnCellEditSetData: TTMSFNCDataGridCellEditSetDataEvent read GetOnCellEditSetData write SetOnCellEditSetData;
    property OnCellEditValidateData: TTMSFNCDataGridCellEditValidateDataEvent read GetOnCellEditValidateData write SetOnCellEditValidateData;
    property OnCellRadioButtonChange: TTMSFNCDataGridCellClickEvent read GetOnCellRadioButtonChange write SetOnCellRadioButtonChange;
    property OnCellRightClick: TTMSFNCDataGridCellClickEvent read GetOnCellRightClick write SetOnCellRightClick;
    property OnColumnDropped: TTMSFNCDataGridColumnDroppedEvent read GetOnColumnDropped write SetOnColumnDropped;
    property OnColumnSized: TTMSFNCDataGridColumnSizedEvent read GetOnColumnSized write SetOnColumnSized;
    property OnCreateCustomFilterEditor: TTMSFNCDataGridCreateCustomFilterEditorEvent read GetOnCreateCustomFilterEditor write SetOnCreateCustomFilterEditor;
    property OnCustomColumnCalculation: TTMSFNCDataGridDataCustomColumnCalculationEvent read GetOnCustomColumnCalculation write SetOnCustomColumnCalculation;
    property OnCustomCompare: TTMSFNCDataGridDataCustomCompareEvent read GetOnCustomCompare write SetOnCustomCompare;
    property OnCustomizeFilterPopupEditor: TTMSFNCDataGridCustomizeFilterPopupEditorEvent read GetOnCustomizeFilterPopupEditor write SetOnCustomizeFilterPopupEditor;
    property OnCustomizeHeaderGroup: TTMSFNCDataGridCustomizeHeaderGroupEvent read FOnCustomizeHeaderGroup write FOnCustomizeHeaderGroup;
    property OnCustomRowCalculation: TTMSFNCDataGridDataCustomRowCalculationEvent read GetOnCustomRowCalculation write SetOnCustomRowCalculation;
    property OnDeleteColumn: TTMSFNCDataGridDeleteColumnEvent read GetOnDeleteColumn write SetOnDeleteColumn;
    property OnDeleteRow: TTMSFNCDataGridDeleteRowEvent read GetOnDeleteRow write SetOnDeleteRow;
    property OnDropFiles: TTMSFNCDataGridDropFilesEvent read GetOnDropFiles write SetOnDropFiles;
    property OnGetCellClass: TTMSFNCDataGridGetCellClassEvent read GetOnGetCellClass write SetOnGetCellClass;
    property OnGetCellData: TTMSFNCDataGridDataGetCellDataEvent read GetOnGetCellData write SetOnGetCellData;
    property OnGetCellDataProperties: TTMSFNCDataGridDataGetCellDataPropertiesEvent read GetOnGetCellDataProperties write SetOnGetCellDataProperties;
    property OnGetCellDisplayValue: TTMSFNCDataGridDataGetCellDisplayValueEvent read GetOnGetCellDisplayValue write SetOnGetCellDisplayValue;
    property OnGetCellFormatting: TTMSFNCDataGridDataGetCellFormattingEvent read GetOnGetCellFormatting write SetOnGetCellFormatting;
    property OnGetCellHint: TTMSFNCDataGridGetCellHintEvent read GetOnGetCellHint write SetOnGetCellHint;
    property OnGetCellLayout: TTMSFNCDataGridGetCellLayoutEvent read GetOnGetCellLayout write SetOnGetCellLayout;
    property OnGetCellLayoutHasPriority: TTMSFNCDataGridGetCellLayoutHasPriorityEvent read GetOnGetCellLayoutHasPriority write SetOnGetCellLayoutHasPriority;
    property OnGetCellProperties: TTMSFNCDataGridGetCellPropertiesEvent read GetOnGetCellProperties write SetOnGetCellProperties;
    property OnGetColumnCalculationOptions: TTMSFNCDataGridDataGetColumnCalculationOptionsEvent read GetOnGetColumnCalculationOptions write SetOnGetColumnCalculationOptions;
    property OnGetCustomGroup: TTMSFNCDataGridDataGetCustomGroupEvent read GetOnGetCustomGroup write SetOnGetCustomGroup;
    property OnGetGroupOptions: TTMSFNCDataGridDataGetGroupOptionsEvent read GetOnGetGroupOptions write SetOnGetGroupOptions;
    property OnGetInplaceEditorClass: TTMSFNCDataGridGetInplaceEditorClassEvent read GetOnGetInplaceEditorClass write SetOnGetInplaceEditorClass;
    property OnGetInplaceEditorProperties: TTMSFNCDataGridGetInplaceEditorPropertiesEvent read GetOnGetInplaceEditorProperties write SetOnGetInplaceEditorProperties;
    property OnGetInplaceEditorRect: TTMSFNCDataGridGetInplaceEditorRectEvent read GetOnGetInplaceEditorRect write SetOnGetInplaceEditorRect;
    property OnGetInplaceEditorTarget: TTMSFNCDataGridGetInplaceEditorTargetEvent read GetOnGetInplaceEditorTarget write SetOnGetInplaceEditorTarget;
    property OnGetInplaceEditorType: TTMSFNCDataGridGetInplaceEditorTypeEvent read GetOnGetInplaceEditorType write SetOnGetInplaceEditorType;
    property OnGetRowCalculationOptions: TTMSFNCDataGridDataGetRowCalculationOptionsEvent read GetOnGetRowCalculationOptions write SetOnGetRowCalculationOptions;
    property OnGetSortOptions: TTMSFNCDataGridDataGetSortOptionsEvent read GetOnGetSortOptions write SetOnGetSortOptions;
    property OnGroup: TNotifyEvent read FOnGroup write FOnGroup;
    property OnHScroll: TTMSFNCDataGridScrollEvent read GetOnHScroll write SetOnHScroll;
    property OnInsertColumn: TTMSFNCDataGridInsertColumnEvent read GetOnInsertColumn write SetOnInsertColumn;
    property OnInsertRow: TTMSFNCDataGridInsertRowEvent read GetOnInsertRow write SetOnInsertRow;
    property OnLoadCellData: TTMSFNCDataGridDataCellDataEvent read GetOnLoadCellData write SetOnLoadCellData;
    property OnLogMessage: TTMSFNCDataGridLogMessageEvent read GetOnLogMessage write SetOnLogMessage;
    property OnNeedFilterDropDownData: TTMSFNCDataGridNeedFilterDropDownData read GetOnNeedFilterDropDownData write SetOnNeedFilterDropDownData;
    property OnRemoveFilter: TTMSFNCDataGridRemoveFilterEvent read GetOnRemoveFilter write SetOnRemoveFilter;
    property OnRowCollapse: TTMSFNCDataGridDataRowCollapseEvent read GetOnRowCollapse write SetOnRowCollapse;
    property OnRowDropped: TTMSFNCDataGridRowDroppedEvent read GetOnRowDropped write SetOnRowDropped;
    property OnRowExpand: TTMSFNCDataGridDataRowExpandEvent read GetOnRowExpand write SetOnRowExpand;
    property OnRowSized: TTMSFNCDataGridRowSizedEvent read GetOnRowSized write SetOnRowSized;
    property OnSaveCellData: TTMSFNCDataGridDataCellDataEvent read GetOnSaveCellData write SetOnSaveCellData;
    property OnSelectCell: TTMSFNCDataGridSelectCellEvent read GetOnSelectCell write SetOnSelectCell;
    property OnSelectingCell: TTMSFNCDataGridSelectingCellEvent read GetOnSelectingCell write SetOnSelectingCell;
    property OnSizeColumn: TTMSFNCDataGridSizeColumnEvent read GetOnSizeColumn write SetOnSizeColumn;
    property OnSizeRow: TTMSFNCDataGridSizeRowEvent read GetOnSizeRow write SetOnSizeRow;
    property OnUngroup: TNotifyEvent read FOnUngroup write FOnUngroup;
    property OnVScroll: TTMSFNCDataGridScrollEvent read GetOnVScroll write SetOnVScroll;
    property Options: TTMSFNCDataGridOptions read GetOptions write SetOptions;
    property PageCount: Integer read GetPageCount;
    property PageIndex: Integer read GetPageIndex write SetPageIndex;
    property ReadOnlys[AColumn, ARow: Integer]: Boolean read GetReadOnlys write SetReadOnlys;
    property RightColumn: Integer read GetRightColumn;
    property RowCalculations[ARow: Integer; AName: string]: TTMSFNCDataGridDataRowCalculationArray read GetRowCalculation write SetRowCalculation;
    property RowCount: Integer read GetRowCount write SetRowCount default 24;
    property RowHeights[ARow: Integer]: Double read GetRowHeights write SetRowHeights;
    property RowNodeChildCount[ARow: Integer]: Integer read GetRowNodeChildCount write SetRowNodeChildCount;
    property RowNodeLevel[ARow: Integer]: Integer read GetRowNodeLevel write SetRowNodeLevel;
    property RowNodeState[ARow: Integer]: TTMSFNCDataGridRowNodeState read GetRowNodeState write SetRowNodeState;
    property RowOrders[ARow: Integer]: Integer read GetRowOrder;
    property RowPositions[ARow: Integer]: Double read GetRowPos;
    property RowReverseOrders[ARow: Integer]: Integer read GetRowReverseOrder;
    property RowText[ARow: Integer]: string read GetRowText write SetRowText;
    property RowTypes[ARow: Integer]: TTMSFNCDataGridRowType read GetRowTypes write SetRowTypes;
    property ScrollMode: TTMSFNCDataGridScrollMode read GetScrollMode write SetScrollMode default gsmPixelScrolling;
    property ScrollUpdate: TTMSFNCDataGridScrollUpdate read GetScrollUpdate write SetScrollUpdate default gsuContinuous;
    property SelectedCellCount: Integer read GetSelectedCellCount;
    property SelectedCells[AColumn, ARow: Integer]: Boolean read GetSelectedCells write SetSelectedCells;
    property SelectedColumnCount: Integer read GetSelectedColumnCount;
    property SelectedColumns[AColumn: Integer]: Boolean read GetSelectedColumns write SetSelectedColumns;
    property SelectedRowCount: Integer read GetSelectedRowCount;
    property SelectedRows[ARow: Integer]: Boolean read GetSelectedRows write SetSelectedRows;
    property Selection: TTMSFNCDataGridCellCoordRange read GetSelection write SetSelection;
    property SortIndexList: TTMSFNCDataGridSortIndexList read GetSortIndexList;
    property StartColumn: Integer read GetStartColumn;
    property StartRow: Integer read GetStartRow;
    property States[AColumn, ARow: Integer]: TTMSFNCDataGridCellState read GetStates;
    property StopColumn: Integer read GetStopColumn;
    property StopRow: Integer read GetStopRow;
    property StoredCells[AColumn, ARow: Integer]: TTMSFNCDataGridCellValue read GetStoredCells;
    property StretchScrollBars: Boolean read GetStretchScrollBars write SetStretchScrollBars default False;
    property Strings[AColumn, ARow: Integer]: string read GetStrings write SetStrings;
    property StrippedCells[AColumn, ARow: Integer]: string read GetStrippedCells;
    property TextAligns[AColumn, ARow: Integer]: TTMSFNCGraphicsTextAlign read GetTextAligns write SetTextAligns;
    property TextAngles[AColumn, ARow: Integer]: Double read GetTextAngles write SetTextAngles;
    property TopRow: Integer read GetTopRow write SetTopRow;
    property TotalColumnWidth: Double read GetTotalColumnWidth;
    property TotalRowHeight: Double read GetTotalRowHeight;
    property Version: string read GetVersion;
    property VerticalScrollBar: TScrollBar read GetVerticalScrollBar;
    property VerticalScrollBarVisible: Boolean read GetVerticalScrollBarVisible write SetVerticalScrollBarVisible default True;
    property VerticalTextAligns[AColumn, ARow: Integer]: TTMSFNCGraphicsTextAlign read GetVerticalTextAligns write SetVerticalTextAligns;
    property VisibleColumnCount: Integer read GetVisibleColumnCount;
    property VisibleRowCount: Integer read GetVisibleRowCount;
    property WordWrapping[AColumn, ARow: Integer]: Boolean read GetWordWrapping write SetWordWrapping;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;

    function AddGrid(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridRenderer; overload;
    function AddGrid(AColumn, ARow: Integer): TTMSFNCDataGridRenderer; overload;
    function BaseCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
    function CancelEditing: Boolean;
    function CanDeleteColumn(AColumn: Integer): Boolean;
    function CanDeleteRow(ARow: Integer): Boolean;
    function CanInsertColumn(AColumn: Integer): Boolean;
    function CanInsertRow(ARow: Integer): Boolean;
    function CanMergeCells(AColumn, ARow, AColumnCount, ARowCount: Integer): Boolean;
    function CanMoveColumn(AFromColumn, AToColumn: Integer): Boolean;
    function CanMoveRow(AFromRow, AToRow: Integer): Boolean;
    function CanSwapColumn(AFromColumn, AToColumn: Integer): Boolean;
    function CanSwapRow(AFromRow, AToRow: Integer): Boolean;
    function ColumnAverage(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnByHeader(AHeader: string): TTMSFNCDataGridColumn;
    function ColumnByName(AName: string): TTMSFNCDataGridColumn;
    function ColumnCustomCalculation(AColumn: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnDistinct(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnIndexByHeader(AHeader: string): Integer;
    function ColumnIndexByName(AName: string): Integer;
    function ColumnMax(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnMin(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnSpan(ACell: TTMSFNCDataGridCellCoord): Integer;
    function ColumnStandardDeviation(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnSum(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function DisplayToRealCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
    function Find(AText: string): TTMSFNCDataGridCellCoord; overload;
    function Find(AText: string; AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord; overload;
    function Find(AText: string; AParameters: TTMSFNCDataGridDataFindParameters; AStartCell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord; overload;
    function FindAll(AText: string; AParameters: TTMSFNCDataGridDataFindParameters; var AResults: Integer): TTMSFNCDataGridCellCoord;
    function FindFirst(AText: string; AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord;
    function FindNext: TTMSFNCDataGridCellCoord;
    function GetCellKind(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellKind;
    function GetCellMergeType(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridDataCellMergeType;
    function GetCellObjectForCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCell;
    function GetCellSize(ACell: TTMSFNCDataGridCellCoord): TSizeF;
    function GetFilterTypeName(AType: TTMSFNCDataGridDataFilterType): string;
    function GetHMax: Single;
    function GetHScrollValue: Single;
    function GetHViewPortSize: Single;
    function GetMaxLevel: Integer;
    function GetParentRow(ARow: Integer): Integer;
    function GetRootRow(ARow: Integer): Integer;
    function GetTotalFixedBottomRowHeight: Double;
    function GetTotalFixedColumnWidth: Double;
    function GetTotalFixedRightColumnWidth: Double;
    function GetTotalFixedRowHeight: Double;
    function GetVMax: Single;
    function GetVScrollValue: Single;
    function GetVViewPortSize: Single;
    function HasRowData(ARow: Integer): Boolean;
    function IsCellBase(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellDisabled(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellDisplayed(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFixed(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFixedRoot(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFreeze(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFreezeFixed(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFreezeRoot(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellHidden(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellMerged(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellSelected(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellValid(ACell: TTMSFNCDataGridCellCoord): Boolean; overload;
    function IsCellValid(AColumn, ARow: Integer): Boolean; overload;
    function IsCellVisible(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsColumnDisplayed(AColumn: Integer): Boolean;
    function IsColumnHidden(AColumn: Integer): Boolean;
    function IsColumnMerged(AColumn: Integer): Boolean;
    function IsColumnValid(AColumn: Integer): Boolean;
    function IsColumnVisible(AColumn: Integer): Boolean;
    function IsEditing: Boolean;
    function IsFixedCellSelected(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsGrouped: Boolean;
    function IsRowBand(ARow: Integer): Boolean;
    function IsRowDisplayed(ARow: Integer): Boolean;
    function IsRowFilter(ARow: Integer): Boolean;
    function IsRowFiltered(ARow: Integer): Boolean;
    function IsRowHidden(ARow: Integer): Boolean;
    function IsRowMerged(ARow: Integer): Boolean;
    function IsRowNode(ARow: Integer): Boolean;
    function IsRowNormal(ARow: Integer): Boolean;
    function IsRowSummary(ARow: Integer): Boolean;
    function IsRowValid(ARow: Integer): Boolean;
    function IsRowVisible(ARow: Integer): Boolean;
    function MatchFilter(ARow: Integer): Boolean;
    function NextSelectableColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function NextSelectableRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function NextVisibleColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function NextVisibleRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function PreviousSelectableColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function PreviousSelectableRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function PreviousVisibleColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function PreviousVisibleRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function RealToDisplayCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
    function Replace(AOriginalValue, ANewValue: string): Integer; overload;
    function Replace(AOriginalValue, ANewValue: string; AParameters: TTMSFNCDataGridDataFindParameters): Integer; overload;
    function RowAverage(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowCustomCalculation(ARow: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowDistinct(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowMax(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowMin(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowSpan(ACell: TTMSFNCDataGridCellCoord): Integer;
    function RowStandardDeviation(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowSum(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function SetGrid(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridRenderer; overload;
    function SetGrid(AColumn, ARow: Integer): TTMSFNCDataGridRenderer; overload;
    function StopEditing: Boolean;
    function TypeOfValue(AValue: TTMSFNCDataGridCellValue): TTypeKind;
    function ValueIsEmpty(AValue: TTMSFNCDataGridCellValue): Boolean;
    function ValueIsFloat(AValue: TTMSFNCDataGridCellValue): Boolean;
    function ValueIsInteger(AValue: TTMSFNCDataGridCellValue): Boolean;
    function ValueIsString(AValue: TTMSFNCDataGridCellValue): Boolean;
    function ValueToBoolean(AValue: TTMSFNCDataGridCellValue): Boolean; overload;
    function ValueToBoolean(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): Boolean; overload;
    function ValueToFloat(AValue: TTMSFNCDataGridCellValue): Double; overload;
    function ValueToFloat(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): Double; overload;
    function ValueToInt64(AValue: TTMSFNCDataGridCellValue): Int64; overload;
    function ValueToInt64(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): Int64; overload;
    function ValueToInteger(AValue: TTMSFNCDataGridCellValue): Integer; overload;
    function ValueToInteger(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): Integer; overload;
    function ValueToString(AValue: TTMSFNCDataGridCellValue): string; overload;
    function ValueToString(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): string; overload;
    function XYToHeaderGroup(X, Y: Double): TTMSFNCDataGridHeaderGroup;
    function XYToHeader(X, Y: Double): Boolean;
    function XYToFooter(X, Y: Double): Boolean;
    function XYToCell(X, Y: Double): TTMSFNCDataGridCellCoord;
    function XYToColumn(X, Y: Double): Integer;
    function XYToRenderer(X, Y: Double): TTMSFNCDataGridRenderer;
    function XYToRow(X, Y: Double): Integer;
    procedure AddBitmap(ACell: TTMSFNCDataGridCellCoord; ABitmap: TTMSFNCBitmap); overload;
    procedure AddBitmap(ACell: TTMSFNCDataGridCellCoord; AName: string); overload;
    procedure AddBitmap(AColumn, ARow: Integer; ABitmap: TTMSFNCBitmap); overload;
    procedure AddBitmap(AColumn, ARow: Integer; AName: string); overload;
    procedure AddBitmapColumn(AColumn: Integer; ABitmap: TTMSFNCBitmap); overload;
    procedure AddBitmapColumn(AColumn: Integer; AName: string); overload;
    procedure AddButton(ACell: TTMSFNCDataGridCellCoord; AText: string = ''); overload;
    procedure AddButton(AColumn, ARow: Integer; AText: string = ''); overload;
    procedure AddButtonColumn(AColumn: Integer; AText: string = '');
    procedure AddCheckBox(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False); overload;
    procedure AddCheckBox(AColumn, ARow: Integer; AValue: Boolean = False); overload;
    procedure AddCheckBoxColumn(AColumn: Integer; AValue: Boolean = False);
    procedure AddColumn;
    procedure AddDataCheckBox(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure AddDataCheckBox(AColumn, ARow: Integer); overload;
    procedure AddDataCheckBoxColumn(AColumn: Integer);
    procedure AddDataProgressBar(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure AddDataProgressBar(AColumn, ARow: Integer); overload;
    procedure AddDataProgressBarColumn(AColumn: Integer);
    procedure AddDataRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure AddDataRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure AddDataRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
    procedure AddFilterEditor(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure AddFilterEditor(AColumn, ARow: Integer); overload;
    procedure AddNodeRow(ARow: Integer; AChildCount: Integer; ALevel: Integer = 0; AMerge: Boolean = True);
    procedure AddProgressBar(ACell: TTMSFNCDataGridCellCoord; AValue: Integer = 0); overload;
    procedure AddProgressBar(AColumn, ARow: Integer; AValue: Integer = 0); overload;
    procedure AddProgressBarColumn(AColumn: Integer; AValue: Integer = 0);
    procedure AddRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure AddRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure AddRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
    procedure AddRow;
    procedure ApplyFilter;
    procedure Assign(Source: TPersistent); override;
    procedure AutoNumberColumn(AColumn: Integer; AStartRow: Integer = 0; AStartValue: Integer = 0);
    procedure AutoNumberRow(ARow: Integer; AStartColumn: Integer = 0; AStartValue: Integer = 0);
    procedure AutoSizeColumn(AColumn: Integer; const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
    procedure AutoSizeColumns(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
    procedure AutoSizeGrid(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APaddingColumn: Integer = 5; const APaddingRow: Integer = 5);
    procedure AutoSizeRow(ARow: Integer; const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
    procedure AutoSizeRows(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
    procedure ClearCells(ACellRange: TTMSFNCDataGridCellCoordRange);
    procedure ClearColumns;
    procedure ClearControlCache;
    procedure ClearFilter;
    procedure ClearNodeRow(ARow: Integer);
    procedure ClearSelection;
    procedure CloseFilterPopup;
    procedure CollapseAllNodes;
    procedure CollapseNode(ARow: Integer);
    procedure ColumnCalculation(AColumn: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure ColumnCalculation(AColumns: TArray<Integer>; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure CopyToClipboard(ASelectedCells: Boolean = True);
    procedure CutToClipboard(ASelectedCells: Boolean = True);
    procedure DeleteColumn(AColumn: Integer);
    procedure DeleteColumns(AColumn, ACount: Integer);
    procedure DeleteRow(ARow: Integer);
    procedure DeleteRows(ARow, ACount: Integer);
    procedure Draw(AGraphics: TTMSFNCGraphics; ARect: TRectF); override;
    procedure EditCell(ACell: TTMSFNCDataGridCellCoord; ACellStart: TTMSFNCDataGridEditCellStart = gcsNone; AInsertKey: Boolean = False; AValue: string = ''; AKey: Word = 0); virtual;
    procedure ExpandAllNodes;
    procedure ExpandNode(ARow: Integer);
    procedure GoToCell(ACell: TTMSFNCDataGridCellCoord; ASelect: Boolean = True);
    procedure Group(AColumn: Integer); overload;
    procedure Group(AColumns: TArray<Integer>; ASortColumns: TArray<Integer> = nil; ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback = nil; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); overload;
    procedure Group(AGroupInfo: TTMSFNCDataGridDataGroupInfo; ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback = nil; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); overload;
    procedure GroupAverage(AColumn: Integer); overload;
    procedure GroupAverage(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupCalculation(AColumn: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure GroupCalculation(AColumns: TArray<Integer>; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure GroupCount(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupCount(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupCustomCalculation(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure GroupCustomCalculation(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure GroupDistinct(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupDistinct(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupMax(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupMax(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupMin(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupMin(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupSort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection); overload;
    procedure GroupSort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); reintroduce; overload;
    procedure GroupStandardDeviation(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupStandardDeviation(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupSum(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupSum(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure HideColumn(AColumn: Integer);
    procedure HideRow(ARow: Integer);
    procedure HilightCells(AHilightText: string; AOptions: TTMSFNCDataGridHilightOptions);
    procedure InitSample;
    procedure InsertColumn(AColumn: Integer);
    procedure InsertColumns(AColumn, ACount: Integer);
    procedure InsertRow(ARow: Integer);
    procedure InsertRows(ARow, ACount: Integer); virtual;
    procedure LinearFill(ADoFixed: Boolean = False);
    procedure LoadFromCSVData(AFileName: string); overload; virtual;
    procedure LoadFromCSVData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromCSVStreamData(AStream: TStream); overload; virtual;
    procedure LoadFromCSVStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromFileData(AFileName: string); overload; virtual;
    procedure LoadFromFileData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromFileStreamData(AStream: TStream); overload; virtual;
    procedure LoadFromFileStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromJSONData(AFileName: string; AEncoding: TEncoding; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil); overload; virtual;
    procedure LoadFromJSONData(AFileName: string; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil); overload; virtual;
    procedure LoadFromJSONStreamData(AStream: TStream; AEncoding: TEncoding; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil ); overload; virtual;
    procedure LoadFromJSONStreamData(AStream: TStream; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil ); overload; virtual;
    procedure LoadFromJSONTextData(AText: string; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil ); virtual;
    procedure LoadFromXMLData(AFileName: string); overload; virtual;
    procedure LoadFromXMLData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromXMLStreamData(AStream: TStream); overload; virtual;
    procedure LoadFromXMLStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure LoadSampleData;
    procedure LogMessage(AMessage: string; AMessageType: TTMSFNCDataGridLogMessageType = glmInfo);
    procedure LoopThroughColumns(ALoopCallback: TTMSFNCDataGridLoopColumnsCallback; AIncludeFixed: Boolean = True);
    procedure LoopThroughRows(ALoopCallback: TTMSFNCDataGridLoopRowsCallback; AIncludeFixed: Boolean = True);
    procedure MarkCells(AHilightText: string; AOptions: TTMSFNCDataGridMarkOptions);
    procedure MergeCells(AColumn, ARow, AColumnCount, ARowCount: Integer);
    procedure MergeColumn(AColumn: Integer);
    procedure MergeColumns(AColumn, ACount: Integer);
    procedure MergeRange(ARange: TTMSFNCDataGridCellCoordRange);
    procedure MergeRow(ARow: Integer);
    procedure MergeRows(ARow, ACount: Integer);
    procedure MoveColumn(AFromColumn, AToColumn: Integer);
    procedure MoveRow(AFromRow, AToRow: Integer);
    procedure PasteFromClipboard;
    procedure RandomFill(ADoFixed: Boolean = False; ARandomNumber: Integer = 100);
    procedure RemoveBitmap(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveBitmap(AColumn, ARow: Integer); overload;
    procedure RemoveButton(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveButton(AColumn, ARow: Integer); overload;
    procedure RemoveCheckBox(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveCheckBox(AColumn, ARow: Integer); overload;
    procedure RemoveDataCheckBox(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveDataCheckBox(AColumn, ARow: Integer); overload;
    procedure RemoveDataRadioButton(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveDataRadioButton(AColumn, ARow: Integer); overload;
    procedure RemoveFilter;
    procedure RemoveFilterEditor(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveFilterEditor(AColumn, ARow: Integer); overload;
    procedure RemoveGrid(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveGrid(AColumn, ARow: Integer); overload;
    procedure RemoveLayout(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveLayout(AColumn, ARow: Integer); overload;
    procedure RemoveNodeRow(ARow: Integer);
    procedure RemoveProgressBar(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveProgressBar(AColumn, ARow: Integer); overload;
    procedure RemoveRadioButton(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveRadioButton(AColumn, ARow: Integer); overload;
    procedure RowCalculation(ARow: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil); overload;
    procedure RowCalculation(ARows: TArray<Integer>; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil); overload;
    procedure ResetDrag;
    procedure SaveToCSVData(AFileName: string); overload;
    procedure SaveToCSVData(AFileName: string; AEncoding: TEncoding); overload;
    procedure SaveToCSVStreamData(AStream: TStream); overload;
    procedure SaveToCSVStreamData(AStream: TStream; AEncoding: TEncoding); overload;
    procedure SaveToFileData(AFileName: string); overload; virtual;
    procedure SaveToFileData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure SaveToFileStreamData(AStream: TStream); overload;
    procedure SaveToFileStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure SaveToHTMLData(AFileName: string); overload;
    procedure SaveToHTMLData(AFileName: string; AEncoding: TEncoding); overload;
    procedure SaveToHTMLStreamData(AStream: TStream); overload;
    procedure SaveToHTMLStreamData(AStream: TStream; AEncoding: TEncoding); overload;
    procedure SaveToImageData(AFileName: string); overload;
    procedure SaveToImageStreamData(AStream: TStream); overload;
    procedure Scroll(AHorizontalPos, AVerticalPos: Double);
    procedure SelectAllCells(AIncludeHidden: Boolean = True);
    procedure SelectCell(ACell: TTMSFNCDataGridCellCoord);
    procedure SetBitmap(ACell: TTMSFNCDataGridCellCoord; ABitmap: TTMSFNCBitmap); overload;
    procedure SetBitmap(ACell: TTMSFNCDataGridCellCoord; AName: string); overload;
    procedure SetBitmap(AColumn, ARow: Integer; ABitmap: TTMSFNCBitmap); overload;
    procedure SetBitmap(AColumn, ARow: Integer; AName: string); overload;
    procedure SetBitmapColumn(AColumn: Integer; ABitmap: TTMSFNCBitmap); overload;
    procedure SetBitmapColumn(AColumn: Integer; AName: string); overload;
    procedure SetButton(ACell: TTMSFNCDataGridCellCoord; AText: string = ''); overload;
    procedure SetButton(AColumn, ARow: Integer; AText: string = ''); overload;
    procedure SetButtonColumn(AColumn: Integer; AText: string = '');
    procedure SetCheckBox(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False); overload;
    procedure SetCheckBox(AColumn, ARow: Integer; AValue: Boolean = False); overload;
    procedure SetCheckBoxColumn(AColumn: Integer; AValue: Boolean = False);
    procedure SetDataCheckBox(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure SetDataCheckBox(AColumn, ARow: Integer); overload;
    procedure SetDataCheckBoxColumn(AColumn: Integer);
    procedure SetDataProgressBar(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure SetDataProgressBar(AColumn, ARow: Integer); overload;
    procedure SetDataProgressBarColumn(AColumn: Integer);
    procedure SetDataRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure SetDataRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure SetDataRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
    procedure SetNodeRow(ARow: Integer; AChildCount: Integer; ALevel: Integer = 0; AMerge: Boolean = True);
    procedure SetProgressBar(ACell: TTMSFNCDataGridCellCoord; AValue: Integer = 0); overload;
    procedure SetProgressBar(AColumn, ARow: Integer; AValue: Integer = 0); overload;
    procedure SetProgressBarColumn(AColumn: Integer; AValue: Integer = 0);
    procedure SetRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure SetRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure SetRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
    procedure ShowFilterDialog;
    procedure ShowColumnsEditor;
    procedure ShowFindDialog;
    procedure ShowReplaceDialog;
    procedure Sort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection); reintroduce; overload;
    procedure Sort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection; AFromRow, AToRow: Integer); reintroduce; overload;
    procedure Sort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); reintroduce; overload;
    procedure Sort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; AFromRow, AToRow: Integer; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); reintroduce; overload;
    procedure SortIndexed;
    procedure SplitCell(ACell: TTMSFNCDataGridCellCoord);
    procedure StretchColumns(AMode: TTMSFNCDataGridStretchingMode = gstmAll; ARange: TTMSFNCDataGridStretchingRange = gstrNormal; AIndex: Integer = -1; ANewWidth: Double = -1);
    procedure StretchRows(AMode: TTMSFNCDataGridStretchingMode = gstmAll; ARange: TTMSFNCDataGridStretchingRange = gstrNormal; AIndex: Integer = -1; ANewHeight: Double = -1);
    procedure SwapCells(AFromCell, AToCell: TTMSFNCDataGridCellCoord);
    procedure SwapColumns(AFromColumn, AToColumn: Integer);
    procedure SwapRows(AFromRow, AToRow: Integer);
    procedure ToggleNode(ARow: Integer);
    procedure Ungroup;
    procedure UnhideAll;
    procedure UnhideAllColumns;
    procedure UnhideAllRows;
    procedure UnhideColumn(AColumn: Integer);
    procedure UnhideRow(ARow: Integer);
    procedure UnhilightCells(AOptions: TTMSFNCDataGridHilightOptions);
    procedure UnmarkCells(AOptions: TTMSFNCDataGridMarkOptions);
    procedure UpdateCalculations;
    procedure UpdateColumnCalculation(AColumn: Integer; AName: string);
    procedure UpdateColumnCalculations;
    procedure UpdateRowCalculation(ARow: Integer; AName: string);
    procedure UpdateRowCalculations;
  end;

  [ComponentPlatformsAttribute(TMSPlatformsWeb)]
  TTMSFNCDataGrid = class(TTMSFNCCustomDataGrid)
  protected
    procedure RegisterRuntimeClasses; override;
  public
    property Booleans;
    property BottomRow;
    property CellData;
    property Cells;
    property Colors;
    property ColumnCalculations;
    property ColumnsEditor;
    property ColumnOrders;
    property ColumnPositions;
    property ColumnReverseOrders;
    property ColumnWidths;
    property Comments;
    property ControlAligns;
    property ControlHeights;
    property ControlPositions;
    property Controls;
    property ControlTexts;
    property ControlWidths;
    property CustomHorizontalScrollBar;
    property CustomScrollBars;
    property CustomVerticalScrollBar;
    property DragCell;
    property EditingCell;
    property FilterBuilder;
    property Filter;
    property FilterEditors;
    property FilteredRowCount;
    property Floats;
    property FocusedCell;
    property FontColors;
    property FontNames;
    property FontSizes;
    property FontStyles;
    property Grids;
    property GroupInfo;
    property HasFilter;
    property HasFilterEditor;
    property HasLayout;
    property HasRowOrder;
    property HeaderRect;
    property HeaderVisualGroupingRect;
    property HeaderBarRect;
    property HiddenColumnCount;
    property HiddenColumns;
    property HiddenColumnWidths;
    property HiddenRowCount;
    property HiddenRowHeights;
    property HiddenRows;
    property Hints;
    property HeaderGroups;
    property HorizontalScrollBar;
    property Ints;
    property Layouts;
    property LeftColumn;
    property ManagedObjects;
    property MergedColumns;
    property MergedRows;
    property Objects;
    property ReadOnlys;
    property RightColumn;
    property RowCalculations;
    property RowHeights;
    property RowNodeChildCount;
    property RowNodeLevel;
    property RowNodeState;
    property RowOrders;
    property RowPositions;
    property RowReverseOrders;
    property RowText;
    property RowTypes;
    property SelectedCellCount;
    property SelectedCells;
    property SelectedColumnCount;
    property SelectedColumns;
    property SelectedRowCount;
    property SelectedRows;
    property Selection;
    property SortIndexList;
    property StartColumn;
    property StartRow;
    property States;
    property StopColumn;
    property StopRow;
    property StoredCells;
    property Strings;
    property StrippedCells;
    property TextAligns;
    property TextAngles;
    property TopRow;
    property TotalColumnWidth;
    property TotalRowHeight;
    property VerticalScrollBar;
    property VerticalTextAligns;
    property VisibleColumnCount;
    property VisibleRowCount;
    property WordWrapping;
  published
    //property Footer;
    property Header;
    property AcceptDropFiles;
    property Adapter;
    property BitmapContainer;
    property CellAppearance;
    property CellPopupMenu;
    property ColumnCount;
    property Columns;
    property ColumnViewMode;
    property DefaultColumnWidth;
    property DefaultRowHeight;
    property Designer;
    property DragAppearance;
    property Fill;
    property FilterActions;
    property FilterAppearance;
    property FixedBottomRowCount;
    property FixedColumnCount;
    property FixedRightColumnCount;
    property FixedRowCount;
    property FreezeColumnCount;
    property FreezeRowCount;
    property GlobalFont;
    property HorizontalScrollBarVisible;
    property Icons;
    property OnBeforeDrawHeader;
    property OnAfterDrawHeader;
//    property OnBeforeDrawFooter;
//    property OnAfterDrawFooter;
    property OnBeforeDrawHeaderGroupLevel;
    property OnAfterDrawHeaderGroupLevel;
    property OnBeforeDrawHeaderGroup;
    property OnAfterDrawHeaderGroup;
    property OnBeforeDropHeaderGroup;
    property OnCanDragHeaderGroup;
    property OnAfterDropHeaderGroup;
    property OnBeforeSortHeaderGroup;
    property OnAfterSortHeaderGroup;
    property OnBeforeCloseHeaderGroup;
    property OnAfterCloseHeaderGroup;
    property OnHeaderGroupClick;
    property OnAfterApplyFilter;
    property OnAfterCloseInplaceEditor;
    property OnAfterCopyToClipboard;
    property OnAfterCreateFilterEditor;
    property OnAfterCutToClipboard;
    property OnAfterDrawCell;
    property OnAfterDrawEmptySpace;
    property OnAfterDrawGrid;
    property OnAfterLookup;
    property OnAfterOpenInplaceEditor;
    property OnAfterPasteFromClipboard;
    property OnAfterSetColumnCalculation;
    property OnAfterSetRowCalculation;
    property OnAfterShowFilter;
    property OnAfterShowFilterDialog;
    property OnAfterShowFindDialog;
    property OnAfterShowReplaceDialog;
    property OnAfterSortColumn;
    property OnAppendColumn;
    property OnAppendRow;
    property OnBeforeApplyFilter;
    property OnBeforeCloseInplaceEditor;
    property OnBeforeCopyToClipboard;
    property OnBeforeCreateFilterEditor;
    property OnBeforeCutToClipboard;
    property OnBeforeDrawCell;
    property OnBeforeDrawEmptySpace;
    property OnBeforeLookup;
    property OnBeforeOpenInplaceEditor;
    property OnBeforePasteFromClipboard;
    property OnBeforeSetColumnCalculation;
    property OnBeforeSetRowCalculation;
    property OnBeforeShowFilter;
    property OnBeforeShowFilterDialog;
    property OnBeforeShowFindDialog;
    property OnBeforeShowReplaceDialog;
    property OnBeforeSortColumn;
    property OnCanAppendColumn;
    property OnCanAppendRow;
    property OnCanDeleteColumn;
    property OnCanDeleteRow;
    property OnCanDragColumn;
    property OnCanDragRow;
    property OnCanDropColumn;
    property OnCanDropRow;
    property OnCanEditCell;
    property OnCanInsertColumn;
    property OnCanInsertRow;
    property OnCanSizeColumn;
    property OnCanSizeRow;
    property OnCellAfterNodeCollapse;
    property OnCellAfterNodeExpand;
    property OnCellAnchorClick;
    property OnCellBeforeNodeCollapse;
    property OnCellBeforeNodeExpand;
    property OnCellBeforeShowPopupMenu;
    property OnCellButtonClick;
    property OnCellCheckBoxChange;
    property OnCellClick;
    property OnCellDblClick;
    property OnCellEditGetData;
    property OnCellEditSetData;
    property OnCellEditValidateData;
    property OnCellRadioButtonChange;
    property OnCellRightClick;
    property OnColumnDropped;
    property OnColumnSized;
    property OnCreateCustomFilterEditor;
    property OnCustomColumnCalculation;
    property OnCustomCompare;
    property OnCustomizeHeaderGroup;
    property OnCustomizeFilterPopupEditor;
    property OnCustomRowCalculation;
    property OnDeleteColumn;
    property OnDeleteRow;
    property OnDropFiles;
    property OnGetCellClass;
    property OnGetCellData;
    property OnGetCellDataProperties;
    property OnGetCellDisplayValue;
    property OnGetCellFormatting;
    property OnGetCellHint;
    property OnGetCellLayout;
    property OnGetCellLayoutHasPriority;
    property OnGetCellProperties;
    property OnGetColumnCalculationOptions;
    property OnGetCustomGroup;
    property OnGetGroupOptions;
    property OnGetInplaceEditorClass;
    property OnGetInplaceEditorProperties;
    property OnGetInplaceEditorRect;
    property OnGetInplaceEditorTarget;
    property OnGetInplaceEditorType;
    property OnGetRowCalculationOptions;
    property OnGetSortOptions;
    property OnGroup;
    property OnHScroll;
    property OnInsertColumn;
    property OnInsertRow;
    property OnLoadCellData;
    property OnLogMessage;
    property OnNeedFilterDropDownData;
    property OnRemoveFilter;
    property OnRowCollapse;
    property OnRowDropped;
    property OnRowExpand;
    property OnRowSized;
    property OnSaveCellData;
    property OnSelectCell;
    property OnSelectingCell;
    property OnSizeColumn;
    property OnSizeRow;
    property OnUngroup;
    property OnVScroll;
    property Options;
    property RootPersistence;
    property RowCount;
    property RowViewMode;
    property ScrollMode;
    property ScrollUpdate;
    property StretchScrollBars;
    property Stroke;
    property Version;
    property VerticalScrollBarVisible;
  end;

implementation

uses
  DateUtils, Variants, Math, WEBLib.Dialogs, WEBLib.TMSFNCImage, WEBLib.TMSFNCUtils, WEBLib.TMSFNCStyles
  {$IFDEF FMXLIB}
  ,FMX.Layouts
  {$ENDIF}
  ;

type
  TTMSFNCDataGridRendererOpen = class(TTMSFNCDataGridRenderer);

  {$IFNDEF WEBLIB}
  TTMSFNCDataGridDesignerOpenDialog = TOpenDialog;
  TTMSFNCDataGridDesignerControlOpen = class(TTMSFNCDataGridCellControl);
  TTMSFNCImageOpen = class(TTMSFNCImage);
  {$ENDIF}

{$IFDEF WEBLIB}
const
  TTMSFNCDATAGRIDCLOSE =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiI+PGRlZnM+PHN0eWxlPi5jbHMtMXtmaWxsOm5vbmU7fS5jbHMtMntmaWxsOiNlY'+
  'TNmNDE7fS5jbHMtM3tjbGlwLXBhdGg6dXJsKCNjbGlwLXBhdGgpO30uY2xzLTR7ZmlsbDojZmFmYWZhO30uY2xzLTV7Y2xpcC1wYXRoOnVybCgjY2xpcC1wYXRoLTIpO30uY2xzLTZ7ZmlsbDp1cmwoI05ld19QYXR0ZXJuX1N3YXRjaF8zKTt9PC9zdHlsZT48Y2xpcFBhdGggaWQ9ImNsaXAtcGF0aCI+PHJlY3QgY2xhc3M9ImNscy0xIiB4'+
  'PSItNDUuNSIgeT0iLTQuNSIgd2lkdGg9IjM0IiBoZWlnaHQ9IjM0Ii8+PC9jbGlwUGF0aD48Y2xpcFBhdGggaWQ9ImNsaXAtcGF0aC0yIj48cmVjdCBjbGFzcz0iY2xzLTEiIHg9IjIuNSIgeT0iMi41IiB3aWR0aD0iMjciIGhlaWdodD0iMjciLz48L2NsaXBQYXRoPjxwYXR0ZXJuIGlkPSJOZXdfUGF0dGVybl9Td2F0Y2hfMyIgZGF0YS1'+
  'uYW1lPSJOZXcgUGF0dGVybiBTd2F0Y2ggMyIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4IiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIiB2aWV3Qm94PSIwIDAgNjggNjgiPjxyZWN0IGNsYXNzPSJjbHMtMSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PHJlY3QgY2xhc3M9ImNscy0yIiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48L3'+
  'BhdHRlcm4+PC9kZWZzPjx0aXRsZT5XaW5kb3ctQ2xvc2U8L3RpdGxlPjxnIGlkPSJJY29uIj48ZyBjbGFzcz0iY2xzLTMiPjxyZWN0IGNsYXNzPSJjbHMtMSIgeD0iLTU0IiB5PSItNDkiIHdpZHRoPSI2OCIgaGVpZ2h0PSI2OCIvPjxyZWN0IGNsYXNzPSJjbHMtNCIgeD0iLTU0IiB5PSItNDkiIHdpZHRoPSI2OCIgaGVpZ2h0PSI2OCIvP'+
  'jxyZWN0IGNsYXNzPSJjbHMtMSIgeD0iLTU0IiB5PSIxOSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PHJlY3QgY2xhc3M9ImNscy00IiB4PSItNTQiIHk9IjE5IiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48L2c+PGcgY2xhc3M9ImNscy01Ij48cmVjdCBjbGFzcz0iY2xzLTEiIHg9IjE0IiB5PSItNDkiIHdpZHRoPSI2OCIgaGVpZ2h0'+
  'PSI2OCIvPjxyZWN0IGNsYXNzPSJjbHMtNCIgeD0iMTQiIHk9Ii00OSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PHJlY3QgY2xhc3M9ImNscy0xIiB4PSIxNCIgeT0iMTkiIHdpZHRoPSI2OCIgaGVpZ2h0PSI2OCIvPjxyZWN0IGNsYXNzPSJjbHMtNCIgeD0iMTQiIHk9IjE5IiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48cmVjdCBjbGF'+
  'zcz0iY2xzLTEiIHg9Ii01NCIgeT0iLTQ5IiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48cmVjdCBjbGFzcz0iY2xzLTQiIHg9Ii01NCIgeT0iLTQ5IiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48cmVjdCBjbGFzcz0iY2xzLTEiIHg9Ii01NCIgeT0iMTkiIHdpZHRoPSI2OCIgaGVpZ2h0PSI2OCIvPjxyZWN0IGNsYXNzPSJjbHMtNCIgeD'+
  '0iLTU0IiB5PSIxOSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PC9nPjxwYXRoIGNsYXNzPSJjbHMtNiIgZD0iTTMwLDMwSDJWMkgzMFpNMywyOUgyOVYzSDNaIi8+PHBvbHlnb24gY2xhc3M9ImNscy02IiBwb2ludHM9IjIyLjY1IDIzLjM1IDguNjUgOS4zNSA5LjM1IDguNjUgMjMuMzUgMjIuNjUgMjIuNjUgMjMuMzUiLz48cG9seWdvb'+
  'iBjbGFzcz0iY2xzLTYiIHBvaW50cz0iOS4zNSAyMy4zNSA4LjY1IDIyLjY1IDIyLjY1IDguNjUgMjMuMzUgOS4zNSA5LjM1IDIzLjM1Ii8+PC9nPjwvc3ZnPg';
{$ENDIF}

{ TTMSFNCCustomDataGrid }

procedure TTMSFNCCustomDataGrid.NextPage;
begin
  Root.NextPage;
end;

function TTMSFNCCustomDataGrid.NextSelectableColumn(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.NextSelectableColumn(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.NextSelectableRow(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.NextSelectableRow(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.NextVisibleColumn(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.NextVisibleColumn(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.NextVisibleRow(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.NextVisibleRow(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.Notification(AComponent: TComponent;
  Operation: TOperation);
begin
  inherited;
  if (Operation = opRemove) and not (csDestroying in ComponentState) then
  begin
    if (AComponent = CellPopupMenu) then
      CellPopupMenu := nil;

    if (AComponent = BitmapContainer) then
      BitmapContainer := nil;

    if (AComponent = Adapter) then
      Adapter := nil;
  end;
end;

function TTMSFNCCustomDataGrid.Find(AText: string;
  AParameters: TTMSFNCDataGridDataFindParameters;
  AStartCell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
begin
  Result := Root.Find(AText, AParameters, AStartCell);
end;

function TTMSFNCCustomDataGrid.Find(AText: string;
  AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord;
begin
  Result := Root.Find(AText, AParameters);
end;

function TTMSFNCCustomDataGrid.Find(AText: string): TTMSFNCDataGridCellCoord;
begin
  Result := Root.Find(AText);
end;

function TTMSFNCCustomDataGrid.FindAll(AText: string;
  AParameters: TTMSFNCDataGridDataFindParameters;
  var AResults: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.FindAll(AText, AParameters, AResults);
end;

function TTMSFNCCustomDataGrid.FindFirst(AText: string;
  AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord;
begin
  Result := Root.FindFirst(AText, AParameters);
end;

function TTMSFNCCustomDataGrid.FindNext: TTMSFNCDataGridCellCoord;
begin
  Result := Root.FindNext;
end;

procedure TTMSFNCCustomDataGrid.FirstPage;
begin
  Root.FirstPage;
end;

procedure TTMSFNCCustomDataGrid.PasteFromClipboard;
begin
  Root.PasteFromClipboard;
end;

function TTMSFNCCustomDataGrid.PositionToHeaderGroupIndex(
  AHeaderGroups: TTMSFNCDataGridHeaderGroups;
  ASkipHeaderGroup: TTMSFNCDataGridHeaderGroup;
  ALevel: Integer;
  X: Single): Integer;
var
  I: Integer;
  HG: TTMSFNCDataGridHeaderGroup;
  l: TList<TTMSFNCDataGridHeaderGroup>;
begin
  Result := -1;

  l := TList<TTMSFNCDataGridHeaderGroup>.Create;
  try
    for I := 0 to AHeaderGroups.Count - 1 do
    begin
      HG := AHeaderGroups[I];
      if (HG <> ASkipHeaderGroup) and (HG.Level = ALevel) then
        l.Add(HG);
    end;

    for I := 0 to L.Count - 1 do
    begin
      HG := l[I];
      if (X >= HG.FRect.Left) and (X <= HG.FRect.Right) then
        Result := I
      else if (X < HG.FRect.Left + (HG.FRect.Right - HG.FRect.Left) / 2) and (I = 0) then
        Result := Max(0, AHeaderGroups.IndexOf(HG) - 1)
      else if (X > HG.FRect.Right - (HG.FRect.Right - HG.FRect.Left) / 2) and (I = L.Count - 1) then
        Result := Min(AHeaderGroups.Count - 1, AHeaderGroups.IndexOf(HG) + 1);
    end;

  finally
    l.Free;
  end;
end;

function TTMSFNCCustomDataGrid.PositionToHeaderGroupLevel(AHeaderGroups: TTMSFNCDataGridHeaderGroups; ASkipHeaderGroup: TTMSFNCDataGridHeaderGroup; Y: Single): Integer;
var
  r: TRectF;
  hs, levelHeight, ys: Single;
  L: Integer;
  insertRect: TRectF;
begin
  if Assigned(ASkipHeaderGroup) then
    Result := ASkipHeaderGroup.Level
  else
    Result := 0;

  r := GetHeaderRect;
  hs := GetHeaderSize;

  if Length(FHeaderLevelRects) > 0 then
    levelHeight := (FHeaderLevelRects[0].Bottom - FHeaderLevelRects[0].Top) + 2
  else
    levelHeight := hs / 3;

  ys := r.Top + 3;

  for L := 0 to 2 do
  begin
    if CanAddLevel(AHeaderGroups, ASkipHeaderGroup, L, False) then
    begin
      insertRect := RectF(r.Left, ys, r.Right, Min(r.Bottom, ys + levelHeight));
      if PtInRectEx(insertRect, PointF(0, Y)) then
      begin
        Result := L;
        Exit;
      end;
    end;
    ys := ys + levelHeight;
  end;
end;

procedure TTMSFNCCustomDataGrid.PreviousPage;
begin
  Root.PreviousPage;
end;

function TTMSFNCCustomDataGrid.PreviousSelectableColumn(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.PreviousSelectableColumn(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.PreviousSelectableRow(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.PreviousSelectableRow(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.PreviousVisibleColumn(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.PreviousVisibleColumn(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.PreviousVisibleRow(AColumn,
  ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result := Root.PreviousVisibleRow(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.FooterChangedRebuild(Sender: TObject);
begin
  UpdateRoot;
end;

procedure TTMSFNCCustomDataGrid.HeaderChangedRebuild(Sender: TObject);
begin
  UpdateRoot;
end;

function TTMSFNCCustomDataGrid.HeaderGroupsToGroupInfo(AHeaderGroups: TTMSFNCDataGridHeaderGroups; var AGroupInfo: TTMSFNCDataGridDataGroupInfo): Boolean;

var
  sd: TTMSFNCDataGridSortDirection;
  h: TTMSFNCDataGridHeaderGroup;
  gi: TTMSFNCDataGridDataGroupInfo;
  i, k: Integer;
  l: TObjectList<TTMSFNCDataGridSortIndex>;
  d: TObjectDictionary<Integer, TObjectList<TTMSFNCDataGridSortIndex>>;
  levelCols: array[0..2] of TArray<Integer>;
  levelDirs: array[0..2] of TArray<TTMSFNCDataGridSortDirection>;
begin
  Result := False;
  AGroupInfo := DefaultGroupInfo;

  if AHeaderGroups.Count = 0 then
    Exit;

  d := TObjectDictionary<Integer, TObjectList<TTMSFNCDataGridSortIndex>>.Create([doOwnsValues]);
  try
    for i := 0 to AHeaderGroups.Count - 1 do
    begin
      h := AHeaderGroups[i];

      if not d.TryGetValue(h.Level, l) then
      begin
        l := TObjectList<TTMSFNCDataGridSortIndex>.Create;
        d.Add(h.Level, l);
      end;

      case h.SortIndicator of
        gsiAscending:   sd := gsdAscending;
        gsiDescending:  sd := gsdDescending;
      else
        sd := gsdNone;
      end;

      l.Add(TTMSFNCDataGridSortIndex.Create(h.Column, sd));
    end;

    for i in d.Keys do
    begin
      if (i >= Low(levelCols)) and (i <= High(levelCols)) then
      begin
        l := d[i];
        SetLength(levelCols[i], l.Count);
        SetLength(levelDirs[i], l.Count);

        for k := 0 to l.Count - 1 do
        begin
          levelCols[i][k] := l[k].Column;
          levelDirs[i][k] := l[k].Direction;
        end;
      end;
    end;

    if Length(levelCols[0]) > 0 then
    begin
      gi.Columns := levelCols[0];
//      gi.SortDirections := levelDirs[0];

      if Length(levelCols[1]) > 0 then
      begin
        SetLength(gi.SubColumns, 1);
        gi.SubColumns[0].Columns := levelCols[1];
//        gi.SubColumns[0].SortDirections := levelDirs[1];

        if Length(levelCols[2]) > 0 then
        begin
          SetLength(gi.SubColumns[0].SubColumns, 1);
          gi.SubColumns[0].SubColumns[0].Columns := levelCols[2];
//          gi.SubColumns[0].SubColumns[0].SortDirections := levelDirs[2];
        end;
      end;

      AGroupInfo := gi;
      Result := True;
    end;

  finally
    d.Free;
  end;
end;

procedure TTMSFNCCustomDataGrid.HideColumn(AColumn: Integer);
begin
  Root.HideColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.HideRow(ARow: Integer);
begin
  Root.HideRow(ARow);
end;

procedure TTMSFNCCustomDataGrid.HilightCells(AHilightText: string;
  AOptions: TTMSFNCDataGridHilightOptions);
begin
  Root.HilightCells(AHilightText, AOptions);
end;

{$IFDEF FMXLIB}
procedure TTMSFNCCustomDataGrid.DoAbsoluteChanged;
begin
  inherited;
  if Parent is TScaledLayout then
    UpdateRoot;
end;
{$ENDIF}

procedure TTMSFNCCustomDataGrid.DoAfterCloseHeaderGroup(AColumn: Integer);
begin
  if Assigned(OnAfterCloseHeaderGroup) then
    OnAfterCloseHeaderGroup(Self, AColumn);
end;

procedure TTMSFNCCustomDataGrid.DoAfterDrawFooter(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
begin
  if Assigned(OnAfterDrawFooter) then
    OnAfterDrawFooter(Self, AGraphics, ARect);
end;

procedure TTMSFNCCustomDataGrid.DoAfterDrawHeader(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
begin
  if Assigned(OnAfterDrawHeader) then
    OnAfterDrawHeader(Self, AGraphics, ARect);
end;

procedure TTMSFNCCustomDataGrid.DoAfterDrawHeaderGroup(Sender: TObject;
  AGraphics: TTMSFNCGraphics);
begin
  if Assigned(OnAfterDrawHeaderGroup) and (Sender is TTMSFNCDataGridHeaderGroup) then
    OnAfterDrawHeaderGroup(Self, AGraphics, Sender as TTMSFNCDataGridHeaderGroup);
end;

procedure TTMSFNCCustomDataGrid.DoAfterDrawHeaderGroupLevel(AGraphics: TTMSFNCGraphics; ARect: TRectF; ALevel: Integer; AActive: Boolean);
begin
  if Assigned(OnAfterDrawHeaderGroupLevel) then
    OnAfterDrawHeaderGroupLevel(Self, AGraphics, ARect, ALevel, AActive);
end;

procedure TTMSFNCCustomDataGrid.DoAfterDropHeaderGroup(AColumn: Integer);
begin
  if Assigned(OnAfterDropHeaderGroup) then
    OnAfterDropHeaderGroup(Self, AColumn);
end;

procedure TTMSFNCCustomDataGrid.DoAfterSortHeaderGroup(AColumn: Integer);
begin
  if Assigned(OnAfterSortHeaderGroup) then
    OnAfterSortHeaderGroup(Self, AColumn);
end;

procedure TTMSFNCCustomDataGrid.DoBeforeCloseHeaderGroup(AColumn: Integer;
  var ACanClose: Boolean);
begin
  if Assigned(OnBeforeCloseHeaderGroup) then
    OnBeforeCloseHeaderGroup(Self, AColumn, ACanClose);
end;

procedure TTMSFNCCustomDataGrid.DoBeforeDrawFooter(AGraphics: TTMSFNCGraphics;
  ARect: TRectF; var ACanDraw: Boolean);
begin
  if Assigned(OnBeforeDrawFooter) then
    OnBeforeDrawFooter(Self, AGraphics, ARect, ACanDraw);
end;

procedure TTMSFNCCustomDataGrid.DoBeforeDrawHeader(AGraphics: TTMSFNCGraphics;
  ARect: TRectF; var ACanDraw: Boolean);
begin
  if Assigned(OnBeforeDrawHeader) then
    OnBeforeDrawHeader(Self, AGraphics, ARect, ACanDraw);
end;

procedure TTMSFNCCustomDataGrid.DoBeforeDrawHeaderGroup(Sender: TObject;
  AGraphics: TTMSFNCGraphics; var ACanDraw: Boolean);
begin
  if Assigned(OnBeforeDrawHeaderGroup) and (Sender is TTMSFNCDataGridHeaderGroup) then
    OnBeforeDrawHeaderGroup(Self, AGraphics, Sender as TTMSFNCDataGridHeaderGroup, ACanDraw);
end;

procedure TTMSFNCCustomDataGrid.DoBeforeDrawHeaderGroupLevel(AGraphics: TTMSFNCGraphics; ARect: TRectF; ALevel: Integer; AActive: Boolean;
  var ACanDraw: Boolean);
begin
  if Assigned(OnBeforeDrawHeaderGroupLevel) then
    OnBeforeDrawHeaderGroupLevel(Self, AGraphics, ARect, ALevel, AActive, ACanDraw);
end;

procedure TTMSFNCCustomDataGrid.DoBeforeDropHeaderGroup(AColumn: Integer;
  var ACanDrop: Boolean);
begin
  if Assigned(OnBeforeDropHeaderGroup) then
    OnBeforeDropHeaderGroup(Self, AColumn, ACanDrop);
end;

procedure TTMSFNCCustomDataGrid.DoBeforeSortHeaderGroup(AColumn: Integer;
  var ACanSort: Boolean);
begin
  if Assigned(OnBeforeSortHeaderGroup) then
    OnBeforeSortHeaderGroup(Self, AColumn, ACanSort);
end;

procedure TTMSFNCCustomDataGrid.DoCanDragHeaderGroup(AColumn: Integer;
  var ACanDrag: Boolean);
begin
  if Assigned(OnCanDragHeaderGroup) then
    OnCanDragHeaderGroup(Self, AColumn, ACanDrag);
end;

procedure TTMSFNCCustomDataGrid.DoCustomizeHeaderGroup(
  AHeaderGroup: TTMSFNCDataGridHeaderGroup);
begin
  if Assigned(OnCustomizeHeaderGroup) then
    OnCustomizeHeaderGroup(Self, AHeaderGroup);
end;

procedure TTMSFNCCustomDataGrid.DoEnter;
begin
  inherited;
  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.HandleEnter
  else
    Root.HandleEnter;
end;

procedure TTMSFNCCustomDataGrid.DoExit;
begin
  inherited;
  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.HandleExit
  else
    Root.HandleExit;
end;

procedure TTMSFNCCustomDataGrid.DoGroup;
begin
  if Assigned(OnGroup) then
    OnGroup(Self);
end;

procedure TTMSFNCCustomDataGrid.DoHeaderButtonClick(Sender: TObject);
var
  btn: TTMSFNCToolBarButton;
  b: TTMSFNCDataGridButton;
begin
  if Assigned(Sender) and (Sender is TTMSFNCToolBarButton) then
  begin
    btn := (Sender as TTMSFNCToolBarButton);
    if Assigned(btn.DataObject) and (btn.DataObject is TTMSFNCDataGridButton) then
    begin
      b := (btn.DataObject as TTMSFNCDataGridButton);
      if Assigned(b.Callback) then
        b.Callback();

      if Assigned(b.OnClick) then
        b.OnClick(b);
    end;
  end;
end;

procedure TTMSFNCCustomDataGrid.DoHeaderGroupClick(AColumn: Integer);
begin
  if Assigned(OnHeaderGroupClick) then
    OnHeaderGroupClick(Self, AColumn);
end;

procedure TTMSFNCCustomDataGrid.DoRootBeforeCalculate(Sender: TObject);
begin
  inherited;
  CalculateHeader;
end;

procedure TTMSFNCCustomDataGrid.DoRootFocus(Sender: TObject);
begin
  if CanFocus then
    SetFocus;

  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.ClearFocus;

  if Assigned(FFocusedRenderer) and (FFocusedRenderer <> Sender) then
  begin
    FFocusedRenderer.ClearSelection;
    FFocusedRenderer.StopEditing;
  end;

  if Sender is TTMSFNCDataGridRenderer then
    FFocusedRenderer := (Sender as TTMSFNCDataGridRenderer);

  Root.Display;
end;

{$IFDEF SUPPORTABSTRACT}

function TTMSFNCCustomDataGrid.GetRecord(AIndex: Integer): TValue;
var
  i: ITMSFNCDataObject;
begin
  Result := TValue.Empty;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.GetRecord(AIndex);
end;

function TTMSFNCCustomDataGrid.GetRecordCount: Integer;
var
  i: ITMSFNCDataObject;
begin
  Result := 0;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.GetRecordCount;
end;

function TTMSFNCCustomDataGrid.GetRecordIndex(ARecord: TValue): Integer;
var
  i: ITMSFNCDataObject;
begin
  Result := -1;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.GetRecordIndex(ARecord);
end;

function TTMSFNCCustomDataGrid.GetRecordValue(ARecord: TValue;
  AField: string; AFieldType: TFieldType): ValueType;
var
  i: ITMSFNCDataObject;
begin
  Result := Null;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.GetRecordValue(ARecord, AField, AFieldType);
end;

function TTMSFNCCustomDataGrid.Clone: TObject;
var
  i: ITMSFNCDataObject;
begin
  Result := nil;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.Clone;
end;

procedure TTMSFNCCustomDataGrid.SetRecordValue(ARecordIndex: Integer;
  AField: string; AValue: ValueType);
var
  i: ITMSFNCDataObject;
begin
  if Supports(Root, ITMSFNCDataObject, i) then
    i.SetRecordValue(ARecordIndex, AField, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetNotification(
  ANotifyObject: ITMSFNCDataObjectNotification);
var
  i: ITMSFNCDataObject;
begin
  if Supports(Root, ITMSFNCDataObject, i) then
    i.SetNotification(ANotifyObject);
end;

procedure TTMSFNCCustomDataGrid.InitializeFieldDefs(AFieldDefs: TFieldDefs);
var
  i: ITMSFNCDataObject;
begin
  if Supports(Root, ITMSFNCDataObject, i) then
    i.InitializeFieldDefs(AFieldDefs);
end;

procedure TTMSFNCCustomDataGrid.Activate(AValue: Boolean);
var
  i: ITMSFNCDataObject;
begin
  if not (csDestroying in ComponentState) and Supports(Root, ITMSFNCDataObject, i) then
    i.Activate(AValue);
end;

procedure TTMSFNCCustomDataGrid.InsertRecord(AIndex: Integer; ARecord: TValue);
var
  i: ITMSFNCDataObject;
begin
  if Supports(Root, ITMSFNCDataObject, i) then
    i.InsertRecord(AIndex, ARecord);
end;

procedure TTMSFNCCustomDataGrid.AddRecord(ARecord: TValue);
var
  i: ITMSFNCDataObject;
begin
  if Supports(Root, ITMSFNCDataObject, i) then
    i.AddRecord(ARecord);
end;

procedure TTMSFNCCustomDataGrid.DeleteRecord(AIndex: Integer);
var
  i: ITMSFNCDataObject;
begin
  if Supports(Root, ITMSFNCDataObject, i) then
    i.DeleteRecord(AIndex);
end;

function TTMSFNCCustomDataGrid.CompareRecords(AField: string; ARecord1,
  ARecord2: TValue): Integer;
var
  i: ITMSFNCDataObject;
begin
  Result := 0;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.CompareRecords(AField, ARecord1, ARecord2);
end;

function TTMSFNCCustomDataGrid.CreateDefaultRecord: TValue;
var
  i: ITMSFNCDataObject;
begin
  Result := TValue.Empty;
  if Supports(Root, ITMSFNCDataObject, i) then
    Result := i.CreateDefaultRecord;
end;

{$ENDIF}

function TTMSFNCCustomDataGrid.DoRootIsFocused(Sender: TObject): Boolean;
begin
  Result := inherited;
  Result := Result and ((Assigned(FFocusedRenderer) and (FFocusedRenderer = Sender)) or (not Assigned(FFocusedRenderer) and (Root = Sender)));
end;

procedure TTMSFNCCustomDataGrid.DoRootUpdateCursor(Sender: TObject;
  ACursor: TCursor);
begin
  inherited;
  Cursor := ACursor;
end;

procedure TTMSFNCCustomDataGrid.DoUngroup;
begin
  if Assigned(OnUngroup) then
    OnUngroup(Self);
end;

procedure TTMSFNCCustomDataGrid.ClearCells(
  ACellRange: TTMSFNCDataGridCellCoordRange);
begin
  Root.ClearCells(ACellRange);
end;

procedure TTMSFNCCustomDataGrid.ClearColumns;
begin
  Root.ClearColumns;
end;

procedure TTMSFNCCustomDataGrid.ClearControlCache;
begin
  Root.ClearControlCache;
end;

procedure TTMSFNCCustomDataGrid.ClearFilter;
begin
  Root.ClearFilter;
end;

procedure TTMSFNCCustomDataGrid.ClearNodeRow(ARow: Integer);
begin
  Root.ClearNodeRow(ARow);
end;

procedure TTMSFNCCustomDataGrid.ClearSelection;
begin
  Root.ClearSelection;
end;

procedure TTMSFNCCustomDataGrid.CloseFilterPopup;
begin
  Root.CloseFilterPopup;
end;

procedure TTMSFNCCustomDataGrid.CollapseAllNodes;
begin
  Root.CollapseAllNodes;
end;

procedure TTMSFNCCustomDataGrid.CollapseNode(ARow: Integer);
begin
  Root.CollapseNode(ARow);
end;

function TTMSFNCCustomDataGrid.ColumnAverage(AColumn, AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnAverage(AColumn, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.ColumnByHeader(
  AHeader: string): TTMSFNCDataGridColumn;
begin
  Result := Root.ColumnByHeader(AHeader);
end;

function TTMSFNCCustomDataGrid.ColumnByName(
  AName: string): TTMSFNCDataGridColumn;
begin
  Result := Root.ColumnByName(AName);
end;

procedure TTMSFNCCustomDataGrid.ColumnCalculation(AColumns: TArray<Integer>;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  Root.ColumnCalculation(AColumns, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCCustomDataGrid.ColumnCalculation(AColumn: Integer;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  Root.ColumnCalculation(AColumn, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

function TTMSFNCCustomDataGrid.RowAverage(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowAverage(ARow, AFromColumn, AToColumn);
end;

procedure TTMSFNCCustomDataGrid.RowCalculation(ARows: TArray<Integer>;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback);
begin
  Root.RowCalculation(ARows, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCCustomDataGrid.RowCalculation(ARow: Integer;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback);
begin
  Root.RowCalculation(ARow, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

function TTMSFNCCustomDataGrid.ColumnCustomCalculation(AColumn: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback; AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnCustomCalculation(AColumn, ACustomCalculationCallback, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.ColumnDistinct(AColumn, AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnDistinct(AColumn, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.ColumnIndexByHeader(AHeader: string): Integer;
begin
  Result := Root.ColumnIndexByHeader(AHeader);
end;

function TTMSFNCCustomDataGrid.ColumnIndexByName(AName: string): Integer;
begin
  Result := Root.ColumnIndexByName(AName);
end;

function TTMSFNCCustomDataGrid.ColumnMax(AColumn, AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnMax(AColumn, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.ColumnMin(AColumn, AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnMin(AColumn, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.ColumnSpan(
  ACell: TTMSFNCDataGridCellCoord): Integer;
begin
  Result := Root.ColumnSpan(ACell);
end;

function TTMSFNCCustomDataGrid.ColumnStandardDeviation(AColumn, AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnStandardDeviation(AColumn, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.ColumnSum(AColumn, AFromRow,
  AToRow: Integer): Double;
begin
  Result := Root.ColumnSum(AColumn, AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.RowCustomCalculation(ARow: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback; AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowCustomCalculation(ARow, ACustomCalculationCallback, AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.RowDistinct(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowDistinct(ARow, AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.RowMax(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowMax(ARow, AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.RowMin(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowMin(ARow, AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.RowSpan(
  ACell: TTMSFNCDataGridCellCoord): Integer;
begin
  Result := Root.RowSpan(ACell);
end;

function TTMSFNCCustomDataGrid.RowStandardDeviation(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowStandardDeviation(ARow, AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.RowSum(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := Root.RowSum(ARow, AFromColumn, AToColumn);
end;

procedure TTMSFNCCustomDataGrid.CopyToClipboard(ASelectedCells: Boolean);
begin
  Root.CopyToClipboard(ASelectedCells);
end;

procedure TTMSFNCCustomDataGrid.Assign(Source: TPersistent);
begin
  inherited;
  if Source is TTMSFNCCustomDataGrid then
  begin
    FFooter.Assign((Source as TTMSFNCCustomDataGrid).Footer);
    FHeader.Assign((Source as TTMSFNCCustomDataGrid).Header);
    Root.Assign((Source as TTMSFNCCustomDataGrid).Root);
  end;
end;

procedure TTMSFNCCustomDataGrid.AutoNumberColumn(AColumn, AStartRow,
  AStartValue: Integer);
begin
  Root.AutoNumberColumn(AColumn, AStartRow, AStartValue);
end;

procedure TTMSFNCCustomDataGrid.AutoNumberRow(ARow, AStartColumn,
  AStartValue: Integer);
begin
  Root.AutoNumberRow(ARow, AStartColumn, AStartValue);
end;

procedure TTMSFNCCustomDataGrid.AutoSizeColumn(AColumn: Integer;
  const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
begin
  Root.AutoSizeColumn(AColumn, AMode, APadding);
end;

procedure TTMSFNCCustomDataGrid.AutoSizeColumns(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
begin
  Root.AutoSizeColumns(AMode, APadding);
end;

procedure TTMSFNCCustomDataGrid.AutoSizeGrid(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APaddingColumn: Integer = 5; const APaddingRow: Integer = 5);
begin
  Root.AutoSizeGrid(AMode, APaddingColumn, APaddingRow);
end;

procedure TTMSFNCCustomDataGrid.AutoSizeRow(ARow: Integer;
  const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
begin
  Root.AutoSizeRow(ARow, AMode, APadding);
end;

procedure TTMSFNCCustomDataGrid.AutoSizeRows(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
begin
  Root.AutoSizeRows(AMode, APadding);
end;

function TTMSFNCCustomDataGrid.BaseCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
begin
  Result := Root.BaseCell(ACell);
end;

procedure TTMSFNCCustomDataGrid.HandleMouseDown(Button: TTMSFNCMouseButton; Shift: TShiftState;
  X, Y: Single);
var
  m: TTMSFNCDataGridMouseInfo;
  r: TTMSFNCDataGridRenderer;
begin
  inherited;
  if not FDoubleClicked then
    CaptureEx;

  FDoubleClicked := False;

  if (TMouseButton.mbLeft = Button) and StartDragOnHeader(X, Y) then
    Exit;

  m := DefaultMouseInfo;
  m.Shift := Shift;
  m.X := X;
  m.Y := Y;
  m.Button := Button;
  m.Kind := gmkDown;

  r := Root.XYToRenderer(X, Y);
  if not Assigned(r) then
    r := Root;

  FDownRenderer := r;

  if Assigned(r) then
    r.HandleMouse(m);
end;

procedure TTMSFNCCustomDataGrid.HandleMouseMove(Shift: TShiftState; X, Y: Single);
var
  m: TTMSFNCDataGridMouseInfo;
  r: TTMSFNCDataGridRenderer;
begin
  inherited;

  if HandleDragOnHeader(X, Y) then
    Exit;

  m := DefaultMouseInfo;
  m.Shift := Shift;
  m.X := X;
  m.Y := Y;
  m.Kind := gmkMove;

  r := Root.XYToRenderer(X, Y);
  if not Assigned(r) then
    r := Root;

  if (FDownRenderer <> r) and Assigned(FDownRenderer) then
    TTMSFNCDataGridRendererOpen(FDownRenderer).ResetScrollBarCapture;

  if Assigned(r) then
    r.HandleMouse(m);

  HandleEndDragOnHeader(X, Y);
end;

procedure TTMSFNCCustomDataGrid.GroupActiveHeaders;
var
  gi: TTMSFNCDataGridDataGroupInfo;
begin
  BeginUpdate;
  try
    if IsGrouped then
    begin
      DoUngroup;
      Ungroup;
    end;

    gi := DefaultGroupInfo;
    if HeaderGroupsToGroupInfo(HeaderGroups, gi) then
    begin
      Group(gi);
      DoGroup;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCCustomDataGrid.HandleMouseUp(Button: TTMSFNCMouseButton; Shift: TShiftState;
  X, Y: Single);
var
  m: TTMSFNCDataGridMouseInfo;
  r: TTMSFNCDataGridRenderer;
begin
  inherited;
  ReleaseCaptureEx;

  if (TMouseButton.mbLeft = Button) and HandleDropOnHeader(X, Y, Shift) then
    Exit;

  m := DefaultMouseInfo;
  m.Shift := Shift;
  m.X := X;
  m.Y := Y;
  m.Button := Button;
  m.Kind := gmkUp;

  r := Root.XYToRenderer(X, Y);
  if not Assigned(r) then
    r := Root;

  if Assigned(FDownRenderer) and (FDownRenderer <> r) then
    FDownRenderer.HandleExit;

  FDownRenderer := nil;

  HandleEndDropOnHeader;

  if Assigned(r) then
    r.HandleMouse(m);
end;

procedure TTMSFNCCustomDataGrid.HandleMouseWheel(Shift: TShiftState; WheelDelta: Integer;
  var Handled: Boolean);
var
  m: TTMSFNCDataGridMouseInfo;
  r: TTMSFNCDataGridRenderer;
  c: TPointF;
begin
  inherited;
  if Handled or not Visible or not Assigned(Parent) then
    Exit;

  c := ScreenToLocalEx(TTMSFNCUtils.GetMousePos);
  m := DefaultMouseInfo;
  m.Kind := gmkWheel;
  m.Handled := Handled;
  m.WheelDelta := WheelDelta;
  m.Shift := Shift;
  m.X := c.X;
  m.Y := c.Y;

  r := Root.XYToRenderer(c.X, c.Y);
  if not Assigned(r) then
    r := Root;

  if Assigned(r) then
  begin
    if r.IsEditing then
      r.StopEditing;

    if not r.IsCellValid(r.FocusedCell) and not r.Options.Mouse.WheelScrollKeepSelection then
      r.FocusedCell := r.XYToCell(c.X, c.Y);

    r.Focus;
    r.HandleMouse(m);
    Handled := m.Handled;
  end;
end;

function TTMSFNCCustomDataGrid.ValueIsEmpty(
  AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := Root.ValueIsEmpty(AValue);
end;

function TTMSFNCCustomDataGrid.ValueIsFloat(
  AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := Root.ValueIsFloat(AValue);
end;

function TTMSFNCCustomDataGrid.ValueIsInteger(
  AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := Root.ValueIsInteger(AValue);
end;

function TTMSFNCCustomDataGrid.ValueIsString(
  AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := Root.ValueIsString(AValue);
end;

function TTMSFNCCustomDataGrid.ValueToBoolean(
  AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := Root.ValueToBoolean(AValue);
end;

function TTMSFNCCustomDataGrid.ValueToFloat(
  AValue: TTMSFNCDataGridCellValue): Double;
begin
  Result := Root.ValueToFloat(AValue);
end;

function TTMSFNCCustomDataGrid.ValueToInt64(
  AValue: TTMSFNCDataGridCellValue): Int64;
begin
  Result := Root.ValueToInt64(AValue);
end;

function TTMSFNCCustomDataGrid.ValueToInteger(
  AValue: TTMSFNCDataGridCellValue): Integer;
begin
  Result := Root.ValueToInt64(AValue);
end;

function TTMSFNCCustomDataGrid.ValueToString(
  AValue: TTMSFNCDataGridCellValue): string;
begin
  Result := Root.ValueToString(AValue);
end;

function TTMSFNCCustomDataGrid.XYToCell(X, Y: Double): TTMSFNCDataGridCellCoord;
begin
  Result := Root.XYToCell(X, Y);
end;

function TTMSFNCCustomDataGrid.XYToColumn(X, Y: Double): Integer;
begin
  Result := Root.XYToColumn(X, Y);
end;

function TTMSFNCCustomDataGrid.XYToFooter(X, Y: Double): Boolean;
begin
  Result := PtInRectEx(GetFooterRect, PointF(X, Y));
end;

function TTMSFNCCustomDataGrid.XYToHeader(X, Y: Double): Boolean;
begin
  Result := PtInRectEx(GetHeaderRect, PointF(X, Y));
end;

function TTMSFNCCustomDataGrid.XYToHeaderGroup(X,
  Y: Double): TTMSFNCDataGridHeaderGroup;
begin
  Result := InternalXYToHeaderGroup(X, Y, HeaderGroups, False, False);
end;

function TTMSFNCCustomDataGrid.XYToRenderer(X,
  Y: Double): TTMSFNCDataGridRenderer;
begin
  Result := Root.XYToRenderer(X, Y);
end;

function TTMSFNCCustomDataGrid.XYToRow(X, Y: Double): Integer;
begin
  Result := Root.XYToRow(X, Y);
end;

procedure TTMSFNCCustomDataGrid.Calculate;
begin
  CalculateHeader;
end;

procedure TTMSFNCCustomDataGrid.CalculateHeader;
begin
  CalculateHeaderVisualGrouping;
  CalculateHeaderBar;
end;

procedure TTMSFNCCustomDataGrid.CalculateHeaderBar;
var
  r: TRectF;
  I, x, y, w, h: Integer;
  b: TTMSFNCDataGridButton;
  btn: TTMSFNCToolBarButton;
  c: TTMSFNCDataGridCellControl;
begin
  if (UpdateCount > 0) or IsDestroying or (Assigned(Root) and ((Root.UpdateCount > 0) or (Root.GetIsDestroying))) then
    Exit;

  if not Header.Visible or not Header.Bar.Visible or (GetHeaderSize <= 0) or (GetHeaderBarSize <= 0) then
  begin
    for I := 0 to Header.Bar.Buttons.Count - 1 do
    begin
      b := Header.Bar.Buttons[I];
      if Assigned(b.Button) then
        b.Button.Visible := False;
    end;
    Exit;
  end;

  r := GetHeaderBarRect;
  x := Round(r.Left) + 4;
  y := Round(r.Top) + 4;

  for I := 0 to Header.Bar.Buttons.Count - 1 do
  begin
    b := Header.Bar.Buttons[I];
    w := Round(b.Width);
    h := Round(r.Bottom - r.Top) - 8;

    if Assigned(b.Control) then
    begin
      c := b.Control;
      c.Parent := Self;
      c.Enabled := b.Enabled;
      c.Visible := b.Visible;
      c.Hint := b.Hint;

      c.SetBounds(x, y, w, h);

      if c.Visible then
        x := x + w + 4;
    end
    else
    begin
      btn := b.Button;
      if Assigned(btn) then
      begin
        btn.DataObject := b;
        btn.OnClick := DoHeaderButtonClick;
        btn.Parent := Self;
        btn.Text := b.Text;
        btn.Hint := b.Hint;
        btn.Enabled := b.Enabled;
        btn.Visible := b.Visible;
        if btn.AdaptToStyle <> AdaptToStyle then
          btn.AdaptToStyle := AdaptToStyle;
        btn.Bitmaps.Clear;
        btn.Bitmaps.AddBitmap(b.Bitmap);

        btn.SetBounds(x, y, w, h);

        if btn.Visible then
          x := x + w + 4;
      end;
    end
  end;

  Root.SetLocalRect(GetCalculationRect);
end;

procedure TTMSFNCCustomDataGrid.CalculateHeaderVisualGrouping;
begin
  if (UpdateCount > 0) or IsDestroying or (Assigned(Root) and ((Root.UpdateCount > 0) or (Root.GetIsDestroying))) then
    Exit;

  if not Header.Visible or not Header.VisualGrouping.Visible then
    Exit;

  FreeAndNil(FTempHeaderGroups);
  FVisualGroupingSize := 0;
  SetLength(FHeaderLevelRects, 0);
  GroupInfoToHeaderGroups(Root.IsGrouped, Root.GroupInfo, HeaderGroups, FDragHeaderGroup, FVisualGroupingSize, FHeaderLevelRects);
  Root.SetLocalRect(GetCalculationRect);
end;

function TTMSFNCCustomDataGrid.CanAddLevel(AHeaderGroups: TTMSFNCDataGridHeaderGroups;
  ASkipHeaderGroup: TTMSFNCDataGridHeaderGroup;
  ALevel: Integer;
  ACheckLevel: Boolean): Boolean;
var
  I, CountAtLevel0, CountAtLevel1, CountAtLevel2: Integer;
  CurrentLevel: Integer;
begin
  if Assigned(ASkipHeaderGroup) then
    CurrentLevel := ASkipHeaderGroup.Level
  else
    CurrentLevel := 0;

  if (ALevel < 0) or (ALevel > 2) then
    Exit(False);

  if ACheckLevel then
  begin
    for I := 0 to AHeaderGroups.Count - 1 do
    begin
      if (AHeaderGroups[I] <> ASkipHeaderGroup) and (AHeaderGroups[I].Level = ALevel) then
        Exit(False);
    end;
  end;

  CountAtLevel0 := 0;
  CountAtLevel1 := 0;
  CountAtLevel2 := 0;

  for I := 0 to AHeaderGroups.Count - 1 do
  begin
    if AHeaderGroups[I] = ASkipHeaderGroup then
      Continue;

    case AHeaderGroups[I].Level of
      0: Inc(CountAtLevel0);
      1: Inc(CountAtLevel1);
      2: Inc(CountAtLevel2);
    end;
  end;

  if CurrentLevel = ALevel then
    Exit(True);

  if (CurrentLevel = 0) and (ALevel = 1) then
    Exit(CountAtLevel0 > 0);

  if (CurrentLevel = 1) and (ALevel = 2) then
    Exit(CountAtLevel1 > 0);

  if (CurrentLevel = 0) and (ALevel = 2) then
    Exit((CountAtLevel0 > 0) and (CountAtLevel1 > 0));

  if (CurrentLevel = 2) and ((ALevel = 1) or (ALevel = 0)) then
    Exit(True);

  if (CurrentLevel = 1) and (ALevel = 0) then
  begin
    Exit(
      (CountAtLevel2 > 0) and (CountAtLevel1 = 1) or
      (CountAtLevel2 = 0)
    );
  end;

  Result := False;
end;

function TTMSFNCCustomDataGrid.CancelEditing: Boolean;
begin
  Result := Root.CancelEditing;
end;

function TTMSFNCCustomDataGrid.CanDeleteColumn(AColumn: Integer): Boolean;
begin
  Result := Root.CanDeleteColumn(AColumn);
end;

function TTMSFNCCustomDataGrid.CanDeleteRow(ARow: Integer): Boolean;
begin
  Result := Root.CanDeleteRow(ARow);
end;

function TTMSFNCCustomDataGrid.CanInsertColumn(AColumn: Integer): Boolean;
begin
  Result := Root.CanInsertColumn(AColumn);
end;

function TTMSFNCCustomDataGrid.CanInsertRow(ARow: Integer): Boolean;
begin
  Result := Root.CanInsertRow(ARow);
end;

function TTMSFNCCustomDataGrid.CanMergeCells(AColumn, ARow, AColumnCount,
  ARowCount: Integer): Boolean;
begin
  Result := Root.CanMergeCells(AColumn, ARow, AColumnCount, ARowCount);
end;

function TTMSFNCCustomDataGrid.CanMoveColumn(AFromColumn,
  AToColumn: Integer): Boolean;
begin
  Result := Root.CanMoveColumn(AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.CanMoveRow(AFromRow, AToRow: Integer): Boolean;
begin
  Result := Root.CanMoveRow(AFromRow, AToRow);
end;

function TTMSFNCCustomDataGrid.CanSwapColumn(AFromColumn,
  AToColumn: Integer): Boolean;
begin
  Result := Root.CanSwapColumn(AFromColumn, AToColumn);
end;

function TTMSFNCCustomDataGrid.CanSwapRow(AFromRow, AToRow: Integer): Boolean;
begin
  Result := Root.CanSwapRow(AFromRow, AToRow);
end;

constructor TTMSFNCCustomDataGrid.Create(AOwner: TComponent);
begin
  inherited;
  FDesigner := True;

  {$IFNDEF WEBLIB}
  if IsDesigning then
  begin
    FDesignerControl := TTMSFNCDataGridDesigner.Create(Self);
    FDesignerControl.HitTest := True;
    FDesignerControl.Stored := False;
    FDesignerControl.Parent := Self;
    FDesignerControl.ControlAlignment := caLeft;
    FDesignerControl.Stroke.Width := 1;
    FDesignerControl.Stroke.Color := gcGray;
    FDesignerControl.Fill.Color := Lighter(gcLightsteelblue, 80);
  end;
  {$ENDIF}

  FFooter := TTMSFNCDataGridFooter.Create(Self);
  FFooter.OnChangeRebuild := FooterChangedRebuild;
  FHeader := TTMSFNCDataGridHeader.Create(Self);
  FHeader.OnChangeRebuild := HeaderChangedRebuild;

  Width := 540;
  Height := 400;
  ShowAcceleratorChar := False;

  {$IFNDEF WEBLIB}
  if Assigned(FDesignerControl) then
  begin
    case FDesignerControl.ControlAlignment of
      caTop, caBottom: Height := Height + FDesignerControl.Height;
      caLeft, caRight: Width := Width + FDesignerControl.Width;
    end;
  end;
  {$ENDIF}

  if (csDesigning in ComponentState) and not ((csReading in Owner.ComponentState) or (csLoading in Owner.ComponentState)) then
    InitSample;
end;

procedure TTMSFNCCustomDataGrid.CutToClipboard(ASelectedCells: Boolean);
begin
  Root.CutToClipboard(ASelectedCells);
end;

function TTMSFNCCustomDataGrid.GetAdapter: TTMSFNCDataGridDataAdapter;
begin
  Result := Root.Adapter;
end;

function TTMSFNCCustomDataGrid.GetBitmapContainer: TTMSFNCBitmapContainer;
begin
  Result := Root.BitmapContainer;
end;

function TTMSFNCCustomDataGrid.GetBitmapNames(AColumn, ARow: Integer): string;
begin
  Result := Root.BitmapNames[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetBitmaps(AColumn,
  ARow: Integer): TTMSFNCBitmap;
begin
  Result := Root.Bitmaps[AColumn, ARow];
end;

procedure TTMSFNCCustomDataGrid.SetBitmap(AColumn, ARow: Integer;
  AName: string);
begin
  Root.SetBitmap(AColumn, ARow, AName);
end;

procedure TTMSFNCCustomDataGrid.SetBitmap(ACell: TTMSFNCDataGridCellCoord;
  ABitmap: TTMSFNCBitmap);
begin
  Root.SetBitmap(ACell, ABitmap);
end;

procedure TTMSFNCCustomDataGrid.SetBitmap(AColumn, ARow: Integer;
  ABitmap: TTMSFNCBitmap);
begin
  Root.SetBitmap(AColumn, ARow, ABitmap);
end;

procedure TTMSFNCCustomDataGrid.SetBitmap(ACell: TTMSFNCDataGridCellCoord;
  AName: string);
begin
  Root.SetBitmap(ACell, AName);
end;

procedure TTMSFNCCustomDataGrid.SetBitmapColumn(AColumn: Integer;
  ABitmap: TTMSFNCBitmap);
begin
  Root.SetBitmapColumn(AColumn, ABitmap);
end;

procedure TTMSFNCCustomDataGrid.SetBitmapColumn(AColumn: Integer;
  AName: string);
begin
  Root.SetBitmapColumn(AColumn, AName);
end;

procedure TTMSFNCCustomDataGrid.SetBitmapContainer(const Value: TTMSFNCBitmapContainer);
begin
  Root.BitmapContainer := Value;
end;

procedure TTMSFNCCustomDataGrid.AddButton(ACell: TTMSFNCDataGridCellCoord; AText: string = '');
begin
  Root.AddButton(ACell, AText);
end;

procedure TTMSFNCCustomDataGrid.AddBitmap(ACell: TTMSFNCDataGridCellCoord;
  AName: string);
begin
  Root.AddBitmap(ACell, AName);
end;

procedure TTMSFNCCustomDataGrid.AddBitmap(AColumn, ARow: Integer;
  AName: string);
begin
  Root.AddBitmap(AColumn, ARow, AName);
end;

procedure TTMSFNCCustomDataGrid.AddBitmap(ACell: TTMSFNCDataGridCellCoord;
  ABitmap: TTMSFNCBitmap);
begin
  Root.AddBitmap(ACell, ABitmap);
end;

procedure TTMSFNCCustomDataGrid.AddBitmap(AColumn, ARow: Integer;
  ABitmap: TTMSFNCBitmap);
begin
  Root.AddBitmap(AColumn, ARow, ABitmap);
end;

procedure TTMSFNCCustomDataGrid.AddBitmapColumn(AColumn: Integer;
  AName: string);
begin
  Root.AddBitmapColumn(AColumn, AName);
end;

procedure TTMSFNCCustomDataGrid.AddBitmapColumn(AColumn: Integer;
  ABitmap: TTMSFNCBitmap);
begin
  Root.AddBitmapColumn(AColumn, ABitmap);
end;

procedure TTMSFNCCustomDataGrid.AddButton(AColumn, ARow: Integer; AText: string = '');
begin
  Root.AddButton(AColumn, ARow, AText);
end;

procedure TTMSFNCCustomDataGrid.AddButtonColumn(AColumn: Integer; AText: string = '');
begin
  Root.AddButtonColumn(AColumn, AText);
end;

procedure TTMSFNCCustomDataGrid.AddCheckBox(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean);
begin
  Root.AddCheckBox(ACell, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddCheckBox(AColumn, ARow: Integer;
  AValue: Boolean);
begin
  Root.AddCheckBox(AColumn, ARow, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddCheckBoxColumn(AColumn: Integer;
  AValue: Boolean);
begin
  Root.AddCheckBoxColumn(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddDataCheckBox(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.AddDataCheckBox(ACell);
end;

procedure TTMSFNCCustomDataGrid.AddDataCheckBox(AColumn, ARow: Integer);
begin
  Root.AddDataCheckBox(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.AddDataCheckBoxColumn(AColumn: Integer);
begin
  Root.AddDataCheckBoxColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.AddColumn;
begin
  Root.AddColumn;
end;

function TTMSFNCCustomDataGrid.AddGrid(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridRenderer;
begin
  Result := Root.AddGrid(ACell);
end;

function TTMSFNCCustomDataGrid.AddGrid(AColumn, ARow: Integer): TTMSFNCDataGridRenderer;
begin
  Result := Root.AddGrid(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.AddNodeRow(ARow, AChildCount, ALevel: Integer;
  AMerge: Boolean);
begin
  Root.AddNodeRow(ARow, AChildCount, ALevel, AMerge);
end;

procedure TTMSFNCCustomDataGrid.AddProgressBar(ACell: TTMSFNCDataGridCellCoord;
  AValue: Integer);
begin
  Root.AddProgressBar(ACell, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddProgressBar(AColumn, ARow, AValue: Integer);
begin
  Root.AddProgressBar(AColumn, ARow, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddProgressBarColumn(AColumn, AValue: Integer);
begin
  Root.AddProgressBarColumn(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddDataProgressBar(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.AddDataProgressBar(ACell);
end;

procedure TTMSFNCCustomDataGrid.AddDataProgressBar(AColumn, ARow: Integer);
begin
  Root.AddDataProgressBar(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.AddDataProgressBarColumn(AColumn: Integer);
begin
  Root.AddDataProgressBarColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.AddRadioButton(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.AddRadioButton(ACell, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.AddRadioButton(AColumn, ARow: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.AddRadioButton(AColumn, ARow, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.AddRadioButtonColumn(AColumn: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.AddRadioButtonColumn(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddDataRadioButton(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.AddDataRadioButton(ACell, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.AddDataRadioButton(AColumn, ARow: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.AddDataRadioButton(AColumn, ARow, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.AddDataRadioButtonColumn(AColumn: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.AddDataRadioButtonColumn(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.AddFilterEditor(AColumn, ARow: Integer);
begin
  Root.AddFilterEditor(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.AddFilterEditor(
  ACell: TTMSFNCDataGridCellCoord);
begin
  Root.AddFilterEditor(ACell);
end;

procedure TTMSFNCCustomDataGrid.AddRow;
begin
  Root.AddRow;
end;

procedure TTMSFNCCustomDataGrid.ApplyFilter;
begin
  Root.ApplyFilter;
end;

procedure TTMSFNCCustomDataGrid.ApplyStyle;
var
  c: TTMSFNCGraphicsColor;
begin
  inherited;
  BeginUpdate;
  try
    c := gcNull;
    if TTMSFNCStyles.GetStyleBackgroundFillColor(c) then
    begin
      Header.Fill.Color := c;
      Footer.Stroke.Color := c;
    end;

    if TTMSFNCStyles.GetStyleBackgroundFillColor(c) then
    begin
      Header.VisualGrouping.LevelIndicationFill.Color := Lighter(c, 30);
      Header.VisualGrouping.LevelActiveIndicationFill.Color := Lighter(c, 30);
      Footer.Stroke.Color := c;
    end;

    c := gcNull;
    if TTMSFNCStyles.GetStyleHeaderFillColor(c) then
      Header.Bar.Fill.Color := c;

    c := gcNull;
    if TTMSFNCStyles.GetStyleHeaderFillColor(c) then
    begin
      Header.VisualGrouping.Layout.Fill.Color := c;
      Header.VisualGrouping.Layout.Fill.Kind := gfkSolid;
    end;

    c := gcNull;
    if TTMSFNCStyles.GetStyleHeaderFillColorTo(c) then
    begin
      Header.VisualGrouping.Layout.Fill.ColorTo := c;
      Header.VisualGrouping.Layout.Fill.Kind := gfkGradient;
    end;

    c := gcNull;
    if TTMSFNCStyles.GetStyleTextFontColor(c) then
      Header.VisualGrouping.Layout.Font.Color := c;

    c := gcNull;
    if TTMSFNCStyles.GetStyleLineFillColor(c) then
    begin
      Header.VisualGrouping.Layout.Stroke.Color := c;
      Header.Stroke.Color := c;
      Header.Bar.Stroke.Color := c;
      Footer.Stroke.Color := c;
      Header.VisualGrouping.LevelIndicationStroke.Color := c;
      Header.VisualGrouping.LevelActiveIndicationStroke.Color := c;
    end;

  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCCustomDataGrid.RandomFill(ADoFixed: Boolean;
  ARandomNumber: Integer);
begin
  Root.RandomFill(ADoFixed, ARandomNumber);
end;

function TTMSFNCCustomDataGrid.RealToDisplayCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
begin
  Result := Root.RealToDisplayCell(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveCheckBox(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveCheckBox(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveCheckBox(AColumn, ARow: Integer);
begin
  Root.RemoveCheckBox(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveDataCheckBox(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveDataCheckBox(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveDataCheckBox(AColumn, ARow: Integer);
begin
  Root.RemoveDataCheckBox(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveProgressBar(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveProgressBar(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveProgressBar(AColumn, ARow: Integer);
begin
  Root.RemoveProgressBar(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveButton(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveButton(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveBitmap(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveBitmap(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveBitmap(AColumn, ARow: Integer);
begin
  Root.RemoveBitmap(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveButton(AColumn, ARow: Integer);
begin
  Root.RemoveButton(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveRadioButton(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveRadioButton(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveRadioButton(AColumn, ARow: Integer);
begin
  Root.RemoveRadioButton(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveDataRadioButton(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveDataRadioButton(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveDataRadioButton(AColumn, ARow: Integer);
begin
  Root.RemoveDataRadioButton(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveFilter;
begin
  Root.RemoveFilter;
end;

procedure TTMSFNCCustomDataGrid.RemoveLayout(AColumn, ARow: Integer);
begin
  Root.RemoveLayout(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveLayout(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveLayout(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveFilterEditor(AColumn, ARow: Integer);
begin
  Root.RemoveFilterEditor(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveFilterEditor(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveFilterEditor(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveGrid(AColumn, ARow: Integer);
begin
  Root.RemoveGrid(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.RemoveGrid(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.RemoveGrid(ACell);
end;

procedure TTMSFNCCustomDataGrid.RemoveNodeRow(ARow: Integer);
begin
  Root.RemoveNodeRow(ARow);
end;

function TTMSFNCCustomDataGrid.Replace(AOriginalValue,
  ANewValue: string): Integer;
begin
  Result := Root.Replace(AOriginalValue, ANewValue);
end;

function TTMSFNCCustomDataGrid.Replace(AOriginalValue, ANewValue: string;
  AParameters: TTMSFNCDataGridDataFindParameters): Integer;
begin
  Result := Root.Replace(AOriginalValue, ANewValue, AParameters);
end;

procedure TTMSFNCCustomDataGrid.ResetDrag;
begin
  Root.ResetDrag;
end;

procedure TTMSFNCCustomDataGrid.ResetToDefaultStyle;
begin
  BeginUpdate;
  try
    Fill.Kind := gfkSolid;
    Stroke.Kind := gskSolid;
    Fill.Color := gcWhite;
    Stroke.Color := gcSilver;
  finally
    EndUpdate;
  end;
end;

function TTMSFNCCustomDataGrid.GetHeader: TTMSFNCDataGridHeader;
begin
  Result := FHeader;
end;

function TTMSFNCCustomDataGrid.GetHeaderGroups: TTMSFNCDataGridHeaderGroups;
begin
  if not Assigned(FHeaderGroups) then
    FHeaderGroups := TTMSFNCDataGridHeaderGroups.Create;

  Result := FHeaderGroups;
end;

function TTMSFNCCustomDataGrid.GetHeaderBarRect: TRectF;
var
  r: TRectF;
begin
  r := GetHeaderRect;
  Result := RectF(r.Left, r.Bottom - GetHeaderBarSize, r.Right, r.Bottom);
end;

function TTMSFNCCustomDataGrid.GetHeaderBarSize: Double;
begin
  Result := 0;
  if Header.Visible and Header.Bar.Visible then
    Result := Header.Bar.Size;
end;

function TTMSFNCCustomDataGrid.GetHeaderRect: TRectF;
begin
  Result := inherited GetCalculationRect;

  Result.Top := Result.Top + GetDesignerTopSize;
  Result.Left := Result.Left + GetDesignerLeftSize;
  Result.Bottom := Result.Top + GetHeaderSize + 1;
  Result.Right := Result.Right - GetDesignerRightSize;
end;

function TTMSFNCCustomDataGrid.GetHeaderSize: Double;
begin
  Result := 0;
  if Header.Visible then
  begin
    if Header.AutoSize then
    begin
      if Header.VisualGrouping.Visible then
      begin
        if FVisualGroupingSize > 0 then
          Result := FVisualGroupingSize
        else
          Result := Header.VisualGrouping.Size;
      end;

      if Header.Bar.Visible then
        Result := Result + Header.Bar.Size;
    end
    else
      Result := Header.Size;
  end;
end;

function TTMSFNCCustomDataGrid.GetHintString: string;
var
  h: string;
  r: TTMSFNCDataGridRendererOpen;
begin
  Result := inherited GetHintString;
  if Assigned(FFocusedRenderer) then
    r := TTMSFNCDataGridRendererOpen(FFocusedRenderer)
  else
    r := TTMSFNCDataGridRendererOpen(Root);

  if r.HasHint then
  begin
    h := r.GetHintString;
    Result := h;
  end;
end;

function TTMSFNCCustomDataGrid.GetHMax: Single;
begin
  Result := Root.GetHMax;
end;

function TTMSFNCCustomDataGrid.GetTextAligns(AColumn,
  ARow: Integer): TTMSFNCGraphicsTextAlign;
begin
  Result := Root.TextAligns[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetHorizontalScrollBar: TScrollBar;
begin
  Result := Root.HorizontalScrollBar;
end;

function TTMSFNCCustomDataGrid.GetHorizontalScrollBarVisible: Boolean;
begin
  Result := Root.HorizontalScrollBarVisible;
end;

function TTMSFNCCustomDataGrid.GetHScrollValue: Single;
begin
  Result := Root.GetHScrollValue;
end;

function TTMSFNCCustomDataGrid.GetHViewPortSize: Single;
begin
  Result := Root.GetHViewPortSize;
end;

function TTMSFNCCustomDataGrid.HasHint: Boolean;
var
  h: string;
  r: TTMSFNCDataGridRendererOpen;
begin
  Result := inherited HasHint;
  if Assigned(FFocusedRenderer) then
    r := TTMSFNCDataGridRendererOpen(FFocusedRenderer)
  else
    r := TTMSFNCDataGridRendererOpen(Root);

  if Assigned(r) and r.HasHint then
  begin
    h := r.GetHintString;
    Result := h <> '';
  end;
end;

function TTMSFNCCustomDataGrid.HasRowData(ARow: Integer): Boolean;
begin
  Result := Root.HasRowData(ARow);
end;

procedure TTMSFNCCustomDataGrid.ChangeDPIScale(M: Integer; D: Integer);
begin
  inherited;
  BeginUpdate;
  try
    Header.VisualGrouping.Layout.Font.Height := TTMSFNCUtils.MulDivInt(Header.VisualGrouping.Layout.Font.Height, M, D);
    Header.Bar.Size := TTMSFNCUtils.MulDivInt(Header.Bar.Size, M, D);
    Header.VisualGrouping.Size := TTMSFNCUtils.MulDivInt(Header.VisualGrouping.Size, M, D);
    Header.Size := TTMSFNCUtils.MulDivInt(Header.Size, M, D);
    Footer.Size := TTMSFNCUtils.MulDivInt(Footer.Size, M, D);
    Root.ChangeDPIScale(M, D);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCCustomDataGrid.HandleDblClick(X, Y: Single);
var
  m: TTMSFNCDataGridMouseInfo;
  r: TTMSFNCDataGridRenderer;
  c: TPointF;
begin
  inherited;
  c := ScreenToLocalEx(TTMSFNCUtils.GetMousePos);
  m := DefaultMouseInfo;
  m.Kind := gmkDouble;
  m.Handled := False;
  m.X := c.X;
  m.Y := c.Y;

  r := Root.XYToRenderer(X, Y);
  if not Assigned(r) then
    r := Root;

  if Assigned(r) then
    r.HandleMouse(m);

  FDoubleClicked := True;
end;

procedure TTMSFNCCustomDataGrid.DataBeginUpdate;
begin
  BeginUpdate;
end;

procedure TTMSFNCCustomDataGrid.DataClearData;
begin
  TTMSFNCDataGridRendererOpen(Root).DataClearData;
end;

procedure TTMSFNCCustomDataGrid.DataEndUpdate;
begin
  EndUpdate;
end;

function TTMSFNCCustomDataGrid.DataGetItemIndex: Integer;
begin
  Result := TTMSFNCDataGridRendererOpen(Root).DataGetItemIndex;
end;

procedure TTMSFNCCustomDataGrid.DataInsertRow(AInsertPosition: Integer);
begin
  TTMSFNCDataGridRendererOpen(Root).DataInsertRow(AInsertPosition);
end;

procedure TTMSFNCCustomDataGrid.DataSetItemIndex(AValue: Integer);
begin
  TTMSFNCDataGridRendererOpen(Root).DataSetItemIndex(AValue);
end;

procedure TTMSFNCCustomDataGrid.DeleteColumn(AColumn: Integer);
begin
  Root.DeleteColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.DeleteColumns(AColumn, ACount: Integer);
begin
  Root.DeleteColumns(AColumn, ACount);
end;

procedure TTMSFNCCustomDataGrid.DeleteRow(ARow: Integer);
begin
  Root.DeleteRow(ARow);
end;

procedure TTMSFNCCustomDataGrid.DeleteRows(ARow, ACount: Integer);
begin
  Root.DeleteRows(ARow, ACount);
end;

destructor TTMSFNCCustomDataGrid.Destroy;
begin
  {$IFNDEF WEBLIB}
  FreeAndNil(FDesignerControl);
  {$ENDIF}
  FHeader.Free;
  FFooter.Free;
  FreeAndNil(FHeaderGroups);
  FreeAndNil(FTempHeaderGroups);
  if Assigned(FCalculateGraphics) then
    FCalculateGraphics.EndScene;
  FreeAndNil(FCalculateGraphics);
  inherited;
end;

procedure TTMSFNCCustomDataGrid.DestroyGraphicElements;
begin
  inherited;
  ClearControlCache;
end;

function TTMSFNCCustomDataGrid.DisplayToRealCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
begin
  Result := Root.DisplayToRealCell(ACell);
end;

procedure TTMSFNCCustomDataGrid.HandleDialogKey(var Key: Word; Shift: TShiftState);
var
  k: TTMSFNCDataGridKeyInfo;
begin
  k := DefaultKeyInfo;
  k.Handled := True;
  k.Key := Key;
  k.Kind := gkkDialog;
  k.Shift := Shift;
  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.HandleKey(k)
  else
    Root.HandleKey(k);

  Key := k.Key;

  if k.Handled then
    inherited;
end;

procedure TTMSFNCCustomDataGrid.HandleMouseEnter;
var
  m: TTMSFNCDataGridMouseInfo;
begin
  inherited;
  m := DefaultMouseInfo;
  m.Kind := gmkEnter;
  Root.HandleMouse(m);
end;

procedure TTMSFNCCustomDataGrid.HandleMouseLeave;
var
  m: TTMSFNCDataGridMouseInfo;
begin
  inherited;
  m := DefaultMouseInfo;
  m.Kind := gmkLeave;
  Root.HandleMouse(m);
end;

procedure TTMSFNCCustomDataGrid.HandleDragDrop(const Source: TObject;
  const Point: TPointF);
var
  c: TPointF;
  r: TTMSFNCDataGridRenderer;
begin
  inherited;
  if Assigned(OnDragDrop) then
    Exit;

  c := ScreenToLocalEx(TTMSFNCUtils.GetMousePos);

  r := Root.XYToRenderer(c.X, c.Y);
  if not Assigned(r) then
    r := Root;

  if Assigned(r) then
    TTMSFNCDataGridRendererOpen(r).HandleDragDrop(Source, Point);
end;

function TTMSFNCCustomDataGrid.HandleDragOnHeader(X, Y: Single): Boolean;
var
  NeedsInit, a: Boolean;
  Col: Integer;
  GI: TTMSFNCDataGridDataGroupInfo;
  DragHG: TTMSFNCDataGridHeaderGroup;
  InsertIdx: Integer;
begin
  Result := False;

  if Assigned(FDragHeaderGroup) then
  begin
    OffsetRectEx(FDragHeaderGroup.FRect, X - FMoveX, Y - FMoveY);

    if not Assigned(FTempHeaderGroups) then
    begin
      FTempHeaderGroups := TTMSFNCDataGridHeaderGroups.Create;
      GI := DefaultGroupInfo;
      HeaderGroupsToGroupInfo(HeaderGroups, GI);
      GroupInfoToHeaderGroups(True, GI, FTempHeaderGroups);
    end;

    if Assigned(FTempHeaderGroups) then
    begin
      DragHG := nil;
      if FTempHeaderGroups.Find(FDragHeaderGroup.Column, DragHG) and Assigned(DragHG) then
      begin
        DragHG.FLevel := PositionToHeaderGroupLevel(FTempHeaderGroups, DragHG, Y);

        InsertIdx := PositionToHeaderGroupIndex(FTempHeaderGroups, DragHG, DragHG.FLevel, X);

        if InRange(InsertIdx, 0, FTempHeaderGroups.Count) then
        begin
          FTempHeaderGroups.Extract(DragHG);
          FTempHeaderGroups.Insert(InsertIdx, DragHG);
        end;

        GI := DefaultGroupInfo;
        HeaderGroupsToGroupInfo(FTempHeaderGroups, GI);
        GroupInfoToHeaderGroups(True, GI, FTempHeaderGroups);
      end;
    end;

    Invalidate;
    Result := PtInRectEx(GetHeaderRect, PointF(X, Y));
  end
  else if Assigned(FDownHeaderGroup) and Header.VisualGrouping.Dragging then
  begin
    a := True;
    DoCanDragHeaderGroup(FDownHeaderGroup.Column, a);
    if a and ((Abs(X - FDownX) > Options.Mouse.ClickMargin) or (Abs(Y - FDownY) > Options.Mouse.ClickMargin)) then
    begin
      NeedsInit := not Assigned(FDragHeaderGroup);
      FDragHeaderGroup := FDownHeaderGroup;
      Cursor := crDrag;

      if NeedsInit then
      begin
        Col := FDragHeaderGroup.Column;
        Root.Update;
        HeaderGroups.Find(Col, FDragHeaderGroup);
      end;

      FDownHeaderGroup := nil;
    end;

    Result := PtInRectEx(GetHeaderRect, PointF(X, Y));
  end;

  FMoveX := X;
  FMoveY := Y;
end;

procedure TTMSFNCCustomDataGrid.HandleDragOver(const Source: TObject;
  const Point: TPointF; var Accept: Boolean);
var
  c: TPointF;
  r: TTMSFNCDataGridRenderer;
begin
  inherited;
  if Assigned(OnDragOver) then
    Exit;

  c := Point;

  r := Root.XYToRenderer(c.X, c.Y);
  if not Assigned(r) then
    r := Root;

  if Assigned(r) then
    TTMSFNCDataGridRendererOpen(r).HandleDragOver(Source, Point, Accept);
end;

function TTMSFNCCustomDataGrid.HandleDropOnHeader(X, Y: Single; AShift: TShiftState): Boolean;
var
  CurrentHG, TargetHG: TTMSFNCDataGridHeaderGroup;
  DoAction, CanSort, Modified: Boolean;
  Col, Level, InsertIdx: Integer;
  SortIdx: TTMSFNCDataGridSortIndex;
  SortDir: TTMSFNCDataGridSortDirection;
begin
  Result := False;
  TargetHG := InternalXYToHeaderGroup(X, Y, HeaderGroups, True, False);

  if FDraggingOnHeader and IsCellValid(Root.DragCell) then
  begin
    Cursor := crDefault;
    Col := Root.DragCell.Column;
    DoAction := True;
    DoBeforeDropHeaderGroup(Col, DoAction);

    if DoAction then
    begin
      if not HeaderGroups.Find(Col, CurrentHG) then
      begin
        CurrentHG := TTMSFNCDataGridHeaderGroup.Create;
        CurrentHG.FNew := True;
        CurrentHG.OnBeforeDraw := DoBeforeDrawHeaderGroup;
        CurrentHG.OnAfterDraw := DoAfterDrawHeaderGroup;
      end;

      if CurrentHG <> TargetHG then
      begin
        Modified := (CurrentHG.FColumn <> Col) or CurrentHG.FNew;
        CurrentHG.FColumn := Col;

        if Assigned(TargetHG) then
        begin
          Modified := Modified or (CurrentHG.FLevel <> TargetHG.Level);
          CurrentHG.FLevel := TargetHG.Level;

          if HeaderGroups.Contains(CurrentHG) then
            HeaderGroups.Extract(CurrentHG);

          HeaderGroups.Insert(HeaderGroups.IndexOf(TargetHG), CurrentHG);
        end
        else
        begin
          Level := PositionToHeaderGroupLevel(HeaderGroups, CurrentHG, Y);
          Modified := Modified or (CurrentHG.FLevel <> Level);
          CurrentHG.FLevel := Level;

          if HeaderGroups.Contains(CurrentHG) then
            HeaderGroups.Extract(CurrentHG);

          HeaderGroups.Add(CurrentHG);
        end;

        if Modified then
          GroupActiveHeaders
        else
          Invalidate;
      end;

      DoAfterDropHeaderGroup(Col);
    end;

    if Assigned(FDragHeaderGroup) then
    begin
      FDragHeaderGroup.Reset;
      FDragHeaderGroup := nil;
      Root.Update;
    end;
  end
  else if not Assigned(FDragHeaderGroup) and Assigned(FDownHeaderGroup) and (FDownHeaderGroup = TargetHG) then
  begin
    CurrentHG := FDownHeaderGroup;
    Col := IfThen(Assigned(CurrentHG), CurrentHG.Column, -1);

    if (Col <> -1) and CurrentHG.XYToCloseIndicator(X, Y) then
    begin
      DoAction := True;
      DoBeforeCloseHeaderGroup(Col, DoAction);
      if DoAction then
      begin
        HeaderGroups.Remove(CurrentHG);
        GroupActiveHeaders;
        DoAfterCloseHeaderGroup(Col);
      end;
    end
    else if (Col <> -1) and Options.Sorting.Enabled and Header.VisualGrouping.SortOnClick then
    begin
      DoAction := True;
      DoBeforeSortHeaderGroup(Col, DoAction);
      if DoAction then
      begin
        CanSort := True;
        TTMSFNCDataGridRendererOpen(Root).DoBeforeSortColumn(Col, CanSort);

        if CanSort then
        begin
          if not (ssShift in AShift) then
            SortIndexList.Clear;

          if not SortIndexList.Find(Col, SortIdx) then
          begin
            SortDir := TTMSFNCUtils.IfElse<TTMSFNCDataGridSortDirection>(CurrentHG.SortIndicator = gsiAscending, gsdDescending, gsdAscending);
            SortIndexList.Add(Col, SortDir);
          end
          else
          begin
            SortIdx.Direction := TTMSFNCUtils.IfElse<TTMSFNCDataGridSortDirection>(SortIdx.Direction = gsdAscending, gsdDescending, gsdAscending);
          end;

          if Root.IsGrouped then
            Root.GroupSortIndexed
          else
            Root.SortIndexed;
        end;

        TTMSFNCDataGridRendererOpen(Root).DoAfterSortColumn(Col);
      end;

      DoAfterSortHeaderGroup(Col);
    end;

    if Col <> -1 then
      DoHeaderGroupClick(Col);

    FDownHeaderGroup := nil;
    Result := True;
  end
  else if Assigned(FDragHeaderGroup) and (TargetHG <> FDragHeaderGroup) then
  begin
    CurrentHG := FDragHeaderGroup;
    FDragHeaderGroup.Reset;
    FDragHeaderGroup := nil;
    FDownHeaderGroup := nil;

    Col := CurrentHG.Column;
    DoAction := True;
    DoBeforeDropHeaderGroup(Col, DoAction);

    if DoAction then
    begin
      if Assigned(TargetHG) then
      begin
        Modified := (CurrentHG.FColumn <> TargetHG.Column);
        CurrentHG.FColumn := TargetHG.Column;
        CurrentHG.FLevel := TargetHG.Level;
        TargetHG.FColumn := Col;
      end
      else
      begin
        Level := PositionToHeaderGroupLevel(HeaderGroups, CurrentHG, Y);
        InsertIdx := PositionToHeaderGroupIndex(HeaderGroups, CurrentHG, Level, X);
        Modified := (CurrentHG.FLevel <> Level);
        CurrentHG.FLevel := Level;

        if InRange(InsertIdx, 0, HeaderGroups.Count) then
        begin
          HeaderGroups.Extract(CurrentHG);
          HeaderGroups.Insert(InsertIdx, CurrentHG);
          Modified := True;
        end;
      end;

      if Modified then
        GroupActiveHeaders
      else
        Root.Update;

      DoAfterDropHeaderGroup(Col);
    end;

    Result := True;
  end
  else if FDraggedOnHeader then
  begin
    Root.Update;
    FDraggedOnHeader := False;
  end;
end;

procedure TTMSFNCCustomDataGrid.HandleEndDragOnHeader(X, Y: Single);
var
  headerCenterX, headerCenterY: Single;
begin
  FDraggingOnHeader := Header.VisualGrouping.Dragging and Header.VisualGrouping.Visible and Header.Visible and Options.Mouse.ColumnDragging and (gcdmVisualGrouping in Options.Mouse.ColumnDragModes) and
    IsCellValid(Root.DragCell) and XYToHeader(X, Y);

  if FDraggingOnHeader then
  begin
    FDraggedOnHeader := True;
    Cursor := crDrag;

    if not Assigned(FDragHeaderGroup) then
    begin
      if not HeaderGroups.Find(Root.DragCell.Column, FDragHeaderGroup) then
      begin
        FDragHeaderGroup := TTMSFNCDataGridHeaderGroup.Create;
        FDragHeaderGroup.OnBeforeDraw := DoBeforeDrawHeaderGroup;
        FDragHeaderGroup.OnAfterDraw := DoAfterDrawHeaderGroup;
        FDragHeaderGroup.FNew := True;

        InitializeHeaderGroup(CalculateGraphics, FDragHeaderGroup, Root.DragCell.Column, 0, X, Y);

        Root.Update;
        HeaderGroups.Add(FDragHeaderGroup);
      end;

      headerCenterX := FDragHeaderGroup.Rect.Left + (FDragHeaderGroup.Rect.Right - FDragHeaderGroup.Rect.Left) / 2;
      headerCenterY := FDragHeaderGroup.Rect.Top + (FDragHeaderGroup.Rect.Bottom - FDragHeaderGroup.Rect.Top) / 2;

      FDragHeaderGroup.FOrigRect := FDragHeaderGroup.Rect;
      FDragHeaderGroup.FRect.Left := FDragHeaderGroup.FRect.Left - (headerCenterX - X);
      FDragHeaderGroup.FRect.Top := FDragHeaderGroup.FRect.Top - (headerCenterY - Y);
      FDragHeaderGroup.FRect.Right := FDragHeaderGroup.FRect.Right - (headerCenterX - X);
      FDragHeaderGroup.FRect.Bottom := FDragHeaderGroup.FRect.Bottom - (headerCenterY - Y);
    end;
  end
  else if Assigned(FDragHeaderGroup) then
  begin
    if FDragHeaderGroup.FNew then
      HeaderGroups.Remove(FDragHeaderGroup);

    FDragHeaderGroup.Reset;
    FDragHeaderGroup := nil;
    Root.Update;
  end;
end;

procedure TTMSFNCCustomDataGrid.HandleEndDropOnHeader;
var
  needsUpdate: Boolean;
begin
  if Assigned(FDragHeaderGroup) then
  begin
    FDragHeaderGroup.Reset;
    Invalidate;
  end;

  needsUpdate := FDraggingOnHeader or Assigned(FDragHeaderGroup);

  FDragHeaderGroup := nil;
  FDraggingOnHeader := False;
  FDraggedOnHeader := False;

  if needsUpdate then
    Root.Update;
end;

procedure TTMSFNCCustomDataGrid.DrawFooter(AGraphics: TTMSFNCGraphics);
var
  r: TRectF;
  c: Boolean;
begin
  if not Footer.Visible or (GetFooterSize <= 0)  then
    Exit;

  c := True;
  r := GetFooterRect;
  DoBeforeDrawFooter(AGraphics, r, c);
  if c then
  begin
    DrawFooterBackground(AGraphics);
    DoAfterDrawFooter(AGraphics, r);
  end;
end;

procedure TTMSFNCCustomDataGrid.DrawFooterBackground(
  AGraphics: TTMSFNCGraphics);
var
  r: TRectF;
begin
  if not Footer.Visible or (GetFooterSize <= 0)  then
    Exit;

  r := GetFooterRect;
  AGraphics.Fill.Assign(Footer.Fill);
  AGraphics.Stroke.Assign(Footer.Stroke);
  AGraphics.DrawRectangle(r);
end;

procedure TTMSFNCCustomDataGrid.DrawHeader(AGraphics: TTMSFNCGraphics);
var
  r: TRectF;
  c: Boolean;
begin
  if not Header.Visible or (GetHeaderSize <= 0) then
    Exit;

  c := True;
  r := GetHeaderRect;
  DoBeforeDrawHeader(AGraphics, r, c);
  if c then
  begin
    DrawHeaderBackground(AGraphics);
    DrawHeaderBar(AGraphics);
    if Assigned(FTempHeaderGroups) then
      DrawHeaderGroups(AGraphics, FTempHeaderGroups)
    else
      DrawHeaderGroups(AGraphics, HeaderGroups);

    DoAfterDrawHeader(AGraphics, r);
  end;
end;

procedure TTMSFNCCustomDataGrid.DrawHeaderGroups(AGraphics: TTMSFNCGraphics; AHeaderGroups: TTMSFNCDataGridHeaderGroups);
var
  r: TRectF;
  I, L: Integer;
  hg, hgn: TTMSFNCDataGridHeaderGroup;
  levelHeight, hs, y: Single;
  InsertRects: array[0..2] of TRectF;
  st: TTMSFNCGraphicsSaveState;

  procedure DrawLevel(ARect: TRectF; ALevel: Integer; AIndication: Boolean);
  var
    a: Boolean;
  begin
    InflateRectEx(ARect, -1, -1);

    a := True;
    DoBeforeDrawHeaderGroupLevel(AGraphics, ARect, ALevel, AIndication, a);
    if a then
    begin
      if AIndication then
      begin
        AGraphics.Stroke.Assign(Header.VisualGrouping.LevelActiveIndicationStroke);
        AGraphics.Fill.Assign(Header.VisualGrouping.LevelActiveIndicationFill);
      end
      else
      begin
        AGraphics.Stroke.Assign(Header.VisualGrouping.LevelIndicationStroke);
        AGraphics.Fill.Assign(Header.VisualGrouping.LevelIndicationFill);
      end;

      if (AGraphics.Stroke.Kind in [gskDash, gskDot, gskDashDot, gskDashDotDot]) then
        AGraphics.StartSpecialPen;

      AGraphics.DrawRectangle(ARect);

      if (AGraphics.Stroke.Kind in [gskDash, gskDot, gskDashDot, gskDashDotDot]) then
        AGraphics.StopSpecialPen;

      DoAfterDrawHeaderGroupLevel(AGraphics, ARect, ALevel, AIndication);
    end;
  end;

begin
  if not Header.Visible or not Header.VisualGrouping.Visible or (GetHeaderSize <= 0) then
    Exit;

  st := AGraphics.SaveState;
  try
    AGraphics.ClipRect(GetVisualGroupingRect);

    if (FDraggingOnHeader or Assigned(FDragHeaderGroup)) and (AHeaderGroups.Count > 1) then
    begin
      r := GetHeaderRect;
      hs := GetHeaderSize;

      if Length(FHeaderLevelRects) > 0 then
        levelHeight := (FHeaderLevelRects[0].Bottom - FHeaderLevelRects[0].Top) + 2
      else
        levelHeight := hs / 3;

      y := r.Top + 3;
      for L := 0 to 2 do
      begin
        InsertRects[L] := RectF(r.Left, y, r.Right, Min(r.Bottom, y + levelHeight));
        y := y + levelHeight;
      end;

      for L := 2 downto 0 do
      begin
        if CanAddLevel(HeaderGroups, FDragHeaderGroup, L, False) then
          DrawLevel(InsertRects[L], L, PtInRectEx(InsertRects[L], PointF(FMoveX, FMoveY)));
      end;
    end;

    for I := 0 to AHeaderGroups.Count - 1 do
    begin
      hg := AHeaderGroups[I];
      if not Assigned(FDragHeaderGroup) and Header.VisualGrouping.ConnectionLines and (I < AHeaderGroups.Count - 1) then
      begin
        AGraphics.Stroke.Assign(Header.VisualGrouping.ConnectionStroke);
        hgn := AHeaderGroups[I + 1];

        AGraphics.DrawLine(PointF(hg.Rect.Right, hg.Rect.Top + (hg.Rect.Bottom - hg.Rect.Top) / 2), PointF(hgn.Rect.Left - 4, hg.Rect.Top + (hg.Rect.Bottom - hg.Rect.Top) / 2));

        if hgn.Level = hg.Level then
          AGraphics.DrawLine(PointF(hgn.Rect.Left - 4, hg.Rect.Top + (hg.Rect.Bottom - hg.Rect.Top) / 2), PointF(hgn.Rect.Left, hg.Rect.Top + (hg.Rect.Bottom - hg.Rect.Top) / 2))
        else
        begin
          AGraphics.DrawLine(PointF(hgn.Rect.Left - 4, hg.Rect.Top + (hg.Rect.Bottom - hg.Rect.Top) / 2), PointF(hgn.Rect.Left - 4, hgn.Rect.Top + (hgn.Rect.Bottom - hgn.Rect.Top) / 2));
          AGraphics.DrawLine(PointF(hgn.Rect.Left - 4, hgn.Rect.Top + (hgn.Rect.Bottom - hgn.Rect.Top) / 2), PointF(hgn.Rect.Left, hgn.Rect.Top + (hgn.Rect.Bottom - hgn.Rect.Top) / 2));
        end;
      end;

      if not hg.FNew then
      begin
        hg.Active := FDragHeaderGroup = hg;
        hg.Draw(AGraphics);
      end;
    end;

  //  if Assigned(FDragHeaderGroup) then
  //  begin
  //    FDragHeaderGroup.Active := False;
  //    FDragHeaderGroup.Draw(AGraphics);
  //  end;
  finally
    AGraphics.RestoreState(st);
  end;
end;

procedure TTMSFNCCustomDataGrid.DrawHeaderBackground(
  AGraphics: TTMSFNCGraphics);
var
  r: TRectF;
begin
  if not Header.Visible or (GetHeaderSize <= 0) then
    Exit;

  r := GetHeaderRect;
  AGraphics.Fill.Assign(Header.Fill);
  AGraphics.Stroke.Assign(Header.Stroke);
  AGraphics.DrawRectangle(r);
end;

procedure TTMSFNCCustomDataGrid.DrawHeaderBar(AGraphics: TTMSFNCGraphics);
var
  r: TRectF;
begin
  if not Header.Visible or not Header.Bar.Visible or (GetHeaderSize <= 0) or (GetHeaderBarSize <= 0) then
    Exit;

  r := GetHeaderBarRect;

  AGraphics.Fill.Assign(Header.Bar.Fill);
  AGraphics.Stroke.Assign(Header.Bar.Stroke);
  AGraphics.DrawRectangle(r);
end;

procedure TTMSFNCCustomDataGrid.EditCell(ACell: TTMSFNCDataGridCellCoord; ACellStart: TTMSFNCDataGridEditCellStart = gcsNone; AInsertKey: Boolean = False; AValue: string = ''; AKey: Word = 0);
begin
  Root.EditCell(ACell, ACellStart, AInsertKey, AValue, AKey);
end;

procedure TTMSFNCCustomDataGrid.ExpandAllNodes;
begin
  Root.ExpandAllNodes;
end;

procedure TTMSFNCCustomDataGrid.ExpandNode(ARow: Integer);
begin
  Root.ExpandNode(ARow);
end;

function TTMSFNCCustomDataGrid.GetDocURL: string;
begin
  Result := TTMSFNCDataGridDocURL;
end;

function TTMSFNCCustomDataGrid.GetFocusedCell: TTMSFNCDataGridCellCoord;
begin
  Result := Root.FocusedCell;
end;

function TTMSFNCCustomDataGrid.GetFontColors(AColumn,
  ARow: Integer): TTMSFNCGraphicsColor;
begin
  Result := Root.FontColors[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetFontNames(AColumn, ARow: Integer): string;
begin
  Result := Root.FontNames[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetFontSizes(AColumn, ARow: Integer): Single;
begin
  Result := Root.FontSizes[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetFontStyles(AColumn,
  ARow: Integer): TFontStyles;
begin
  Result := Root.FontStyles[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetFooterRect: TRectF;
begin
  Result := inherited GetCalculationRect;

  Result.Top := Result.Bottom - GetFooterSize - 1;
  Result.Left := Result.Left + GetDesignerLeftSize;
  Result.Bottom := Result.Bottom - GetDesignerBottomSize;
  Result.Right := Result.Right - GetDesignerRightSize;
end;

function TTMSFNCCustomDataGrid.GetFooterSize: Double;
begin
  Result := 0;
  if Footer.Visible then
    Result := Footer.Size;
end;

function TTMSFNCCustomDataGrid.GetVersion: string;
begin
  Result := GetVersionNumber(MAJ_VER, MIN_VER, REL_VER, BLD_VER);
end;

function TTMSFNCCustomDataGrid.GetVerticalTextAligns(AColumn,
  ARow: Integer): TTMSFNCGraphicsTextAlign;
begin
  Result := Root.VerticalTextAligns[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetVerticalScrollBar: TScrollBar;
begin
  Result := Root.VerticalScrollBar;
end;

function TTMSFNCCustomDataGrid.GetVerticalScrollBarVisible: Boolean;
begin
  Result := Root.VerticalScrollBarVisible;
end;

procedure TTMSFNCCustomDataGrid.LastPage;
begin
  Root.LastPage;
end;

procedure TTMSFNCCustomDataGrid.LinearFill(ADoFixed: Boolean);
begin
  Root.LinearFill(ADoFixed);
end;

procedure TTMSFNCCustomDataGrid.LoadFromCSVData(AFileName: string;
  AEncoding: TEncoding);
begin
  Root.LoadFromCSVData(AFileName, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.LoadFromCSVData(AFileName: string);
begin
  Root.LoadFromCSVData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.LoadFromCSVStreamData(AStream: TStream;
  AEncoding: TEncoding);
begin
  Root.LoadFromCSVStreamData(AStream, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.LoadFromCSVStreamData(AStream: TStream);
begin
  Root.LoadFromCSVStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.LoadFromFileData(AFileName: string;
  AEncoding: TEncoding);
begin
  Root.LoadFromFileData(AFileName, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.LoadFromFileData(AFileName: string);
begin
  Root.LoadFromFileData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.LoadFromFileStreamData(AStream: TStream;
  AEncoding: TEncoding);
begin
  Root.LoadFromFileStreamData(AStream, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.LoadFromJSONData(AFileName: string;
  AEncoding: TEncoding; ARowDataName, AColumnDataName: string;
  AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  Root.LoadFromJSONData(AFileName, AEncoding, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCCustomDataGrid.LoadFromJSONData(AFileName, ARowDataName,
  AColumnDataName: string; AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  Root.LoadFromJSONData(AFileName, TEncoding.Default, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCCustomDataGrid.LoadFromJSONStreamData(AStream: TStream;
  ARowDataName, AColumnDataName: string;
  AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  Root.LoadFromJSONStreamData(AStream, TEncoding.Default, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCCustomDataGrid.LoadFromJSONStreamData(AStream: TStream;
  AEncoding: TEncoding; ARowDataName, AColumnDataName: string;
  AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  Root.LoadFromJSONStreamData(AStream, AEncoding, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCCustomDataGrid.LoadFromJSONTextData(AText, ARowDataName,
  AColumnDataName: string; AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  Root.LoadFromJSONTextData(AText, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCCustomDataGrid.LoadFromFileStreamData(AStream: TStream);
begin
  Root.LoadFromFileStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.LoadFromXMLData(AFileName: string;
  AEncoding: TEncoding);
begin
  Root.LoadFromXMLData(AFileName, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.LoadFromXMLData(AFileName: string);
begin
  Root.LoadFromXMLData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.LoadFromXMLStreamData(AStream: TStream);
begin
  Root.LoadFromXMLStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.LoadFromXMLStreamData(AStream: TStream;
  AEncoding: TEncoding);
begin
  Root.LoadFromXMLStreamData(AStream, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.LoadSampleData;
begin
  Root.LoadSampleData;
end;

procedure TTMSFNCCustomDataGrid.LogMessage(AMessage: string;
  AMessageType: TTMSFNCDataGridLogMessageType);
begin
  Root.LogMessage(AMessage, AMessageType);
end;

procedure TTMSFNCCustomDataGrid.LoopThroughColumns(ALoopCallback: TTMSFNCDataGridLoopColumnsCallback; AIncludeFixed: Boolean = True);
begin
  Root.LoopThroughColumns(ALoopCallback, AIncludeFixed);
end;

procedure TTMSFNCCustomDataGrid.LoopThroughRows(ALoopCallback: TTMSFNCDataGridLoopRowsCallback; AIncludeFixed: Boolean = True);
begin
  Root.LoopThroughRows(ALoopCallback, AIncludeFixed);
end;

procedure TTMSFNCCustomDataGrid.MarkCells(AHilightText: string;
  AOptions: TTMSFNCDataGridMarkOptions);
begin
  Root.MarkCells(AHilightText, AOptions);
end;

function TTMSFNCCustomDataGrid.MatchFilter(ARow: Integer): Boolean;
begin
  Result := Root.MatchFilter(ARow);
end;

procedure TTMSFNCCustomDataGrid.MergeCells(AColumn, ARow, AColumnCount,
  ARowCount: Integer);
begin
  Root.MergeCells(AColumn, ARow, AColumnCount, ARowCount);
end;

procedure TTMSFNCCustomDataGrid.MergeColumn(AColumn: Integer);
begin
  Root.MergeColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.MergeColumns(AColumn, ACount: Integer);
begin
  Root.MergeColumns(AColumn, ACount);
end;

procedure TTMSFNCCustomDataGrid.MergeRange(
  ARange: TTMSFNCDataGridCellCoordRange);
begin
  Root.MergeRange(ARange);
end;

procedure TTMSFNCCustomDataGrid.MergeRow(ARow: Integer);
begin
  Root.MergeRow(ARow);
end;

procedure TTMSFNCCustomDataGrid.MergeRows(ARow, ACount: Integer);
begin
  Root.MergeRows(ARow, ACount);
end;

procedure TTMSFNCCustomDataGrid.MoveColumn(AFromColumn, AToColumn: Integer);
begin
  Root.MoveColumn(AFromColumn, AToColumn);
end;

procedure TTMSFNCCustomDataGrid.MoveRow(AFromRow, AToRow: Integer);
begin
  Root.MoveRow(AFromRow, AToRow);
end;

procedure TTMSFNCCustomDataGrid.InitializeHeaderGroup(AGraphics: TTMSFNCGraphics;
  AHeaderGroup: TTMSFNCDataGridHeaderGroup; AColumn: Integer; ALevel: Integer; AX, AY: Single);
var
  c: Integer;
  cl: TTMSFNCDataGridColumn;
  si: TTMSFNCDataGridSortIndex;
  n: string;
  r: TRectF;
  spc, w, h, iw: Single;
begin
  spc := 4;
  AHeaderGroup.Layout.Assign(Header.VisualGrouping.Layout);
  AHeaderGroup.FCloseIndicator := Header.VisualGrouping.CloseIndicator;
  AHeaderGroup.SortDescendingIcon := Icons.SortDescendingIcon;
  AHeaderGroup.SortAscendingIcon := Icons.SortAscendingIcon;
  AHeaderGroup.CloseIcon := Icons.CloseIcon;
  AHeaderGroup.FLevel := ALevel;

  c := AColumn;

  AHeaderGroup.FColumn := c;

  cl := TTMSFNCDataGridRendererOpen(Root).GetColumn(c);
  if Assigned(cl) then
    n := cl.Header
  else
    n := Strings[c, 0];

  AHeaderGroup.Text := n;

  iw := 0;
  if SortIndexList.Find(c, si) and Options.Sorting.Enabled then
  begin
    case si.Direction of
      gsdAscending: AHeaderGroup.FSortIndicator := gsiAscending;
      gsdDescending: AHeaderGroup.FSortIndicator := gsiDescending;
    end;

    if si.Direction <> gsdNone then
      iw := AHeaderGroup.GetSortIndicatorSize + 4 * AHeaderGroup.FScale;
  end;

  if AHeaderGroup.CloseIndicator then
    iw := iw + AHeaderGroup.GetCloseIndicatorSize + 4 * AHeaderGroup.FScale;

  DoCustomizeHeaderGroup(AHeaderGroup);

  AGraphics.Font.Assign(AHeaderGroup.Layout.Font);
  w := AGraphics.CalculateTextWidth(AHeaderGroup.Text) + 4 * AHeaderGroup.FScale;

  if Header.VisualGrouping.AutoSize then
    h := AGraphics.CalculateTextHeight(AHeaderGroup.Text) + 8 * AHeaderGroup.FScale
  else
    h := Header.VisualGrouping.Size;

  w := w + iw;
  r := RectF(AX + spc, AY + spc, AX + spc * 2 + 2 + w, AY + h + spc);
  AHeaderGroup.FRect := r;
end;

procedure TTMSFNCCustomDataGrid.InitSample;
var
  I: Integer;
begin
  BeginUpdate;
  try
    Clear;

    ResetToDefaultStyle;

    Root.DefaultRowHeight := ScalePaintValue(30);
    Root.FixedColumnCount := 0;

    Root.ColumnWidths[0] := ScalePaintValue(70);
    Root.ColumnWidths[1] := ScalePaintValue(250);
    Root.ColumnWidths[2] := ScalePaintValue(100);
    Root.ColumnWidths[3] := ScalePaintValue(100);

    Columns.Clear;
    for I := 0 to 3 do
      Columns.Add;

    LoadSampleData;

  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCCustomDataGrid.InsertColumn(AColumn: Integer);
begin
  Root.InsertColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.InsertColumns(AColumn, ACount: Integer);
begin
  Root.InsertColumns(AColumn, ACount);
end;

procedure TTMSFNCCustomDataGrid.InsertRow(ARow: Integer);
begin
  Root.InsertRow(ARow);
end;

procedure TTMSFNCCustomDataGrid.InsertRows(ARow, ACount: Integer);
begin
  Root.InsertRows(ARow, ACount);
end;

function TTMSFNCCustomDataGrid.InternalXYToHeaderGroup(X, Y: Double; AHeaderGroups: TTMSFNCDataGridHeaderGroups;
  ASkipDragHeaderGroup: Boolean; AUseOriginalRect: Boolean): TTMSFNCDataGridHeaderGroup;
var
  I: Integer;
  hg: TTMSFNCDataGridHeaderGroup;
  r: TRectF;
begin
  Result := nil;
  for I := 0 to AHeaderGroups.Count - 1 do
  begin
    hg := AHeaderGroups[I];
    if AUseOriginalRect then
      r := hg.FOrigRect
    else
      r := hg.Rect;

    if PtInRectEx(r, PointF(x, y)) and ((ASkipDragHeaderGroup and (hg <> FDragHeaderGroup)) or not ASkipDragHeaderGroup) then
    begin
      Result := hg;
      Break;
    end;
  end;
end;

function TTMSFNCCustomDataGrid.IsCellBase(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellBase(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellDisabled(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellDisabled(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellDisplayed(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellDisplayed(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellFixed(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellFixed(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellFixedRoot(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellFixedRoot(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellFreeze(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellFreeze(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellFreezeFixed(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellFreezeFixed(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellFreezeRoot(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellFreezeRoot(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellHidden(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellHidden(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellMerged(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellMerged(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellSelected(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellSelected(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellValid(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellValid(ACell);
end;

function TTMSFNCCustomDataGrid.IsCellValid(AColumn, ARow: Integer): Boolean;
begin
  Result := Root.IsCellValid(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.IsCellVisible(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsCellVisible(ACell);
end;

function TTMSFNCCustomDataGrid.IsColumnDisplayed(AColumn: Integer): Boolean;
begin
  Result := Root.IsColumnDisplayed(AColumn);
end;

function TTMSFNCCustomDataGrid.IsColumnHidden(AColumn: Integer): Boolean;
begin
  Result := Root.IsColumnHidden(AColumn);
end;

function TTMSFNCCustomDataGrid.IsColumnMerged(AColumn: Integer): Boolean;
begin
  Result := Root.IsColumnMerged(AColumn);
end;

function TTMSFNCCustomDataGrid.IsColumnValid(AColumn: Integer): Boolean;
begin
  Result := Root.IsColumnValid(AColumn);
end;

function TTMSFNCCustomDataGrid.IsColumnVisible(AColumn: Integer): Boolean;
begin
  Result := Root.IsColumnVisible(AColumn);
end;

function TTMSFNCCustomDataGrid.IsDefaultColumnWidthStored: Boolean;
begin
  Result := DefaultColumnWidth <> 75;
end;

function TTMSFNCCustomDataGrid.IsDefaultRowHeightStored: Boolean;
begin
  Result := DefaultRowHeight <> 30;
end;

function TTMSFNCCustomDataGrid.IsEditing: Boolean;
begin
  Result := Root.IsEditing;
end;

function TTMSFNCCustomDataGrid.IsFixedCellSelected(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := Root.IsFixedCellSelected(ACell);
end;

function TTMSFNCCustomDataGrid.IsGrouped: Boolean;
begin
  Result := Root.IsGrouped;
end;

function TTMSFNCCustomDataGrid.IsRowBand(ARow: Integer): Boolean;
begin
  Result := Root.IsRowBand(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowDisplayed(ARow: Integer): Boolean;
begin
  Result := Root.IsRowDisplayed(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowFiltered(ARow: Integer): Boolean;
begin
  Result := Root.IsRowFiltered(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowHidden(ARow: Integer): Boolean;
begin
  Result := Root.IsRowHidden(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowMerged(ARow: Integer): Boolean;
begin
  Result := Root.IsRowMerged(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowNode(ARow: Integer): Boolean;
begin
  Result := Root.IsRowNode(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowNormal(ARow: Integer): Boolean;
begin
  Result := Root.IsRowNormal(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowSummary(ARow: Integer): Boolean;
begin
  Result := Root.IsRowSummary(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowFilter(ARow: Integer): Boolean;
begin
  Result := Root.IsRowFilter(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowValid(ARow: Integer): Boolean;
begin
  Result := Root.IsRowValid(ARow);
end;

function TTMSFNCCustomDataGrid.IsRowVisible(ARow: Integer): Boolean;
begin
  Result := Root.IsRowVisible(ARow);
end;

procedure TTMSFNCCustomDataGrid.HandleKeyDown(var Key: Word; Shift: TShiftState);
var
  k: TTMSFNCDataGridKeyInfo;
begin
  inherited;
  k := DefaultKeyInfo;
  k.Key := Key;
  k.Shift := Shift;
  k.Kind := gkkDown;
  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.HandleKey(k)
  else
    Root.HandleKey(k);
  Key := k.Key;
end;

procedure TTMSFNCCustomDataGrid.HandleKeyPress(var Key: Char);
var
  k: TTMSFNCDataGridKeyInfo;
begin
  inherited;
  k := DefaultKeyInfo;
  k.Key := Ord(Key);
  k.KeyChar := Key;
  k.Kind := gkkPress;
  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.HandleKey(k)
  else
    Root.HandleKey(k);
  Key := k.KeyChar;
end;

procedure TTMSFNCCustomDataGrid.HandleKeyUp(var Key: Word; Shift: TShiftState);
var
  k: TTMSFNCDataGridKeyInfo;
begin
  inherited;
  k := DefaultKeyInfo;
  k.Key := Key;
  k.Shift := Shift;
  k.Kind := gkkUp;
  if Assigned(FFocusedRenderer) then
    FFocusedRenderer.HandleKey(k)
  else
    Root.HandleKey(k);
  Key := k.Key;
end;

procedure TTMSFNCCustomDataGrid.Draw(AGraphics: TTMSFNCGraphics; ARect: TRectF);
begin
  inherited;
  if (Root.UpdateCount > 0) then
    Exit;

  AGraphics.BitmapContainer := BitmapContainer;
  Root.Stroke.Assign(Stroke);
  Root.Draw(AGraphics);
  DrawFooter(AGraphics);
  DrawHeader(AGraphics);
end;

procedure TTMSFNCCustomDataGrid.SetFocusedCell(
  const Value: TTMSFNCDataGridCellCoord);
begin
  Root.FocusedCell := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFontColors(AColumn, ARow: Integer;
  const Value: TTMSFNCGraphicsColor);
begin
  Root.FontColors[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFontNames(AColumn, ARow: Integer;
  const Value: string);
begin
  Root.FontNames[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFonts(
  ASetType: TTMSFNCAppearanceGlobalFontType);
begin
  GlobalFont.ApplyChange(Header.VisualGrouping.Layout.Font, ASetType);
end;

procedure TTMSFNCCustomDataGrid.SetFontSizes(AColumn, ARow: Integer;
  const Value: Single);
begin
  Root.FontSizes[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFontStyles(AColumn, ARow: Integer;
  const Value: TFontStyles);
begin
  Root.FontStyles[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetLeftColumn(const Value: Integer);
begin
  Root.LeftColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFooter(const Value: TTMSFNCDataGridFooter);
begin
  FFooter.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetGlobalFont(const Value: TTMSFNCAppearanceGlobalFont);
begin
  Root.GlobalFont.Assign(Value);
end;

function TTMSFNCCustomDataGrid.SetGrid(AColumn, ARow: Integer): TTMSFNCDataGridRenderer;
begin
  Result := Root.SetGrid(AColumn, ARow);
end;

function TTMSFNCCustomDataGrid.SetGrid(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridRenderer;
begin
  Result := Root.SetGrid(ACell);
end;

procedure TTMSFNCCustomDataGrid.SetHeader(const Value: TTMSFNCDataGridHeader);
begin
  FHeader.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetHiddenColumns(AColumn: Integer;
  const Value: Boolean);
begin
  Root.HiddenColumns[AColumn] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetHiddenRows(ARow: Integer;
  const Value: Boolean);
begin
  Root.HiddenRows[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetTextAligns(AColumn, ARow: Integer;
  const Value: TTMSFNCGraphicsTextAlign);
begin
  Root.TextAligns[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetHorizontalScrollBarVisible(
  const Value: Boolean);
begin
  Root.HorizontalScrollBarVisible := Value;
end;

procedure TTMSFNCCustomDataGrid.SetInts(AColumn, ARow: Integer;
  const Value: Int64);
begin
  Root.Ints[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SaveToCSVData(AFileName: string);
begin
  Root.SaveToCSVData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.SaveToCSVData(AFileName: string;
  AEncoding: TEncoding);
begin
  Root.SaveToCSVData(AFileName, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.SaveToCSVStreamData(AStream: TStream);
begin
  Root.SaveToCSVStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.SaveToCSVStreamData(AStream: TStream;
  AEncoding: TEncoding);
begin
  Root.SaveToCSVStreamData(AStream, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.SaveToFileData(AFileName: string);
begin
  Root.SaveToFileData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.SaveToFileData(AFileName: string;
  AEncoding: TEncoding);
begin
  Root.SaveToFileData(AFileName, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.SaveToFileStreamData(AStream: TStream);
begin
  Root.SaveToFileStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.SaveToFileStreamData(AStream: TStream;
  AEncoding: TEncoding);
begin
  Root.SaveToFileStreamData(AStream, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.SaveToHTMLData(AFileName: string);
begin
  Root.SaveToHTMLData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.SaveToHTMLData(AFileName: string;
  AEncoding: TEncoding);
begin
  Root.SaveToHTMLData(AFileName, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.SaveToHTMLStreamData(AStream: TStream);
begin
  Root.SaveToHTMLStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.SaveToHTMLStreamData(AStream: TStream;
  AEncoding: TEncoding);
begin
  Root.SaveToHTMLStreamData(AStream, AEncoding);
end;

procedure TTMSFNCCustomDataGrid.SaveToImageData(AFileName: string);
begin
  Root.SaveToImageData(AFileName);
end;

procedure TTMSFNCCustomDataGrid.SaveToImageStreamData(AStream: TStream);
begin
  Root.SaveToImageStreamData(AStream);
end;

procedure TTMSFNCCustomDataGrid.Scroll(AHorizontalPos, AVerticalPos: Double);
begin
  Root.Scroll(AHorizontalPos, AVerticalPos);
end;

procedure TTMSFNCCustomDataGrid.SelectAllCells(AIncludeHidden: Boolean = True);
begin
  Root.SelectAllCells(AIncludeHidden);
end;

procedure TTMSFNCCustomDataGrid.SelectCell(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.SelectCell(ACell);
end;

procedure TTMSFNCCustomDataGrid.SetAdapter(const Value: TTMSFNCDataGridDataAdapter);
begin
  Root.Adapter := Value;
end;

procedure TTMSFNCCustomDataGrid.SetAdaptToStyle(const Value: Boolean);
begin
  inherited;
  Root.AdaptToStyle := Value;
  {$IFNDEF WEBLIB}
  if Assigned(FDesignerControl) then
    FDesignerControl.AdaptToStyle := Value;
  {$ENDIF}
end;

function TTMSFNCCustomDataGrid.StartDragOnHeader(X, Y: Single): Boolean;
begin
  Result := False;
  FDownX := X;
  FDownY := Y;
  FDownHeaderGroup := XYToHeaderGroup(X, Y);
  FDragHeaderGroup := nil;

  if Assigned(FDownHeaderGroup) then
  begin
    FDownHeaderGroup.FOrigRect := FDownHeaderGroup.FRect;
    Result := True;
  end;
end;

function TTMSFNCCustomDataGrid.StopEditing: Boolean;
begin
  Result := Root.StopEditing;
end;

procedure TTMSFNCCustomDataGrid.StretchColumns(AMode: TTMSFNCDataGridStretchingMode = gstmAll; ARange: TTMSFNCDataGridStretchingRange = gstrNormal; AIndex: Integer = -1; ANewWidth: Double = -1);
begin
  Root.StretchColumns(AMode, ARange, AIndex, ANewWidth);
end;

procedure TTMSFNCCustomDataGrid.StretchRows(AMode: TTMSFNCDataGridStretchingMode = gstmAll; ARange: TTMSFNCDataGridStretchingRange = gstrNormal; AIndex: Integer = -1; ANewHeight: Double = -1);
begin
  Root.StretchRows(AMode, ARange, AIndex, ANewHeight);
end;

procedure TTMSFNCCustomDataGrid.SwapCells(AFromCell,
  AToCell: TTMSFNCDataGridCellCoord);
begin
  Root.SwapCells(AFromCell, AToCell);
end;

procedure TTMSFNCCustomDataGrid.SwapColumns(AFromColumn, AToColumn: Integer);
begin
  Root.SwapColumn(AFromColumn, AToColumn);
end;

procedure TTMSFNCCustomDataGrid.SwapRows(AFromRow, AToRow: Integer);
begin
  Root.SwapRow(AFromRow, AToRow);
end;

procedure TTMSFNCCustomDataGrid.ToggleNode(ARow: Integer);
begin
  Root.ToggleNode(ARow);
end;

function TTMSFNCCustomDataGrid.TypeOfValue(
  AValue: TTMSFNCDataGridCellValue): TTypeKind;
begin
  Result := Root.TypeOfValue(AValue);
end;

procedure TTMSFNCCustomDataGrid.Ungroup;
begin
  Root.Ungroup;
end;

procedure TTMSFNCCustomDataGrid.UnhideAll;
begin
  Root.UnhideAll;
end;

procedure TTMSFNCCustomDataGrid.UnhideAllColumns;
begin
  Root.UnhideAllColumns;
end;

procedure TTMSFNCCustomDataGrid.UnhideAllRows;
begin
  Root.UnhideAllRows;
end;

procedure TTMSFNCCustomDataGrid.UnhideColumn(AColumn: Integer);
begin
  Root.UnhideColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.UnhideRow(ARow: Integer);
begin
  Root.UnhideRow(ARow);
end;

procedure TTMSFNCCustomDataGrid.UnhilightCells(
  AOptions: TTMSFNCDataGridHilightOptions);
begin
  Root.UnhilightCells(AOptions);
end;

procedure TTMSFNCCustomDataGrid.UnmarkCells(
  AOptions: TTMSFNCDataGridMarkOptions);
begin
  Root.UnmarkCells(AOptions);
end;

procedure TTMSFNCCustomDataGrid.UpdateColumnCalculation(AColumn: Integer; AName: string);
begin
  Root.UpdateColumnCalculation(AColumn, AName);
end;

procedure TTMSFNCCustomDataGrid.UpdateColumnCalculations;
begin
  Root.UpdateColumnCalculations;
end;

procedure TTMSFNCCustomDataGrid.UpdateRowCalculation(ARow: Integer; AName: string);
begin
  Root.UpdateRowCalculation(ARow, AName);
end;

procedure TTMSFNCCustomDataGrid.UpdateRowCalculations;
begin
  Root.UpdateRowCalculations;
end;

procedure TTMSFNCCustomDataGrid.UpdateCalculations;
begin
  Root.UpdateCalculations;
end;

function TTMSFNCCustomDataGrid.ValueToBoolean(AValue: TTMSFNCDataGridCellValue;
  AFormatSettings: TFormatSettings): Boolean;
begin
  Result := Root.ValueToBoolean(AValue, AFormatSettings);
end;

function TTMSFNCCustomDataGrid.ValueToFloat(AValue: TTMSFNCDataGridCellValue;
  AFormatSettings: TFormatSettings): Double;
begin
  Result := Root.ValueToFloat(AValue, AFormatSettings);
end;

function TTMSFNCCustomDataGrid.ValueToInt64(AValue: TTMSFNCDataGridCellValue;
  AFormatSettings: TFormatSettings): Int64;
begin
  Result := Root.ValueToInt64(AValue, AFormatSettings);
end;

function TTMSFNCCustomDataGrid.ValueToInteger(AValue: TTMSFNCDataGridCellValue;
  AFormatSettings: TFormatSettings): Integer;
begin
  Result := Root.ValueToInteger(AValue, AFormatSettings);
end;

function TTMSFNCCustomDataGrid.ValueToString(AValue: TTMSFNCDataGridCellValue;
  AFormatSettings: TFormatSettings): string;
begin
  Result := Root.ValueToString(AValue, AFormatSettings);
end;

{ TTMSFNCDataGrid }

function TTMSFNCCustomDataGrid.GetBooleans(AColumn, ARow: Integer): Boolean;
begin
  Result := Root.Booleans[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetBottomRow: Integer;
begin
  Result := Root.BottomRow;
end;

function TTMSFNCCustomDataGrid.GetCalculateGraphics: TTMSFNCGraphics;
begin
  if not Assigned(FCalculateGraphics) then
  begin
    FCalculateGraphics := TTMSFNCGraphics.CreateBitmapCanvas;
    FCalculateGraphics.BeginScene;
  end;

  Result := FCalculateGraphics;
end;

function TTMSFNCCustomDataGrid.GetCalculationRect: TRectF;
begin
  Result := inherited GetCalculationRect;

  Result.Top := Result.Top + GetHeaderSize;
  Result.Top := Result.Top + GetDesignerTopSize;
  Result.Left := Result.Left + GetDesignerLeftSize;
  Result.Bottom := Result.Bottom - GetFooterSize;
  Result.Bottom := Result.Bottom - GetDesignerBottomSize;
  Result.Right := Result.Right - GetDesignerRightSize;

  Result.Right := Result.Left + Max(0, Result.Right - Result.Left);
  Result.Bottom := Result.Top + Max(0, Result.Bottom - Result.Top);
end;

function TTMSFNCCustomDataGrid.GetCellAppearance: TTMSFNCDataGridCellAppearance;
begin
  Result := Root.CellAppearance;
end;

function TTMSFNCCustomDataGrid.GetIcons: TTMSFNCDataGridIcons;
begin
  Result := Root.Icons;
end;

function TTMSFNCCustomDataGrid.GetCellData(AColumn,
  ARow: Integer): TTMSFNCDataGridCellItem;
begin
  Result := Root.CellData[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetCellKind(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellKind;
begin
  Result := Root.GetCellKind(ACell);
end;

function TTMSFNCCustomDataGrid.GetCellMergeType(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridDataCellMergeType;
begin
  Result := Root.GetCellMergeType(ACell);
end;

function TTMSFNCCustomDataGrid.GetCellObjectForCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCell;
begin
  Result := Root.GetCellObjectForCell(ACell);
end;

function TTMSFNCCustomDataGrid.GetCellPopupMenu: TTMSFNCDataGridPopupMenu;
begin
  Result := Root.CellPopupMenu;
end;

function TTMSFNCCustomDataGrid.GetCells(AColumn,
  ARow: Integer): TTMSFNCDataGridCellValue;
begin
  Result := Root.Cells[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetCellSize(
  ACell: TTMSFNCDataGridCellCoord): TSizeF;
begin
  Result := Root.GetCellSize(ACell);
end;

function TTMSFNCCustomDataGrid.GetColumnViewMode: TTMSFNCDataGridColumnViewMode;
begin
  Result := Root.ColumnViewMode;
end;

function TTMSFNCCustomDataGrid.GetRowViewMode: TTMSFNCDataGridRowViewMode;
begin
  Result := Root.RowViewMode;
end;

function TTMSFNCCustomDataGrid.GetColors(AColumn,
  ARow: Integer): TTMSFNCGraphicsColor;
begin
  Result := Root.Colors[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetColumnCalculation(
  AColumn: Integer; AName: string): TTMSFNCDataGridDataColumnCalculationArray;
begin
  Result := Root.ColumnCalculations[AColumn, AName];
end;

function TTMSFNCCustomDataGrid.GetRowCalculation(
  ARow: Integer; AName: string): TTMSFNCDataGridDataRowCalculationArray;
begin
  Result := Root.RowCalculations[ARow, AName];
end;

function TTMSFNCCustomDataGrid.GetColumnCount: Integer;
begin
  Result := Root.ColumnCount;
end;

function TTMSFNCCustomDataGrid.GetColumnOrder(AColumn: Integer): Integer;
begin
  Result := Root.ColumnOrders[AColumn];
end;

function TTMSFNCCustomDataGrid.GetColumnPos(AColumn: Integer): Double;
begin
  Result := Root.ColumnPositions[AColumn];
end;

function TTMSFNCCustomDataGrid.GetColumnReverseOrder(AColumn: Integer): Integer;
begin
  Result := Root.ColumnReverseOrders[AColumn];
end;

function TTMSFNCCustomDataGrid.GetColumns: TTMSFNCDataGridColumns;
begin
  Result := Root.Columns;
end;

function TTMSFNCCustomDataGrid.GetColumnsEditor: TTMSFNCDataGridColumnsEditor;
var
  o: TObject;
begin
  Result := nil;
  o := Root.ColumnsEditor;
  if Assigned(o) and (o is TTMSFNCDataGridColumnsEditor) then
    Result := o as TTMSFNCDataGridColumnsEditor;
end;

function TTMSFNCCustomDataGrid.GetColumnWidths(AColumn: Integer): Double;
begin
  Result := Root.ColumnWidths[AColumn];
end;

function TTMSFNCCustomDataGrid.GetComments(AColumn, ARow: Integer): string;
begin
  Result := Root.Comments[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetHints(AColumn, ARow: Integer): string;
begin
  Result := Root.Hints[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControlAligns(AColumn,
  ARow: Integer): TTMSFNCDataGridCellControlAlign;
begin
  Result := Root.ControlAligns[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControlPositions(AColumn,
  ARow: Integer): TTMSFNCDataGridCellControlPosition;
begin
  Result := Root.ControlPositions[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControlHeights(AColumn,
  ARow: Integer): Integer;
begin
  Result := Root.ControlHeights[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControlMargins(AColumn,
  ARow: Integer): TTMSFNCMargins;
begin
  Result := Root.ControlMargins[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControls(AColumn,
  ARow: Integer): TTMSFNCDataGridCellControl;
begin
  Result := Root.Controls[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControlTexts(AColumn, ARow: Integer): string;
begin
  Result := Root.ControlTexts[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetControlWidths(AColumn,
  ARow: Integer): Integer;
begin
  Result := Root.ControlWidths[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetCustomHorizontalScrollBar: TTMSFNCScrollBar;
begin
  Result := Root.CustomHorizontalScrollBar;
end;

function TTMSFNCCustomDataGrid.GetCustomScrollBars: Boolean;
begin
  Result := Root.CustomScrollBars;
end;

function TTMSFNCCustomDataGrid.GetCustomVerticalScrollBar: TTMSFNCScrollBar;
begin
  Result := Root.CustomVerticalScrollBar;
end;

function TTMSFNCCustomDataGrid.GetDataRowCount: Integer;
begin
  Result := TTMSFNCDataGridRendererOpen(Root).GetDataRowCount;
end;

function TTMSFNCCustomDataGrid.GetDefaultColumnWidth: Double;
begin
  Result := Root.DefaultColumnWidth;
end;

function TTMSFNCCustomDataGrid.GetDefaultRowHeight: Double;
begin
  Result := Root.DefaultRowHeight;
end;

function TTMSFNCCustomDataGrid.GetDesignerTopSize: Double;
begin
  Result := 0;
  {$IFNDEF WEBLIB}
  if Assigned(FDesignerControl) and Assigned(FDesignerControl.Parent) and (FDesignerControl.Visible) then
  begin
    case FDesignerControl.ControlAlignment of
      caTop: Result := FDesignerControl.Height - 1;
    end;
  end;
  {$ENDIF}
end;

function TTMSFNCCustomDataGrid.GetDesignerBottomSize: Double;
begin
  Result := 0;
  {$IFNDEF WEBLIB}
  if Assigned(FDesignerControl) and Assigned(FDesignerControl.Parent) and (FDesignerControl.Visible) then
  begin
    case FDesignerControl.ControlAlignment of
      caBottom: Result := FDesignerControl.Height - 1;
    end;
  end;
  {$ENDIF}
end;

function TTMSFNCCustomDataGrid.GetDesignerRightSize: Double;
begin
  Result := 0;
  {$IFNDEF WEBLIB}
  if Assigned(FDesignerControl) and Assigned(FDesignerControl.Parent) and (FDesignerControl.Visible) then
  begin
    case FDesignerControl.ControlAlignment of
      caRight: Result := FDesignerControl.Width - 1;
    end;
  end;
  {$ENDIF}
end;

function TTMSFNCCustomDataGrid.GetDesignerLeftSize: Double;
begin
  Result := 0;
  {$IFNDEF WEBLIB}
  if Assigned(FDesignerControl) and Assigned(FDesignerControl.Parent) and (FDesignerControl.Visible) then
  begin
    case FDesignerControl.ControlAlignment of
      caLeft: Result := FDesignerControl.Width - 1;
    end;
  end;
  {$ENDIF}
end;

function TTMSFNCCustomDataGrid.GetFilter: TTMSFNCDataGridDataFilter;
begin
  Result := Root.Filter;
end;

function TTMSFNCCustomDataGrid.GetFilterActions: TTMSFNCDataGridDataFilterActions;
begin
  Result := Root.FilterActions;
end;

function TTMSFNCCustomDataGrid.GetFilterAppearance: TTMSFNCDataGridFilterAppearance;
begin
  Result := Root.FilterAppearance;
end;

function TTMSFNCCustomDataGrid.GetFilterBuilder: TTMSFNCFilterBuilder;
begin
  Result := Root.FilterBuilder;
end;

function TTMSFNCCustomDataGrid.GetFilterEditors(AColumn,
  ARow: Integer): TTMSFNCDataGridCellControl;
begin
  Result := Root.FilterEditors[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetFilteredRowCount: Integer;
begin
  Result := Root.FilteredRowCount;
end;

function TTMSFNCCustomDataGrid.GetFilterTypeName(
  AType: TTMSFNCDataGridDataFilterType): string;
begin
  Result := Root.GetFilterTypeName(AType);
end;

function TTMSFNCCustomDataGrid.GetDragAppearance: TTMSFNCDataGridDragAppearance;
begin
  Result := Root.DragAppearance;
end;

function TTMSFNCCustomDataGrid.GetDragCell: TTMSFNCDataGridCellCoord;
begin
  Result := Root.DragCell;
end;

function TTMSFNCCustomDataGrid.GetEditingCell: TTMSFNCDataGridCellCoord;
begin
  Result := Root.EditingCell;
end;

function TTMSFNCCustomDataGrid.GetFixedBottomRowCount: Integer;
begin
  Result := Root.FixedBottomRowCount;
end;

function TTMSFNCCustomDataGrid.GetFixedColumnCount: Integer;
begin
  Result := Root.FixedColumnCount;
end;

function TTMSFNCCustomDataGrid.GetFixedRightColumnCount: Integer;
begin
  Result := Root.FixedRightColumnCount;
end;

function TTMSFNCCustomDataGrid.GetFixedRowCount: Integer;
begin
  Result := Root.FixedRowCount;
end;

function TTMSFNCCustomDataGrid.GetFloats(AColumn, ARow: Integer): Double;
begin
  Result := Root.Floats[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetFreezeColumnCount: Integer;
begin
  Result := Root.FreezeColumnCount;
end;

function TTMSFNCCustomDataGrid.GetFreezeRowCount: Integer;
begin
  Result := Root.FreezeRowCount;
end;

function TTMSFNCCustomDataGrid.GetGlobalFont: TTMSFNCAppearanceGlobalFont;
begin
  Result := Root.GlobalFont;
end;

function TTMSFNCCustomDataGrid.GetGrids(AColumn,
  ARow: Integer): TTMSFNCDataGridRenderer;
begin
  Result := Root.Grids[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetGroupInfo: TTMSFNCDataGridDataGroupInfo;
begin
  Result := Root.GroupInfo;
end;

function TTMSFNCCustomDataGrid.GetHasFilter: Boolean;
begin
  Result := Root.HasFilter;
end;

function TTMSFNCCustomDataGrid.GetHasFilterEditor(AColumn,
  ARow: Integer): Boolean;
begin
  Result := Root.HasFilterEditor[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetHasLayout(AColumn, ARow: Integer): Boolean;
begin
  Result := Root.HasLayout[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetHasRowOrder(ARow: Integer): Boolean;
begin
  Result := Root.HasRowOrder[ARow];
end;

function TTMSFNCCustomDataGrid.GetHiddenColumnCount: Integer;
begin
  Result := Root.HiddenColumnCount;
end;

function TTMSFNCCustomDataGrid.GetHiddenColumns(AColumn: Integer): Boolean;
begin
  Result := Root.HiddenColumns[AColumn];
end;

function TTMSFNCCustomDataGrid.GetHiddenColumnWidths(AColumn: Integer): Double;
begin
  Result := Root.HiddenColumnWidths[AColumn];
end;

function TTMSFNCCustomDataGrid.GetHiddenRowCount: Integer;
begin
  Result := Root.HiddenRowCount;
end;

function TTMSFNCCustomDataGrid.GetHiddenRowHeights(ARow: Integer): Double;
begin
  Result := Root.HiddenRowHeights[ARow];
end;

function TTMSFNCCustomDataGrid.GetHiddenRows(ARow: Integer): Boolean;
begin
  Result := Root.HiddenRows[ARow];
end;

function TTMSFNCCustomDataGrid.GetInts(AColumn, ARow: Integer): Int64;
begin
  Result := Root.Ints[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetLayouts(AColumn,
  ARow: Integer): TTMSFNCDataGridCellLayout;
begin
  Result := Root.Layouts[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetLeftColumn: Integer;
begin
  Result := Root.LeftColumn;
end;

function TTMSFNCCustomDataGrid.GetLink: TTMSFNCDataGridRenderer;
begin
  Result := FFocusedRenderer;
end;

function TTMSFNCCustomDataGrid.GetManagedObjects(AColumn, ARow: Integer): TObject;
begin
  Result := Root.ManagedObjects[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetMaxLevel: Integer;
begin
  Result := Root.GetMaxLevel;
end;

function TTMSFNCCustomDataGrid.GetMergedColumns(AColumn: Integer): Boolean;
begin
  Result := Root.MergedColumns[AColumn];
end;

function TTMSFNCCustomDataGrid.GetMergedRows(ARow: Integer): Boolean;
begin
  Result := Root.MergedRows[ARow];
end;

function TTMSFNCCustomDataGrid.GetObjects(AColumn, ARow: Integer): TObject;
begin
  Result := Root.Objects[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetOnAfterApplyFilter: TTMSFNCDataGridAfterApplyFilterEvent;
begin
  Result := Root.OnAfterApplyFilter;
end;

function TTMSFNCCustomDataGrid.GetOnAfterCloseInplaceEditor: TTMSFNCDataGridAfterCloseInplaceEditorEvent;
begin
  Result := Root.OnAfterCloseInplaceEditor;
end;

function TTMSFNCCustomDataGrid.GetOnAfterCopyToClipboard: TTMSFNCDataGridAfterClipboardEvent;
begin
  Result := Root.OnAfterCopyToClipboard;
end;

function TTMSFNCCustomDataGrid.GetOnAfterCreateFilterEditor: TTMSFNCDataGridAfterCreateFilterEditorEvent;
begin
  Result := Root.OnAfterCreateFilterEditor;
end;

function TTMSFNCCustomDataGrid.GetOnAfterCutToClipboard: TTMSFNCDataGridAfterClipboardEvent;
begin
  Result := Root.OnAfterCutToClipboard;
end;

function TTMSFNCCustomDataGrid.GetOnAfterDrawCell: TTMSFNCDataGridAfterDrawCellEvent;
begin
  Result := Root.OnAfterDrawCell;
end;

function TTMSFNCCustomDataGrid.GetOnAfterDrawEmptySpace: TTMSFNCDataGridAfterDrawEmptySpaceEvent;
begin
  Result := Root.OnAfterDrawEmptySpace;
end;

function TTMSFNCCustomDataGrid.GetOnAfterDrawGrid: TTMSFNCDataGridAfterDrawGridEvent;
begin
  Result := Root.OnAfterDrawGrid;
end;

function TTMSFNCCustomDataGrid.GetOnAfterLookup: TTMSFNCDataGridAfterLookupEvent;
begin
  Result := Root.OnAfterLookup;
end;

function TTMSFNCCustomDataGrid.GetOnAfterOpenInplaceEditor: TTMSFNCDataGridAfterOpenInplaceEditorEvent;
begin
  Result := Root.OnAfterOpenInplaceEditor;
end;

function TTMSFNCCustomDataGrid.GetOnAfterPasteFromClipboard: TTMSFNCDataGridAfterClipboardEvent;
begin
  Result := Root.OnAfterPasteFromClipboard;
end;

function TTMSFNCCustomDataGrid.GetOnAfterSetColumnCalculation: TTMSFNCDataGridDataAfterSetColumnCalculation;
begin
  Result := Root.OnAfterSetColumnCalculation;
end;

function TTMSFNCCustomDataGrid.GetOnAfterSetRowCalculation: TTMSFNCDataGridDataAfterSetRowCalculation;
begin
  Result := Root.OnAfterSetRowCalculation;
end;

function TTMSFNCCustomDataGrid.GetOnAfterShowFilter: TTMSFNCDataGridAfterShowFilterEvent;
begin
  Result := Root.OnAfterShowFilter;
end;

function TTMSFNCCustomDataGrid.GetOnAfterShowFilterDialog: TTMSFNCDataGridAfterShowFilterDialogEvent;
begin
  Result := Root.OnAfterShowFilterDialog;
end;

function TTMSFNCCustomDataGrid.GetOnAfterShowFindDialog: TTMSFNCDataGridAfterShowFindDialogEvent;
begin
  Result := Root.OnAfterShowFindDialog;
end;

function TTMSFNCCustomDataGrid.GetOnAfterShowReplaceDialog: TTMSFNCDataGridAfterShowReplaceDialogEvent;
begin
  Result := Root.OnAfterShowReplaceDialog;
end;

function TTMSFNCCustomDataGrid.GetOnAfterSortColumn: TTMSFNCDataGridAfterSortColumnEvent;
begin
  Result := Root.OnAfterSortColumn;
end;

function TTMSFNCCustomDataGrid.GetOnAppendColumn: TTMSFNCDataGridAppendColumnEvent;
begin
  Result := Root.OnAppendColumn;
end;

function TTMSFNCCustomDataGrid.GetOnAppendRow: TTMSFNCDataGridAppendRowEvent;
begin
  Result := Root.OnAppendRow;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeDrawCell: TTMSFNCDataGridBeforeDrawCellEvent;
begin
  Result := Root.OnBeforeDrawCell;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeDrawEmptySpace: TTMSFNCDataGridBeforeDrawEmptySpaceEvent;
begin
  Result := Root.OnBeforeDrawEmptySpace;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeLookup: TTMSFNCDataGridBeforeLookupEvent;
begin
  Result := Root.OnBeforeLookup;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeOpenInplaceEditor: TTMSFNCDataGridBeforeOpenInplaceEditorEvent;
begin
  Result := Root.OnBeforeOpenInplaceEditor;
end;

function TTMSFNCCustomDataGrid.GetOnBeforePasteFromClipboard: TTMSFNCDataGridBeforeClipboardEvent;
begin
  Result := Root.OnBeforePasteFromClipboard;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeSetColumnCalculation: TTMSFNCDataGridDataBeforeSetColumnCalculation;
begin
  Result := Root.OnBeforeSetColumnCalculation;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeSetRowCalculation: TTMSFNCDataGridDataBeforeSetRowCalculation;
begin
  Result := Root.OnBeforeSetRowCalculation;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeShowFilter: TTMSFNCDataGridBeforeShowFilterEvent;
begin
  Result := Root.OnBeforeShowFilter;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeShowFilterDialog: TTMSFNCDataGridBeforeShowFilterDialogEvent;
begin
  Result := Root.OnBeforeShowFilterDialog;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeShowFindDialog: TTMSFNCDataGridBeforeShowFindDialogEvent;
begin
  Result := Root.OnBeforeShowFindDialog;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeShowReplaceDialog: TTMSFNCDataGridBeforeShowReplaceDialogEvent;
begin
  Result := Root.OnBeforeShowReplaceDialog;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeSortColumn: TTMSFNCDataGridBeforeSortColumnEvent;
begin
  Result := Root.OnBeforeSortColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanAppendColumn: TTMSFNCDataGridCanAppendColumnEvent;
begin
  Result := Root.OnCanAppendColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanAppendRow: TTMSFNCDataGridCanAppendRowEvent;
begin
  Result := Root.OnCanAppendRow;
end;

function TTMSFNCCustomDataGrid.GetOnCanDeleteColumn: TTMSFNCDataGridCanDeleteColumnEvent;
begin
  Result := Root.OnCanDeleteColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanDeleteRow: TTMSFNCDataGridCanDeleteRowEvent;
begin
  Result := Root.OnCanDeleteRow;
end;

function TTMSFNCCustomDataGrid.GetOnCanDragColumn: TTMSFNCDataGridCanDragColumnEvent;
begin
  Result := Root.OnCanDragColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanDragRow: TTMSFNCDataGridCanDragRowEvent;
begin
  Result := Root.OnCanDragRow;
end;

function TTMSFNCCustomDataGrid.GetOnCanDropColumn: TTMSFNCDataGridCanDropColumnEvent;
begin
  Result := Root.OnCanDropColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanDropRow: TTMSFNCDataGridCanDropRowEvent;
begin
  Result := Root.OnCanDropRow;
end;

function TTMSFNCCustomDataGrid.GetOnCanEditCell: TTMSFNCDataGridCanEditCellEvent;
begin
  Result := Root.OnCanEditCell;
end;

function TTMSFNCCustomDataGrid.GetOnCanInsertColumn: TTMSFNCDataGridCanInsertColumnEvent;
begin
  Result := Root.OnCanInsertColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanInsertRow: TTMSFNCDataGridCanInsertRowEvent;
begin
  Result := Root.OnCanInsertRow;
end;

function TTMSFNCCustomDataGrid.GetOnCanSizeColumn: TTMSFNCDataGridCanSizeColumnEvent;
begin
  Result := Root.OnCanSizeColumn;
end;

function TTMSFNCCustomDataGrid.GetOnCanSizeRow: TTMSFNCDataGridCanSizeRowEvent;
begin
  Result := Root.OnCanSizeRow;
end;

function TTMSFNCCustomDataGrid.GetOnCellAnchorClick: TTMSFNCDataGridCellAnchorClickEvent;
begin
  Result := Root.OnCellAnchorClick;
end;

function TTMSFNCCustomDataGrid.GetOnCellButtonClick: TTMSFNCDataGridCellClickEvent;
begin
  Result := Root.OnCellButtonClick;
end;

function TTMSFNCCustomDataGrid.GetOnCellCheckBoxChange: TTMSFNCDataGridCellClickEvent;
begin
  Result := Root.OnCellCheckBoxChange;
end;

function TTMSFNCCustomDataGrid.GetOnCellClick: TTMSFNCDataGridCellClickEvent;
begin
  Result := Root.OnCellClick;
end;

function TTMSFNCCustomDataGrid.GetOnCellDblClick: TTMSFNCDataGridCellClickEvent;
begin
  Result := Root.OnCellDblClick;
end;

function TTMSFNCCustomDataGrid.GetOnCellEditGetData: TTMSFNCDataGridCellEditGetDataEvent;
begin
  Result := Root.OnCellEditGetData;
end;

function TTMSFNCCustomDataGrid.GetOnCellEditSetData: TTMSFNCDataGridCellEditSetDataEvent;
begin
  Result := Root.OnCellEditSetData;
end;

function TTMSFNCCustomDataGrid.GetOnCellEditValidateData: TTMSFNCDataGridCellEditValidateDataEvent;
begin
  Result := Root.OnCellEditValidateData;
end;

function TTMSFNCCustomDataGrid.GetOnCellBeforeShowPopupMenu: TTMSFNCDataGridCellBeforeShowPopupMenuEvent;
begin
  Result := Root.OnCellBeforeShowPopupMenu;
end;

function TTMSFNCCustomDataGrid.GetOnCellBeforeNodeExpand: TTMSFNCDataGridCellBeforeNodeExpandEvent;
begin
  Result := Root.OnCellBeforeNodeExpand;
end;

function TTMSFNCCustomDataGrid.GetOnCellBeforeNodeCollapse: TTMSFNCDataGridCellBeforeNodeCollapseEvent;
begin
  Result := Root.OnCellBeforeNodeCollapse;
end;

function TTMSFNCCustomDataGrid.GetOnCellAfterNodeExpand: TTMSFNCDataGridCellAfterNodeExpandEvent;
begin
  Result := Root.OnCellAfterNodeExpand;
end;

function TTMSFNCCustomDataGrid.GetOnCellAfterNodeCollapse: TTMSFNCDataGridCellAfterNodeCollapseEvent;
begin
  Result := Root.OnCellAfterNodeCollapse;
end;

function TTMSFNCCustomDataGrid.GetOnCellRadioButtonChange: TTMSFNCDataGridCellClickEvent;
begin
  Result := Root.OnCellRadioButtonChange;
end;

function TTMSFNCCustomDataGrid.GetOnCellRightClick: TTMSFNCDataGridCellClickEvent;
begin
  Result := Root.OnCellRightClick;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeApplyFilter: TTMSFNCDataGridBeforeApplyFilterEvent;
begin
  Result := Root.OnBeforeApplyFilter;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeCloseInplaceEditor: TTMSFNCDataGridBeforeCloseInplaceEditorEvent;
begin
  Result := Root.OnBeforeCloseInplaceEditor;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeCopyToClipboard: TTMSFNCDataGridBeforeClipboardEvent;
begin
  Result := Root.OnBeforeCopyToClipboard;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeCreateFilterEditor: TTMSFNCDataGridBeforeCreateFilterEditorEvent;
begin
  Result := Root.OnBeforeCreateFilterEditor;
end;

function TTMSFNCCustomDataGrid.GetOnBeforeCutToClipboard: TTMSFNCDataGridBeforeClipboardEvent;
begin
  Result := Root.OnBeforeCutToClipboard;
end;

function TTMSFNCCustomDataGrid.GetOnCustomColumnCalculation: TTMSFNCDataGridDataCustomColumnCalculationEvent;
begin
  Result := Root.OnCustomColumnCalculation;
end;

function TTMSFNCCustomDataGrid.GetOnCustomRowCalculation: TTMSFNCDataGridDataCustomRowCalculationEvent;
begin
  Result := Root.OnCustomRowCalculation;
end;

function TTMSFNCCustomDataGrid.GetOnColumnDropped: TTMSFNCDataGridColumnDroppedEvent;
begin
  Result := Root.OnColumnDropped;
end;

function TTMSFNCCustomDataGrid.GetOnColumnSized: TTMSFNCDataGridColumnSizedEvent;
begin
  Result := Root.OnColumnSized;
end;

function TTMSFNCCustomDataGrid.GetOnCreateCustomFilterEditor: TTMSFNCDataGridCreateCustomFilterEditorEvent;
begin
  Result := Root.OnCreateCustomFilterEditor;
end;

function TTMSFNCCustomDataGrid.GetOnCustomCompare: TTMSFNCDataGridDataCustomCompareEvent;
begin
  Result := Root.OnCustomCompare;
end;

function TTMSFNCCustomDataGrid.GetOnCustomizeFilterPopupEditor: TTMSFNCDataGridCustomizeFilterPopupEditorEvent;
begin
  Result := Root.OnCustomizeFilterPopupEditor;
end;

function TTMSFNCCustomDataGrid.GetOnDeleteColumn: TTMSFNCDataGridDeleteColumnEvent;
begin
  Result := Root.OnDeleteColumn;
end;

function TTMSFNCCustomDataGrid.GetOnDeleteRow: TTMSFNCDataGridDeleteRowEvent;
begin
  Result := Root.OnDeleteRow;
end;

function TTMSFNCCustomDataGrid.GetOnDropFiles: TTMSFNCDataGridDropFilesEvent;
begin
  Result := Root.OnDropFiles;
end;

function TTMSFNCCustomDataGrid.GetOnGetColumnCalculationOptions: TTMSFNCDataGridDataGetColumnCalculationOptionsEvent;
begin
  Result := Root.OnGetColumnCalculationOptions;
end;

function TTMSFNCCustomDataGrid.GetOnGetRowCalculationOptions: TTMSFNCDataGridDataGetRowCalculationOptionsEvent;
begin
  Result := Root.OnGetRowCalculationOptions;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellClass: TTMSFNCDataGridGetCellClassEvent;
begin
  Result := Root.OnGetCellClass;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellData: TTMSFNCDataGridDataGetCellDataEvent;
begin
  Result := Root.OnGetCellData;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellDataProperties: TTMSFNCDataGridDataGetCellDataPropertiesEvent;
begin
  Result := Root.OnGetCellDataProperties;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellDisplayValue: TTMSFNCDataGridDataGetCellDisplayValueEvent;
begin
  Result := Root.OnGetCellDisplayValue;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellFormatting: TTMSFNCDataGridDataGetCellFormattingEvent;
begin
  Result := Root.OnGetCellFormatting;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellHint: TTMSFNCDataGridGetCellHintEvent;
begin
  Result := Root.OnGetCellHint;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellLayout: TTMSFNCDataGridGetCellLayoutEvent;
begin
  Result := Root.OnGetCellLayout;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellLayoutHasPriority: TTMSFNCDataGridGetCellLayoutHasPriorityEvent;
begin
  Result := Root.OnGetCellLayoutHasPriority;
end;

function TTMSFNCCustomDataGrid.GetOnGetCellProperties: TTMSFNCDataGridGetCellPropertiesEvent;
begin
  Result := Root.OnGetCellProperties;
end;

function TTMSFNCCustomDataGrid.GetOnGetCustomGroup: TTMSFNCDataGridDataGetCustomGroupEvent;
begin
  Result := Root.OnGetCustomGroup;
end;

function TTMSFNCCustomDataGrid.GetOnGetGroupOptions: TTMSFNCDataGridDataGetGroupOptionsEvent;
begin
  Result := Root.OnGetGroupOptions;
end;

function TTMSFNCCustomDataGrid.GetOnGetInplaceEditorClass: TTMSFNCDataGridGetInplaceEditorClassEvent;
begin
  Result := Root.OnGetInplaceEditorClass;
end;

function TTMSFNCCustomDataGrid.GetOnGetInplaceEditorProperties: TTMSFNCDataGridGetInplaceEditorPropertiesEvent;
begin
  Result := Root.OnGetInplaceEditorProperties;
end;

function TTMSFNCCustomDataGrid.GetOnGetInplaceEditorRect: TTMSFNCDataGridGetInplaceEditorRectEvent;
begin
  Result := Root.OnGetInplaceEditorRect;
end;

function TTMSFNCCustomDataGrid.GetOnGetInplaceEditorType: TTMSFNCDataGridGetInplaceEditorTypeEvent;
begin
  Result := Root.OnGetInplaceEditorType;
end;

function TTMSFNCCustomDataGrid.GetOnGetInplaceEditorTarget: TTMSFNCDataGridGetInplaceEditorTargetEvent;
begin
  Result := Root.OnGetInplaceEditorTarget;
end;

function TTMSFNCCustomDataGrid.GetOnGetSortOptions: TTMSFNCDataGridDataGetSortOptionsEvent;
begin
  Result := Root.OnGetSortOptions;
end;

function TTMSFNCCustomDataGrid.GetOnHScroll: TTMSFNCDataGridScrollEvent;
begin
  Result := Root.OnHScroll;
end;

function TTMSFNCCustomDataGrid.GetOnInsertColumn: TTMSFNCDataGridInsertColumnEvent;
begin
  Result := Root.OnInsertColumn;
end;

function TTMSFNCCustomDataGrid.GetOnInsertRow: TTMSFNCDataGridInsertRowEvent;
begin
  Result := Root.OnInsertRow;
end;

function TTMSFNCCustomDataGrid.GetOnLoadCellData: TTMSFNCDataGridDataCellDataEvent;
begin
  Result := Root.OnLoadCellData;
end;

function TTMSFNCCustomDataGrid.GetOnLogMessage: TTMSFNCDataGridLogMessageEvent;
begin
  Result := Root.OnLogMessage;
end;

function TTMSFNCCustomDataGrid.GetOnNeedFilterDropDownData: TTMSFNCDataGridNeedFilterDropDownData;
begin
  Result := Root.OnNeedFilterDropDownData;
end;

function TTMSFNCCustomDataGrid.GetOnRowDropped: TTMSFNCDataGridRowDroppedEvent;
begin
  Result := Root.OnRowDropped;
end;

function TTMSFNCCustomDataGrid.GetOnRowExpand: TTMSFNCDataGridDataRowExpandEvent;
begin
  Result := Root.OnRowExpand;
end;

function TTMSFNCCustomDataGrid.GetOnRemoveFilter: TTMSFNCDataGridRemoveFilterEvent;
begin
  Result := Root.OnRemoveFilter;
end;

function TTMSFNCCustomDataGrid.GetOnRowCollapse: TTMSFNCDataGridDataRowCollapseEvent;
begin
  Result := Root.OnRowCollapse;
end;

function TTMSFNCCustomDataGrid.GetOnRowSized: TTMSFNCDataGridRowSizedEvent;
begin
  Result := Root.OnRowSized;
end;

function TTMSFNCCustomDataGrid.GetOnSaveCellData: TTMSFNCDataGridDataCellDataEvent;
begin
  Result := Root.OnSaveCellData;
end;

function TTMSFNCCustomDataGrid.GetOnSelectCell: TTMSFNCDataGridSelectCellEvent;
begin
  Result := Root.OnSelectCell;
end;

function TTMSFNCCustomDataGrid.GetOnSelectingCell: TTMSFNCDataGridSelectingCellEvent;
begin
  Result := Root.OnSelectingCell;
end;

function TTMSFNCCustomDataGrid.GetOnSizeColumn: TTMSFNCDataGridSizeColumnEvent;
begin
  Result := Root.OnSizeColumn;
end;

function TTMSFNCCustomDataGrid.GetOnSizeRow: TTMSFNCDataGridSizeRowEvent;
begin
  Result := Root.OnSizeRow;
end;

function TTMSFNCCustomDataGrid.GetOnVScroll: TTMSFNCDataGridScrollEvent;
begin
  Result := Root.OnVScroll;
end;

function TTMSFNCCustomDataGrid.GetOptions: TTMSFNCDataGridOptions;
begin
  Result := Root.Options;
end;

function TTMSFNCCustomDataGrid.GetPageCount: Integer;
begin
  Result := Root.PageCount;
end;

function TTMSFNCCustomDataGrid.GetPageIndex: Integer;
begin
  Result := Root.PageIndex;
end;

function TTMSFNCCustomDataGrid.GetParentRow(ARow: Integer): Integer;
begin
  Result := Root.GetParentRow(ARow);
end;

function TTMSFNCCustomDataGrid.GetReadOnlys(AColumn, ARow: Integer): Boolean;
begin
  Result := Root.ReadOnlys[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetRightColumn: Integer;
begin
  Result := Root.RightColumn;
end;

function TTMSFNCCustomDataGrid.GetRootRow(ARow: Integer): Integer;
begin
  Result := Root.GetRootRow(ARow);
end;

function TTMSFNCCustomDataGrid.GetRowCount: Integer;
begin
  Result := Root.RowCount;
end;

function TTMSFNCCustomDataGrid.GetRowHeights(ARow: Integer): Double;
begin
  Result := Root.RowHeights[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowNodeChildCount(ARow: Integer): Integer;
begin
  Result := Root.RowNodeChildCount[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowNodeLevel(ARow: Integer): Integer;
begin
  Result := Root.RowNodeLevel[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowNodeState(
  ARow: Integer): TTMSFNCDataGridRowNodeState;
begin
  Result := Root.RowNodeState[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowOrder(ARow: Integer): Integer;
begin
  Result := Root.RowOrders[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowPos(ARow: Integer): Double;
begin
  Result := Root.RowPositions[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowReverseOrder(ARow: Integer): Integer;
begin
  Result := Root.RowReverseOrders[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowText(ARow: Integer): string;
begin
  Result := Root.RowText[ARow];
end;

function TTMSFNCCustomDataGrid.GetRowTypes(ARow: Integer): TTMSFNCDataGridRowType;
begin
  Result := Root.RowTypes[ARow];
end;

function TTMSFNCCustomDataGrid.GetScrollMode: TTMSFNCDataGridScrollMode;
begin
  Result := Root.ScrollMode;
end;

function TTMSFNCCustomDataGrid.GetScrollUpdate: TTMSFNCDataGridScrollUpdate;
begin
  Result := Root.ScrollUpdate;
end;

function TTMSFNCCustomDataGrid.GetSelectedCellCount: Integer;
begin
  Result := Root.SelectedCellCount;
end;

function TTMSFNCCustomDataGrid.GetSelectedCells(AColumn,
  ARow: Integer): Boolean;
begin
  Result := Root.SelectedCells[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetSelectedColumnCount: Integer;
begin
  Result := Root.SelectedColumnCount;
end;

function TTMSFNCCustomDataGrid.GetSelectedColumns(AColumn: Integer): Boolean;
begin
  Result := Root.SelectedColumns[AColumn];
end;

function TTMSFNCCustomDataGrid.GetSelectedRowCount: Integer;
begin
  Result := Root.SelectedRowCount;
end;

function TTMSFNCCustomDataGrid.GetSelectedRows(ARow: Integer): Boolean;
begin
  Result := Root.SelectedRows[ARow];
end;

function TTMSFNCCustomDataGrid.GetSelection: TTMSFNCDataGridCellCoordRange;
begin
  Result := Root.Selection;
end;

function TTMSFNCCustomDataGrid.GetSortIndexList: TTMSFNCDataGridSortIndexList;
begin
  Result := Root.SortIndexList;
end;

function TTMSFNCCustomDataGrid.GetStartColumn: Integer;
begin
  Result := Root.StartColumn;
end;

function TTMSFNCCustomDataGrid.GetStartRow: Integer;
begin
  Result := Root.StartRow;
end;

function TTMSFNCCustomDataGrid.GetStates(AColumn,
  ARow: Integer): TTMSFNCDataGridCellState;
begin
  Result := Root.States[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetStopColumn: Integer;
begin
  Result := Root.StopColumn;
end;

function TTMSFNCCustomDataGrid.GetStopRow: Integer;
begin
  Result := Root.StopRow;
end;

function TTMSFNCCustomDataGrid.GetStoredCells(AColumn,
  ARow: Integer): TTMSFNCDataGridCellValue;
begin
  Result := Root.StoredCells[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetStretchScrollBars: Boolean;
begin
  Result := Root.StretchScrollBars;
end;

function TTMSFNCCustomDataGrid.GetStrings(AColumn, ARow: Integer): string;
begin
  Result := Root.Strings[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetStrippedCells(AColumn, ARow: Integer): string;
begin
  Result := Root.StrippedCells[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetTextAngles(AColumn, ARow: Integer): Double;
begin
  Result := Root.TextAngles[AColumn, ARow];
end;

function TTMSFNCCustomDataGrid.GetTopRow: Integer;
begin
  Result := Root.TopRow;
end;

function TTMSFNCCustomDataGrid.GetTotalColumnWidth: Double;
begin
  Result := Root.TotalColumnWidth;
end;

function TTMSFNCCustomDataGrid.GetTotalFixedBottomRowHeight: Double;
begin
  Result := Root.GetTotalFixedBottomRowHeight;
end;

function TTMSFNCCustomDataGrid.GetTotalFixedColumnWidth: Double;
begin
  Result := Root.GetTotalFixedColumnWidth;
end;

function TTMSFNCCustomDataGrid.GetTotalFixedRightColumnWidth: Double;
begin
  Result := Root.GetTotalFixedRightColumnWidth;
end;

function TTMSFNCCustomDataGrid.GetTotalFixedRowHeight: Double;
begin
  Result := Root.GetTotalFixedRowHeight;
end;

function TTMSFNCCustomDataGrid.GetTotalRowHeight: Double;
begin
  Result := Root.TotalRowHeight;
end;

function TTMSFNCCustomDataGrid.GetVisibleColumnCount: Integer;
begin
  Result := Root.VisibleColumnCount;
end;

function TTMSFNCCustomDataGrid.GetVisibleRowCount: Integer;
begin
  Result := Root.VisibleRowCount;
end;

function TTMSFNCCustomDataGrid.GetVisualGroupingRect: TRectF;
var
  hr, br: TRectF;
begin
  hr := GetHeaderRect;
  br := GetHeaderBarRect;
  Result := RectF(hr.Left, hr.Top, hr.Right, br.Top);
end;

function TTMSFNCCustomDataGrid.GetVMax: Single;
begin
  Result := Root.GetVMax;
end;

function TTMSFNCCustomDataGrid.GetVScrollValue: Single;
begin
  Result := Root.GetVScrollValue;
end;

function TTMSFNCCustomDataGrid.GetVViewPortSize: Single;
begin
  Result := Root.GetVViewPortSize;
end;

function TTMSFNCCustomDataGrid.GetWordWrapping(AColumn, ARow: Integer): Boolean;
begin
  Result := Root.WordWrapping[AColumn, ARow];
end;

procedure TTMSFNCCustomDataGrid.GoToCell(ACell: TTMSFNCDataGridCellCoord; ASelect: Boolean = True);
begin
  Root.GoToCell(ACell, ASelect);
end;

procedure TTMSFNCCustomDataGrid.GoToPage(APageIndex: Integer);
begin
  Root.GoToPage(APageIndex);
end;

procedure TTMSFNCCustomDataGrid.Group(AColumn: Integer);
begin
  Root.Group(AColumn);
end;

procedure TTMSFNCCustomDataGrid.Group(AGroupInfo: TTMSFNCDataGridDataGroupInfo;
  ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
begin
  Root.Group(AGroupInfo, ACustomGroupCallback, ACustomCompareCallback, AGroupOptionsCallback, ASortOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.Group(AColumns, ASortColumns: TArray<Integer>;
  ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
begin
  Root.Group(AColumns, ASortColumns, ACustomGroupCallback, ACustomCompareCallback, AGroupOptionsCallback, ASortOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupAverage(AColumn: Integer);
begin
  Root.GroupAverage(AColumn);
end;

procedure TTMSFNCCustomDataGrid.GroupAverage(AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupAverage(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupCalculation(AColumns: TArray<Integer>;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  Root.GroupCalculation(AColumns, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupCalculation(AColumn: Integer;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  Root.GroupCalculation(AColumn, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupCount(AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupCount(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupCount(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupCount(AColumn, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupCustomCalculation(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  Root.GroupCustomCalculation(AColumn, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupCustomCalculation(
  AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  Root.GroupCustomCalculation(AColumns, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupDistinct(AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupDistinct(AColumns, ACalculationOptionsCallback);
end;

function TTMSFNCCustomDataGrid.GroupInfoToHeaderGroups(AIsGrouped: Boolean;
  AGroupInfo: TTMSFNCDataGridDataGroupInfo;
  AHeaderGroups: TTMSFNCDataGridHeaderGroups): Boolean;
var
  ah: Single;
  lh: TArray<TRectF>;
begin
  Result := GroupInfoToHeaderGroups(AIsGrouped, AGroupInfo, AHeaderGroups, nil, ah, lh);
end;

function TTMSFNCCustomDataGrid.GroupInfoToHeaderGroups(AIsGrouped: Boolean;
  AGroupInfo: TTMSFNCDataGridDataGroupInfo; AHeaderGroups: TTMSFNCDataGridHeaderGroups;
  ASkipHeaderGroup: TTMSFNCDataGridHeaderGroup; var AAutoHeaderSize: Single;var AHeaderLevelRects: TArray<TRectF>): Boolean;
var
  g: TTMSFNCGraphics;
  hr: TRectF;
  gi: TTMSFNCDataGridDataGroupInfo;
  maxh: Single;
  lvl, i: Integer;

  procedure CalculateGroupInfo(AGraphics: TTMSFNCGraphics; AGroupSubInfo: TTMSFNCDataGridDataGroupInfo; var ALevel: Integer; ARect: TRectF);
  var
    x, height: Single;
    i: Integer;
    hg: TTMSFNCDataGridHeaderGroup;
  begin
    x := ARect.Left;
    maxh := 0;

    for i := 0 to High(AGroupSubInfo.Columns) do
    begin
      if Assigned(ASkipHeaderGroup) and (ASkipHeaderGroup.Column = AGroupSubInfo.Columns[i]) then
        hg := ASkipHeaderGroup
      else
        hg := TTMSFNCDataGridHeaderGroup.Create;

      hg.OnBeforeDraw := DoBeforeDrawHeaderGroup;
      hg.OnAfterDraw := DoAfterDrawHeaderGroup;

      InitializeHeaderGroup(AGraphics, hg, AGroupSubInfo.Columns[i], ALevel, x, ARect.Top);
      AHeaderGroups.Add(hg);

      x := hg.Rect.Right + 4;
      height := hg.Rect.Bottom - hg.Rect.Top;
      maxh := Max(maxh, height);
      AAutoHeaderSize := Max(AAutoHeaderSize, hg.Rect.Bottom);
    end;

    if ALevel >= Length(AHeaderLevelRects) then
      SetLength(AHeaderLevelRects, ALevel + 1);

    if AHeaderLevelRects[ALevel].Bottom < ARect.Top + maxh then
      AHeaderLevelRects[ALevel] := RectF(ARect.Left, ARect.Top, ARect.Right, ARect.Top + maxh);

    Inc(ALevel);
    for i := 0 to High(AGroupSubInfo.SubColumns) do
    begin
      CalculateGroupInfo(
        AGraphics,
        AGroupSubInfo.SubColumns[i],
        ALevel,
        RectF(x, ARect.Top + maxh + 2, ARect.Right, ARect.Bottom)
      );
    end;
  end;

begin
  Result := True;
  AAutoHeaderSize := 0;
  SetLength(AHeaderLevelRects, 0);

  for i := AHeaderGroups.Count - 1 downto 0 do
  begin
    if AHeaderGroups[i] = ASkipHeaderGroup then
      AHeaderGroups.Extract(ASkipHeaderGroup)
    else
      AHeaderGroups.Delete(i);
  end;

  if AIsGrouped then
  begin
    gi := AGroupInfo;
    g := CalculateGraphics;

    if Assigned(g) then
    begin
      g.BitmapContainer := BitmapContainer;
      // g.OptimizedHTMLDrawing := OptimizedHTMLDrawing;
      hr := GetHeaderRect;
      lvl := 0;
      CalculateGroupInfo(g, gi, lvl, hr);
    end;
  end;

  if AAutoHeaderSize = 0 then
    AAutoHeaderSize := Header.VisualGrouping.Size + 2;

  if AAutoHeaderSize > 0 then
    AAutoHeaderSize := AAutoHeaderSize + 2;

  AAutoHeaderSize := Round(AAutoHeaderSize);

  if Assigned(ASkipHeaderGroup) and
     (CanAddLevel(AHeaderGroups, ASkipHeaderGroup, ASkipHeaderGroup.Level + 1, True) or
      CanAddLevel(AHeaderGroups, ASkipHeaderGroup, ASkipHeaderGroup.Level + 2, True)) then
    AAutoHeaderSize := AAutoHeaderSize + maxh + 2;
end;

procedure TTMSFNCCustomDataGrid.GroupDistinct(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupDistinct(AColumn, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupMax(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupMax(AColumn, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupMax(AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupMax(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupMin(AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupMin(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupMin(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupMin(AColumn, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupSort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection);
begin
  Root.GroupSort(AColumn, ADirection);
end;

procedure TTMSFNCCustomDataGrid.GroupSort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
begin
  Root.GroupSort(AColumns, ADirections, ACustomCompareCallback, ASortOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupStandardDeviation(
  AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupStandardDeviation(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupStandardDeviation(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupStandardDeviation(AColumn, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupSum(AColumn: Integer;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupSum(AColumn, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.GroupSum(AColumns: TArray<Integer>;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback);
begin
  Root.GroupSum(AColumns, ACalculationOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.SetBooleans(AColumn, ARow: Integer;
  const Value: Boolean);
begin
  Root.Booleans[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetCellAppearance(
  const Value: TTMSFNCDataGridCellAppearance);
begin
  Root.CellAppearance.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetIcons(
  const Value: TTMSFNCDataGridIcons);
begin
  Root.Icons.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetCellPopupMenu(const Value: TTMSFNCDataGridPopupMenu);
begin
  Root.CellPopupMenu := Value;
end;

procedure TTMSFNCCustomDataGrid.SetCells(AColumn, ARow: Integer;
  const Value: TTMSFNCDataGridCellValue);
begin
  Root.Cells[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetCheckBox(AColumn, ARow: Integer;
  AValue: Boolean);
begin
  Root.SetCheckBox(AColumn, ARow, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetCheckBox(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean);
begin
  Root.SetCheckBox(ACell, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetCheckBoxColumn(AColumn: Integer; AValue: Boolean = False);
begin
  Root.SetCheckBoxColumn(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetRowViewMode(
  const Value: TTMSFNCDataGridRowViewMode);
begin
  Root.RowViewMode := Value;
end;

procedure TTMSFNCCustomDataGrid.SetColumnViewMode(
  const Value: TTMSFNCDataGridColumnViewMode);
begin
  Root.ColumnViewMode := Value;
end;

procedure TTMSFNCCustomDataGrid.SetDataCheckBox(AColumn, ARow: Integer);
begin
  Root.SetDataCheckBox(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.SetDataCheckBox(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.SetDataCheckBox(ACell);
end;

procedure TTMSFNCCustomDataGrid.SetDataCheckBoxColumn(AColumn: Integer);
begin
  Root.SetDataCheckBoxColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.CustomReadProperty(AObject: TObject;
  APropertyName: string; APropertyKind: TTypeKind; AReader: TTMSFNCJSONReader;
  var ACanRead: Boolean);
var
  rd: TTMSFNCReader;
begin
  if ACanRead and (APropertyName = 'RootPersistence') then
  begin
    ACanRead := False;
    rd := TTMSFNCReader.Create(AReader);
    try
      if not AReader.IsNull then
        rd.Read(Root)
    finally
     rd.Free;
    end;
  end;
end;

procedure TTMSFNCCustomDataGrid.CustomWriteProperty(AObject: TObject;
  APropertyName: string; APropertyKind: TTypeKind; AWriter: TTMSFNCJSONWriter;
  var ACanWrite: Boolean);
var
  wr: TTMSFNCWriter;
begin
  if ACanWrite and (APropertyName = 'RootPersistence') then
  begin
    ACanWrite := False;
    wr := TTMSFNCWriter.Create(AWriter);
    try
      AWriter.WriteName('RootPersistence');
      wr.Write(Root);
    finally
      wr.Free;
    end;
  end;
end;

procedure TTMSFNCCustomDataGrid.SetDataColumnCount(AValue: Integer);
begin
  TTMSFNCDataGridRendererOpen(Root).SetDataColumnCount(AValue);
end;

procedure TTMSFNCCustomDataGrid.SetDataHeader(AColumn: Integer; AValue: string);
begin
  TTMSFNCDataGridRendererOpen(Root).SetDataHeader(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetDataProgressBar(AColumn, ARow: Integer);
begin
  Root.SetDataProgressBar(AColumn, ARow);
end;

procedure TTMSFNCCustomDataGrid.SetDataProgressBar(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.SetDataProgressBar(ACell);
end;

procedure TTMSFNCCustomDataGrid.SetDataProgressBarColumn(AColumn: Integer);
begin
  Root.SetDataProgressBarColumn(AColumn);
end;

procedure TTMSFNCCustomDataGrid.SetPageIndex(const Value: Integer);
begin
  Root.PageIndex := Value;
end;

procedure TTMSFNCCustomDataGrid.SetProgressBar(AColumn, ARow: Integer;
  AValue: Integer = 0);
begin
  Root.SetProgressBar(AColumn, ARow, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetProgressBar(ACell: TTMSFNCDataGridCellCoord;
  AValue: Integer = 0);
begin
  Root.SetProgressBar(ACell, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetProgressBarColumn(AColumn: Integer; AValue: Integer = 0);
begin
  Root.SetProgressBarColumn(AColumn, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetButton(AColumn, ARow: Integer; AText: string = '');
begin
  Root.SetButton(AColumn, ARow, AText);
end;

procedure TTMSFNCCustomDataGrid.SetButton(ACell: TTMSFNCDataGridCellCoord; AText: string = '');
begin
  Root.SetButton(ACell, AText);
end;

procedure TTMSFNCCustomDataGrid.SetButtonColumn(AColumn: Integer; AText: string = '');
begin
  Root.SetButtonColumn(AColumn, AText);
end;

procedure TTMSFNCCustomDataGrid.SetRadioButton(AColumn, ARow: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.SetRadioButton(AColumn, ARow, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.SetRadioButton(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.SetRadioButton(ACell, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.SetRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.SetRadioButtonColumn(AColumn, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.SetDataRadioButton(AColumn, ARow: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.SetDataRadioButton(AColumn, ARow, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.SetDataRadioButton(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.SetDataRadioButton(ACell, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.SetDataRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  Root.SetDataRadioButtonColumn(AColumn, AValue, AGroupIndex);
end;

procedure TTMSFNCCustomDataGrid.SetDataRowCount(AValue: Integer);
begin
  TTMSFNCDataGridRendererOpen(Root).SetDataRowCount(AValue);
end;

procedure TTMSFNCCustomDataGrid.SetDataValue(AColumn, ARow: Integer;
  AValue: string);
begin
  TTMSFNCDataGridRendererOpen(Root).SetDataValue(AColumn, ARow, AValue);
end;

procedure TTMSFNCCustomDataGrid.SetColors(AColumn, ARow: Integer;
  const Value: TTMSFNCGraphicsColor);
begin
  Root.Colors[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetColumnCalculation(AColumn: Integer; AName: string;
  const Value: TTMSFNCDataGridDataColumnCalculationArray);
begin
  Root.ColumnCalculations[AColumn, AName] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowCalculation(ARow: Integer; AName: string;
  const Value: TTMSFNCDataGridDataRowCalculationArray);
begin
  Root.RowCalculations[ARow, AName] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetColumnCount(const Value: Integer);
begin
  Root.ColumnCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetColumns(const Value: TTMSFNCDataGridColumns);
begin
  Root.Columns.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetColumnWidths(AColumn: Integer;
  const Value: Double);
begin
  Root.ColumnWidths[AColumn] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetComments(AColumn, ARow: Integer;
  const Value: string);
begin
  Root.Comments[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetHints(AColumn, ARow: Integer;
  const Value: string);
begin
  Root.Hints[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetControlAligns(AColumn, ARow: Integer;
  const Value: TTMSFNCDataGridCellControlAlign);
begin
  Root.ControlAligns[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetControlPositions(AColumn, ARow: Integer;
  const Value: TTMSFNCDataGridCellControlPosition);
begin
  Root.ControlPositions[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetControlHeights(AColumn, ARow: Integer;
  const Value: Integer);
begin
  Root.ControlHeights[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetControls(AColumn, ARow: Integer;
  const Value: TTMSFNCDataGridCellControl);
begin
  Root.Controls[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetControlTexts(AColumn, ARow: Integer;
  const Value: string);
begin
  Root.ControlTexts[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetControlWidths(AColumn, ARow: Integer;
  const Value: Integer);
begin
  Root.ControlWidths[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetCustomScrollBars(const Value: Boolean);
begin
  Root.CustomScrollBars := Value;
end;

procedure TTMSFNCCustomDataGrid.SetDefaultColumnWidth(const Value: Double);
begin
  Root.DefaultColumnWidth := Value;
end;

procedure TTMSFNCCustomDataGrid.SetDefaultRowHeight(const Value: Double);
begin
  Root.DefaultRowHeight := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFilterActions(
  const Value: TTMSFNCDataGridDataFilterActions);
begin
  Root.FilterActions.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetFilterAppearance(const Value: TTMSFNCDataGridFilterAppearance);
begin
  Root.FilterAppearance.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetDragAppearance(const Value: TTMSFNCDataGridDragAppearance);
begin
  Root.DragAppearance.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetFixedBottomRowCount(const Value: Integer);
begin
  Root.FixedBottomRowCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFixedColumnCount(const Value: Integer);
begin
  Root.FixedColumnCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFixedRightColumnCount(const Value: Integer);
begin
  Root.FixedRightColumnCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFixedRowCount(const Value: Integer);
begin
  Root.FixedRowCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFloats(AColumn, ARow: Integer;
  const Value: Double);
begin
  Root.Floats[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFreezeColumnCount(const Value: Integer);
begin
  Root.FreezeColumnCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetFreezeRowCount(const Value: Integer);
begin
  Root.FreezeRowCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetManagedObjects(AColumn, ARow: Integer;
  const Value: TObject);
begin
  Root.ManagedObjects[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetMergedColumns(AColumn: Integer;
  const Value: Boolean);
begin
  Root.MergedColumns[AColumn] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetMergedRows(ARow: Integer;
  const Value: Boolean);
begin
  Root.MergedRows[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetNodeRow(ARow, AChildCount, ALevel: Integer;
  AMerge: Boolean);
begin
  Root.SetNodeRow(ARow, AChildCount, ALevel, AMerge);
end;

procedure TTMSFNCCustomDataGrid.SetObjects(AColumn, ARow: Integer;
  const Value: TObject);
begin
  Root.Objects[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterApplyFilter(
  const Value: TTMSFNCDataGridAfterApplyFilterEvent);
begin
  Root.OnAfterApplyFilter := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterCloseInplaceEditor(
  const Value: TTMSFNCDataGridAfterCloseInplaceEditorEvent);
begin
  Root.OnAfterCloseInplaceEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterCopyToClipboard(
  const Value: TTMSFNCDataGridAfterClipboardEvent);
begin
  Root.OnAfterCopyToClipboard := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterCreateFilterEditor(
  const Value: TTMSFNCDataGridAfterCreateFilterEditorEvent);
begin
  Root.OnAfterCreateFilterEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterCutToClipboard(
  const Value: TTMSFNCDataGridAfterClipboardEvent);
begin
  Root.OnAfterCutToClipboard := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterDrawCell(
  const Value: TTMSFNCDataGridAfterDrawCellEvent);
begin
  Root.OnAfterDrawCell := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterDrawEmptySpace(
  const Value: TTMSFNCDataGridAfterDrawEmptySpaceEvent);
begin
  Root.OnAfterDrawEmptySpace := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterDrawGrid(
  const Value: TTMSFNCDataGridAfterDrawGridEvent);
begin
  Root.OnAfterDrawGrid := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterLookup(
  const Value: TTMSFNCDataGridAfterLookupEvent);
begin
  Root.OnAfterLookup := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterOpenInplaceEditor(
  const Value: TTMSFNCDataGridAfterOpenInplaceEditorEvent);
begin
  Root.OnAfterOpenInplaceEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterPasteFromClipboard(
  const Value: TTMSFNCDataGridAfterClipboardEvent);
begin
  Root.OnAfterPasteFromClipboard := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterSetColumnCalculation(
  const Value: TTMSFNCDataGridDataAfterSetColumnCalculation);
begin
  Root.OnAfterSetColumnCalculation := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterSetRowCalculation(
  const Value: TTMSFNCDataGridDataAfterSetRowCalculation);
begin
  Root.OnAfterSetRowCalculation := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterShowFilter(
  const Value: TTMSFNCDataGridAfterShowFilterEvent);
begin
  Root.OnAfterShowFilter := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterShowFindDialog(
  const Value: TTMSFNCDataGridAfterShowFindDialogEvent);
begin
  Root.OnAfterShowFindDialog := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterShowFilterDialog(
  const Value: TTMSFNCDataGridAfterShowFilterDialogEvent);
begin
  Root.OnAfterShowFilterDialog := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterShowReplaceDialog(
  const Value: TTMSFNCDataGridAfterShowReplaceDialogEvent);
begin
  Root.OnAfterShowReplaceDialog := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAfterSortColumn(
  const Value: TTMSFNCDataGridAfterSortColumnEvent);
begin
  Root.OnAfterSortColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAppendColumn(
  const Value: TTMSFNCDataGridAppendColumnEvent);
begin
  Root.OnAppendColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnAppendRow(
  const Value: TTMSFNCDataGridAppendRowEvent);
begin
  Root.OnAppendRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeDrawCell(
  const Value: TTMSFNCDataGridBeforeDrawCellEvent);
begin
  Root.OnBeforeDrawCell := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeDrawEmptySpace(
  const Value: TTMSFNCDataGridBeforeDrawEmptySpaceEvent);
begin
  Root.OnBeforeDrawEmptySpace := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeLookup(
  const Value: TTMSFNCDataGridBeforeLookupEvent);
begin
  Root.OnBeforeLookup := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeOpenInplaceEditor(
  const Value: TTMSFNCDataGridBeforeOpenInplaceEditorEvent);
begin
  Root.OnBeforeOpenInplaceEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforePasteFromClipboard(
  const Value: TTMSFNCDataGridBeforeClipboardEvent);
begin
  Root.OnBeforePasteFromClipboard := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeSetColumnCalculation(
  const Value: TTMSFNCDataGridDataBeforeSetColumnCalculation);
begin
  Root.OnBeforeSetColumnCalculation := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeSetRowCalculation(
  const Value: TTMSFNCDataGridDataBeforeSetRowCalculation);
begin
  Root.OnBeforeSetRowCalculation := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeShowFilter(
  const Value: TTMSFNCDataGridBeforeShowFilterEvent);
begin
  Root.OnBeforeShowFilter := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeShowFindDialog(
  const Value: TTMSFNCDataGridBeforeShowFindDialogEvent);
begin
  Root.OnBeforeShowFindDialog := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeShowFilterDialog(
  const Value: TTMSFNCDataGridBeforeShowFilterDialogEvent);
begin
  Root.OnBeforeShowFilterDialog := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeShowReplaceDialog(
  const Value: TTMSFNCDataGridBeforeShowReplaceDialogEvent);
begin
  Root.OnBeforeShowReplaceDialog := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeSortColumn(
  const Value: TTMSFNCDataGridBeforeSortColumnEvent);
begin
  Root.OnBeforeSortColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanAppendColumn(
  const Value: TTMSFNCDataGridCanAppendColumnEvent);
begin
  Root.OnCanAppendColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanAppendRow(
  const Value: TTMSFNCDataGridCanAppendRowEvent);
begin
  Root.OnCanAppendRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanDeleteColumn(
  const Value: TTMSFNCDataGridCanDeleteColumnEvent);
begin
  Root.OnCanDeleteColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanDeleteRow(
  const Value: TTMSFNCDataGridCanDeleteRowEvent);
begin
  Root.OnCanDeleteRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanDragColumn(
  const Value: TTMSFNCDataGridCanDragColumnEvent);
begin
  Root.OnCanDragColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanDragRow(
  const Value: TTMSFNCDataGridCanDragRowEvent);
begin
  Root.OnCanDragRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanDropColumn(
  const Value: TTMSFNCDataGridCanDropColumnEvent);
begin
  Root.OnCanDropColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanDropRow(
  const Value: TTMSFNCDataGridCanDropRowEvent);
begin
  Root.OnCanDropRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanEditCell(
  const Value: TTMSFNCDataGridCanEditCellEvent);
begin
  Root.OnCanEditCell := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanInsertColumn(
  const Value: TTMSFNCDataGridCanInsertColumnEvent);
begin
  Root.OnCanInsertColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanInsertRow(
  const Value: TTMSFNCDataGridCanInsertRowEvent);
begin
  Root.OnCanInsertRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanSizeColumn(
  const Value: TTMSFNCDataGridCanSizeColumnEvent);
begin
  Root.OnCanSizeColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCanSizeRow(
  const Value: TTMSFNCDataGridCanSizeRowEvent);
begin
  Root.OnCanSizeRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellAnchorClick(
  const Value: TTMSFNCDataGridCellAnchorClickEvent);
begin
  Root.OnCellAnchorClick := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellButtonClick(
  const Value: TTMSFNCDataGridCellClickEvent);
begin
  Root.OnCellButtonClick := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellCheckBoxChange(
  const Value: TTMSFNCDataGridCellClickEvent);
begin
  Root.OnCellCheckBoxChange := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellClick(
  const Value: TTMSFNCDataGridCellClickEvent);
begin
  Root.OnCellClick := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellDblClick(
  const Value: TTMSFNCDataGridCellClickEvent);
begin
  Root.OnCellDblClick := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellEditGetData(
  const Value: TTMSFNCDataGridCellEditGetDataEvent);
begin
  Root.OnCellEditGetData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellEditSetData(
  const Value: TTMSFNCDataGridCellEditSetDataEvent);
begin
  Root.OnCellEditSetData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellEditValidateData(
  const Value: TTMSFNCDataGridCellEditValidateDataEvent);
begin
  Root.OnCellEditValidateData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellBeforeShowPopupMenu(
  const Value: TTMSFNCDataGridCellBeforeShowPopupMenuEvent);
begin
  Root.OnCellBeforeShowPopupMenu := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellBeforeNodeExpand(
  const Value: TTMSFNCDataGridCellBeforeNodeExpandEvent);
begin
  Root.OnCellBeforeNodeExpand := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellBeforeNodeCollapse(
  const Value: TTMSFNCDataGridCellBeforeNodeCollapseEvent);
begin
  Root.OnCellBeforeNodeCollapse := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellAfterNodeExpand(
  const Value: TTMSFNCDataGridCellAfterNodeExpandEvent);
begin
  Root.OnCellAfterNodeExpand := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellAfterNodeCollapse(
  const Value: TTMSFNCDataGridCellAfterNodeCollapseEvent);
begin
  Root.OnCellAfterNodeCollapse := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellRadioButtonChange(
  const Value: TTMSFNCDataGridCellClickEvent);
begin
  Root.OnCellRadioButtonChange := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCellRightClick(
  const Value: TTMSFNCDataGridCellClickEvent);
begin
  Root.OnCellRightClick := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeApplyFilter(
  const Value: TTMSFNCDataGridBeforeApplyFilterEvent);
begin
  Root.OnBeforeApplyFilter := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeCloseInplaceEditor(
  const Value: TTMSFNCDataGridBeforeCloseInplaceEditorEvent);
begin
  Root.OnBeforeCloseInplaceEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeCopyToClipboard(
  const Value: TTMSFNCDataGridBeforeClipboardEvent);
begin
  Root.OnBeforeCopyToClipboard := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeCreateFilterEditor(
  const Value: TTMSFNCDataGridBeforeCreateFilterEditorEvent);
begin
  Root.OnBeforeCreateFilterEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnBeforeCutToClipboard(
  const Value: TTMSFNCDataGridBeforeClipboardEvent);
begin
  Root.OnBeforeCutToClipboard := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCustomColumnCalculation(
  const Value: TTMSFNCDataGridDataCustomColumnCalculationEvent);
begin
  Root.OnCustomColumnCalculation := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCustomRowCalculation(
  const Value: TTMSFNCDataGridDataCustomRowCalculationEvent);
begin
  Root.OnCustomRowCalculation := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnColumnDropped(
  const Value: TTMSFNCDataGridColumnDroppedEvent);
begin
  Root.OnColumnDropped := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnColumnSized(
  const Value: TTMSFNCDataGridColumnSizedEvent);
begin
  Root.OnColumnSized := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCreateCustomFilterEditor(
  const Value: TTMSFNCDataGridCreateCustomFilterEditorEvent);
begin
  Root.OnCreateCustomFilterEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCustomCompare(
  const Value: TTMSFNCDataGridDataCustomCompareEvent);
begin
  Root.OnCustomCompare := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnCustomizeFilterPopupEditor(
  const Value: TTMSFNCDataGridCustomizeFilterPopupEditorEvent);
begin
  Root.OnCustomizeFilterPopupEditor := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnDeleteColumn(
  const Value: TTMSFNCDataGridDeleteColumnEvent);
begin
  Root.OnDeleteColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnDeleteRow(
  const Value: TTMSFNCDataGridDeleteRowEvent);
begin
  Root.OnDeleteRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnDropFiles(
  const Value: TTMSFNCDataGridDropFilesEvent);
begin
  Root.OnDropFiles := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetColumnCalculationOptions(
  const Value: TTMSFNCDataGridDataGetColumnCalculationOptionsEvent);
begin
  Root.OnGetColumnCalculationOptions := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetRowCalculationOptions(
  const Value: TTMSFNCDataGridDataGetRowCalculationOptionsEvent);
begin
  Root.OnGetRowCalculationOptions := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellClass(
  const Value: TTMSFNCDataGridGetCellClassEvent);
begin
  Root.OnGetCellClass := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellData(
  const Value: TTMSFNCDataGridDataGetCellDataEvent);
begin
  Root.OnGetCellData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellDataProperties(
  const Value: TTMSFNCDataGridDataGetCellDataPropertiesEvent);
begin
  Root.OnGetCellDataProperties := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellDisplayValue(
  const Value: TTMSFNCDataGridDataGetCellDisplayValueEvent);
begin
  Root.OnGetCellDisplayValue := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellFormatting(
  const Value: TTMSFNCDataGridDataGetCellFormattingEvent);
begin
  Root.OnGetCellFormatting := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellHint(
  const Value: TTMSFNCDataGridGetCellHintEvent);
begin
  Root.OnGetCellHint := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellLayout(
  const Value: TTMSFNCDataGridGetCellLayoutEvent);
begin
  Root.OnGetCellLayout := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellLayoutHasPriority(
  const Value: TTMSFNCDataGridGetCellLayoutHasPriorityEvent);
begin
  Root.OnGetCellLayoutHasPriority := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCellProperties(
  const Value: TTMSFNCDataGridGetCellPropertiesEvent);
begin
  Root.OnGetCellProperties := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetCustomGroup(
  const Value: TTMSFNCDataGridDataGetCustomGroupEvent);
begin
  Root.OnGetCustomGroup := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetGroupOptions(
  const Value: TTMSFNCDataGridDataGetGroupOptionsEvent);
begin
  Root.OnGetGroupOptions := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetInplaceEditorClass(
  const Value: TTMSFNCDataGridGetInplaceEditorClassEvent);
begin
  Root.OnGetInplaceEditorClass := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetInplaceEditorType(
  const Value: TTMSFNCDataGridGetInplaceEditorTypeEvent);
begin
  Root.OnGetInplaceEditorType := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetInplaceEditorTarget(
  const Value: TTMSFNCDataGridGetInplaceEditorTargetEvent);
begin
  Root.OnGetInplaceEditorTarget := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetInplaceEditorRect(
  const Value: TTMSFNCDataGridGetInplaceEditorRectEvent);
begin
  Root.OnGetInplaceEditorRect := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetInplaceEditorProperties(
  const Value: TTMSFNCDataGridGetInplaceEditorPropertiesEvent);
begin
  Root.OnGetInplaceEditorProperties := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnGetSortOptions(
  const Value: TTMSFNCDataGridDataGetSortOptionsEvent);
begin
  Root.OnGetSortOptions := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnHScroll(
  const Value: TTMSFNCDataGridScrollEvent);
begin
  Root.OnHScroll := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnInsertColumn(
  const Value: TTMSFNCDataGridInsertColumnEvent);
begin
  Root.OnInsertColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnInsertRow(
  const Value: TTMSFNCDataGridInsertRowEvent);
begin
  Root.OnInsertRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnLoadCellData(
  const Value: TTMSFNCDataGridDataCellDataEvent);
begin
  Root.OnLoadCellData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnLogMessage(
  const Value: TTMSFNCDataGridLogMessageEvent);
begin
  Root.OnLogMessage := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnNeedFilterDropDownData(
  const Value: TTMSFNCDataGridNeedFilterDropDownData);
begin
  Root.OnNeedFilterDropDownData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnRowDropped(
  const Value: TTMSFNCDataGridRowDroppedEvent);
begin
  Root.OnRowDropped := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnRemoveFilter(
  const Value: TTMSFNCDataGridRemoveFilterEvent);
begin
  Root.OnRemoveFilter := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnRowCollapse(
  const Value: TTMSFNCDataGridDataRowCollapseEvent);
begin
  Root.OnRowCollapse := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnRowExpand(
  const Value: TTMSFNCDataGridDataRowExpandEvent);
begin
  Root.OnRowExpand := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnRowSized(
  const Value: TTMSFNCDataGridRowSizedEvent);
begin
  Root.OnRowSized := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnSaveCellData(
  const Value: TTMSFNCDataGridDataCellDataEvent);
begin
  Root.OnSaveCellData := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnSelectCell(
  const Value: TTMSFNCDataGridSelectCellEvent);
begin
  Root.OnSelectCell := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnSelectingCell(
  const Value: TTMSFNCDataGridSelectingCellEvent);
begin
  Root.OnSelectingCell := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnSizeColumn(
  const Value: TTMSFNCDataGridSizeColumnEvent);
begin
  Root.OnSizeColumn := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnSizeRow(
  const Value: TTMSFNCDataGridSizeRowEvent);
begin
  Root.OnSizeRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOnVScroll(
  const Value: TTMSFNCDataGridScrollEvent);
begin
  Root.OnVScroll := Value;
end;

procedure TTMSFNCCustomDataGrid.SetOptions(const Value: TTMSFNCDataGridOptions);
begin
  Root.Options.Assign(Value);
end;

procedure TTMSFNCCustomDataGrid.SetReadOnlys(AColumn, ARow: Integer;
  const Value: Boolean);
begin
  Root.ReadOnlys[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowCount(const Value: Integer);
begin
  Root.RowCount := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowHeights(ARow: Integer; const Value: Double);
begin
  Root.RowHeights[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowNodeChildCount(ARow: Integer;
  const Value: Integer);
begin
  Root.RowNodeChildCount[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowNodeLevel(ARow: Integer; const Value: Integer);
begin
  Root.RowNodeLevel[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowNodeState(ARow: Integer;
  const Value: TTMSFNCDataGridRowNodeState);
begin
  Root.RowNodeState[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowText(ARow: Integer; const Value: string);
begin
  Root.RowText[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetRowTypes(ARow: Integer;
  const Value: TTMSFNCDataGridRowType);
begin
  Root.RowTypes[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetScrollMode(
  const Value: TTMSFNCDataGridScrollMode);
begin
  Root.ScrollMode := Value;
end;

procedure TTMSFNCCustomDataGrid.SetScrollUpdate(
  const Value: TTMSFNCDataGridScrollUpdate);
begin
  Root.ScrollUpdate := Value;
end;

procedure TTMSFNCCustomDataGrid.SetSelectedCells(AColumn, ARow: Integer;
  const Value: Boolean);
begin
  Root.SelectedCells[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetSelectedColumns(AColumn: Integer;
  const Value: Boolean);
begin
  Root.SelectedColumns[AColumn] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetSelectedRows(ARow: Integer;
  const Value: Boolean);
begin
  Root.SelectedRows[ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetSelection(
  const Value: TTMSFNCDataGridCellCoordRange);
begin
  Root.Selection := Value;
end;

procedure TTMSFNCCustomDataGrid.SetDesigner(const Value: Boolean);
begin
  if FDesigner <> Value then
  begin
    FDesigner := Value;
    {$IFNDEF WEBLIB}
    if Assigned(FDesignerControl) then
    begin
      FDesignerControl.Visible := Designer;
      if FDesignerControl.Visible then
      begin
        FDesignerControl.Parent := Self;
        FDesignerControl.FRecreate := True;
        if not IsLoading then
        begin
          case FDesignerControl.ControlAlignment of
            caTop, caBottom: Height := Height + FDesignerControl.Height;
            caLeft, caRight: Width := Width + FDesignerControl.Width;
          end;
        end;
      end
      else
      begin
        FDesignerControl.Parent := nil;
        FDesignerControl.FRecreate := True;
        if not IsLoading then
        begin
          case FDesignerControl.ControlAlignment of
            caTop, caBottom: Height := Height - FDesignerControl.Height;
            caLeft, caRight: Width := Width - FDesignerControl.Width;
          end;
        end;
      end;
    end;
    {$ENDIF}
  end;
end;

procedure TTMSFNCCustomDataGrid.SetStretchScrollBars(const Value: Boolean);
begin
  Root.StretchScrollBars := Value;
end;

procedure TTMSFNCCustomDataGrid.SetStrings(AColumn, ARow: Integer; const Value: string);
begin
  Root.Strings[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetTextAngles(AColumn, ARow: Integer;
  const Value: Double);
begin
  Root.TextAngles[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetTopRow(const Value: Integer);
begin
  Root.TopRow := Value;
end;

procedure TTMSFNCCustomDataGrid.SetVerticalTextAligns(AColumn, ARow: Integer;
  const Value: TTMSFNCGraphicsTextAlign);
begin
  Root.VerticalTextAligns[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.SetWordWrapping(AColumn, ARow: Integer;
  const Value: Boolean);
begin
  Root.WordWrapping[AColumn, ARow] := Value;
end;

procedure TTMSFNCCustomDataGrid.ShowColumnsEditor;
begin
  Root.ShowColumnsEditor;
end;

procedure TTMSFNCCustomDataGrid.ShowFilterDialog;
begin
  Root.ShowFilterDialog;
end;

procedure TTMSFNCCustomDataGrid.ShowFindDialog;
begin
  Root.ShowFindDialog;
end;

procedure TTMSFNCCustomDataGrid.ShowReplaceDialog;
begin
  Root.ShowReplaceDialog;
end;

procedure TTMSFNCCustomDataGrid.SetVerticalScrollBarVisible(
  const Value: Boolean);
begin
  Root.VerticalScrollBarVisible := Value;
end;

procedure TTMSFNCCustomDataGrid.Sort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; AFromRow,
  AToRow: Integer; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
begin
  Root.Sort(AColumns, ADirections, AFromRow, AToRow, ACustomCompareCallback, ASortOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.SortIndexed;
begin
  Root.SortIndexed;
end;

procedure TTMSFNCCustomDataGrid.Sort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection);
begin
  Root.Sort(AColumn, ADirection);
end;

procedure TTMSFNCCustomDataGrid.Sort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection; AFromRow, AToRow: Integer);
begin
  Root.Sort(AColumn, ADirection, AFromRow, AToRow);
end;

procedure TTMSFNCCustomDataGrid.Sort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
begin
  Root.Sort(AColumns, ADirections, ACustomCompareCallback, ASortOptionsCallback);
end;

procedure TTMSFNCCustomDataGrid.SplitCell(ACell: TTMSFNCDataGridCellCoord);
begin
  Root.SplitCell(ACell);
end;

procedure TTMSFNCDataGrid.RegisterRuntimeClasses;
begin
  inherited;
  RegisterClasses([TTMSFNCDataGrid]);
end;

{ TTMSFNCDataGridHeaderFooter }

procedure TTMSFNCDataGridHeaderFooter.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridHeaderFooter then
  begin
    FVisible := (Source as TTMSFNCDataGridHeaderFooter).Visible;
    FSize := (Source as TTMSFNCDataGridHeaderFooter).Size;
    FFill.Assign((Source as TTMSFNCDataGridHeaderFooter).Fill);
    FStroke.Assign((Source as TTMSFNCDataGridHeaderFooter).Stroke);
    FAutoSize := (Source as TTMSFNCDataGridHeaderFooter).AutoSize;
  end;
end;

constructor TTMSFNCDataGridHeaderFooter.Create(AOwner: TPersistent);
begin
  FOwner := AOwner;
  FSize := 30;
  FVisible := False;
  FFill := TTMSFNCGraphicsFill.Create;
  FFill.OnChanged := @DoFillChanged;
  FStroke := TTMSFNCGraphicsStroke.Create;
  FStroke.OnChanged := @DoStrokeChanged;
  FAutoSize := True;
end;

destructor TTMSFNCDataGridHeaderFooter.Destroy;
begin
  FStroke.Free;
  FFill.Free;
  inherited;
end;

procedure TTMSFNCDataGridHeaderFooter.DoFillChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridHeaderFooter.DoStrokeChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

function TTMSFNCDataGridHeaderFooter.GetOwner: TPersistent;
begin
  Result := FOwner;
end;

procedure TTMSFNCDataGridHeaderFooter.SetAutoSize(const Value: Boolean);
begin
  if FAutoSize <> Value then
  begin
    FAutoSize := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

procedure TTMSFNCDataGridHeaderFooter.SetFill(const Value: TTMSFNCGraphicsFill);
begin
  FFill.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderFooter.SetSize(const Value: Integer);
begin
  if FSize <> Value then
  begin
    FSize := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

procedure TTMSFNCDataGridHeaderFooter.SetStroke(
  const Value: TTMSFNCGraphicsStroke);
begin
  FStroke.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderFooter.SetVisible(const Value: Boolean);
begin
  if FVisible <> Value then
  begin
    FVisible := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

{$IFNDEF WEBLIB}

{ TTMSFNCDataGridDesigner }

procedure TTMSFNCDataGridDesigner.AddControls;
begin
  FClose.Parent := Self;
  FLoadSample.Parent := Self;
  FFilter.Parent := Self;
  FSort.Parent := Self;
  FEdit.Parent := Self;

  FClose.SetBounds(5, 5, FClose.Width, FClose.Height);

  case ControlAlignment of
    caTop, caBottom:
    begin
      FLoadSample.SetBounds(30, 5, FLoadSample.Width, Height - 10);
      FFilter.SetBounds(35 + FLoadSample.Width, 5, FFilter.Width, Height - 10);
      FSort.SetBounds(40 + FLoadSample.Width + FFilter.Width, 5, FSort.Width, Height - 10);
      FEdit.SetBounds(45 + FLoadSample.Width + FFilter.Width + FSort.Width, 5, FEdit.Width, Height - 10);
    end;
    caLeft, caRight:
    begin
      FLoadSample.SetBounds(5, 40, FLoadSample.Width, FLoadSample.Height);
      FFilter.SetBounds(5, 45 + FLoadSample.Height, FFilter.Width, FFilter.Height);
      FSort.SetBounds(5, 50 + FLoadSample.Height + FFilter.Height, FSort.Width, FSort.Height);
      FEdit.SetBounds(5, 55 + FLoadSample.Height + FFilter.Height + FSort.Height, FEdit.Width, FEdit.Height);
    end;
  end;

  if Assigned(FGrid) then
  begin
    {$IFDEF FMXLIB}
    FFilter.IsChecked := FGrid.Options.Filtering.Enabled;
    FSort.IsChecked := FGrid.Options.Sorting.Enabled;
    FEdit.IsChecked := FGrid.Options.Editing.Enabled;
    {$ELSE}
    FFilter.Checked := FGrid.Options.Filtering.Enabled;
    FSort.Checked := FGrid.Options.Sorting.Enabled;
    FEdit.Checked := FGrid.Options.Editing.Enabled;
    {$ENDIF}
  end;
end;

procedure TTMSFNCDataGridDesigner.ApplyStyle;
begin
  inherited;
  FRecreate := True;
  Invalidate;
end;

constructor TTMSFNCDataGridDesigner.Create(AOwner: TComponent);
begin
  inherited;

  if AOwner is TTMSFNCCustomDataGrid then
    FGrid := AOwner as TTMSFNCCustomDataGrid;

  {$IFDEF FMXLIB}
  FDesignInteractive := True;
  FFirstMouseDown := False;
  FFirstMouseUp := False;
  {$ENDIF}

  FLoadSample := TButton.Create(nil);
  FLoadSample.Parent := Self;
  FLoadSample.OnClick := DoLoadSampleClick;
  FLoadSample.Width := 90;

  {$IFDEF FMXLIB}
  FLoadSample.Stored := False;
  FLoadSample.Text := 'Load Sample';
  FLoadSample.StaysPressed := True;
  FLoadSample.CanFocus := False;
  {$ELSE}
  FLoadSample.Caption := 'Load Sample';
  {$ENDIF}

  FClose := TTMSFNCButton.Create(nil);
  FClose.Parent := Self;
  FClose.OnClick := DoCloseClick;
  FClose.Width := 20;
  FClose.Height := 20;
  FClose.Bitmap.LoadFromResource(TTMSFNCDATAGRIDCLOSE, HInstance);
  FClose.ShowImage := True;
  FClose.ShowText := False;

  {$IFDEF FMXLIB}
  FClose.ImageAlign := TAlignLayout.Client;
  FClose.Stored := False;
  FClose.StaysPressed := True;
  FClose.CanFocus := False;
  {$ELSE}
  FClose.ImageAlign := alClient;
  {$ENDIF}
  TTMSFNCImageOpen(FClose.Image).DottedDesigntimeRectangle := False;

  FSort := TCheckBox.Create(nil);
  FSort.Parent := Self;
  FSort.OnClick := DoSortClick;
  FSort.Width := 75;
  {$IFDEF FMXLIB}
  FSort.Stored := False;
  FSort.Text := 'Sorting';
  FSort.CanFocus := False;
  {$ELSE}
  FSort.Caption := 'Sorting';
  {$ENDIF}

  FFilter := TCheckBox.Create(nil);
  FFilter.Parent := Self;
  FFilter.OnClick := DoFilterClick;
  FFilter.Width := 75;
  {$IFDEF FMXLIB}
  FFilter.Stored := False;
  FFilter.Text := 'Filtering';
  FFilter.CanFocus := False;
  {$ELSE}
  FFilter.Caption := 'Filtering';
  {$ENDIF}

  FEdit := TCheckBox.Create(nil);
  FEdit.Parent := Self;
  FEdit.OnClick := DoEditClick;
  FEdit.Width := 75;
  {$IFDEF FMXLIB}
  FEdit.Stored := False;
  FEdit.Text := 'Editing';
  FEdit.CanFocus := False;
  {$ELSE}
  FEdit.Caption := 'Editing';
  {$ENDIF}

  Width := 120;
  Height := 35;
  FCreateTimer := TTimer.Create(nil);
  FCreateTimer.Interval := 1;
  FCreateTimer.OnTimer := DoCreateTimer;
  FCreateTimer.Enabled := True;
end;

destructor TTMSFNCDataGridDesigner.Destroy;
begin
  FreeAndNil(FCreateTimer);
  FreeAndNil(FLoadSampleBitmap);
  FreeAndNil(FLoadSample);
  FreeAndNil(FCloseBitmap);
  FreeAndNil(FClose);
  FreeAndNil(FSortBitmap);
  FreeAndNil(FSort);
  FreeAndNil(FEditBitmap);
  FreeAndNil(FEdit);
  FreeAndNil(FFilterBitmap);
  FreeAndNil(FFilter);
  inherited;
end;

procedure TTMSFNCDataGridDesigner.DoCloseClick(Sender: TObject);
begin
  if Assigned(FGrid) then
    FGrid.Designer := False;
end;

procedure TTMSFNCDataGridDesigner.DoCreateTimer(Sender: TObject);
begin
  FCreateTimer.Enabled := False;
  FRecreate := True;
  Invalidate;
end;

procedure TTMSFNCDataGridDesigner.DoEditClick(Sender: TObject);
begin
  if Assigned(FGrid) then
  begin
    {$IFDEF FMXLIB}
    FGrid.Options.Editing.Enabled := FEdit.IsChecked;
    {$ELSE}
    FGrid.Options.Editing.Enabled := FEdit.Checked;
    {$ENDIF}
    FGrid.Width := FGrid.Width + 1;
    FGrid.Width := FGrid.Width - 1;
  end;
end;

procedure TTMSFNCDataGridDesigner.DoFilterClick(Sender: TObject);
begin
  if Assigned(FGrid) then
  begin
    {$IFDEF FMXLIB}
    FGrid.Options.Filtering.Enabled := FFilter.IsChecked;
    {$ELSE}
    FGrid.Options.Filtering.Enabled := FFilter.Checked;
    {$ENDIF}
    FGrid.Width := FGrid.Width + 1;
    FGrid.Width := FGrid.Width - 1;
  end;
end;

procedure TTMSFNCDataGridDesigner.DoLoadSampleClick(Sender: TObject);
var
  i: integer;
  str: String;
  ofd: TTMSFNCDataGridDesignerOpenDialog;
  {$IFDEF WEBLIB}
  res: boolean;
  {$ENDIF}
  ext: string;
begin
  if not Assigned(FGrid) then
    Exit;

  ofd := TTMSFNCDataGridDesignerOpenDialog.Create(Self);
  ofd.Options := [TOpenOption.ofFileMustExist];
  ofd.Filter := 'All (*.csv;*.xml;*.txt)|*.csv;'+
  '*.xml;*.txt|CSV Files (*.csv)|*.csv|TEXT Files (*.txt)|*.txt|'+
  'XML Files (*.xml)|*.xml';
  ofd.FilterIndex := 0;
  try
    {$IFNDEF WEBLIB}
    if ofd.Execute then
    {$ENDIF}
    {$IFDEF WEBLIB}
    res := await(Boolean, ofd.Perform);
    if res then
    {$ENDIF}
    begin
      for I := 0 to ofd.Files.Count - 1 do
      begin
        str := ofd.Files[I]{$IFDEF WEBLIB}.Name{$ENDIF};
        ext := ExtractFileExt(str);
        {$IFNDEF WEBLIB}
        str := ofd.Files[I];
        if UpperCase(ext) = '.CSV' then
          FGrid.LoadFromCSVData(str)
        else if UpperCase(ext) = '.TXT' then
          FGrid.LoadFromFileData(str)
        else if UpperCase(ext) = '.XML' then
          FGrid.LoadFromXMLData(str);

        {$ENDIF}
        {$IFDEF WEBLIB}
//        str := await(string, ofd.Files[I].FileAsBase);
//        str := 'data:' + ofd.Files[I].MimeType + ';base64,' + str;
//        bi.Bitmap.LoadFromResource(str);
        {$ENDIF}
      end;
    end;
  finally
    ofd.Free;
  end;
end;

procedure TTMSFNCDataGridDesigner.DoSortClick(Sender: TObject);
begin
  if Assigned(FGrid) then
  begin
    {$IFDEF FMXLIB}
    FGrid.Options.Sorting.Enabled := FSort.IsChecked;
    {$ELSE}
    FGrid.Options.Sorting.Enabled := FSort.Checked;
    {$ENDIF}
    FGrid.Width := FGrid.Width + 1;
    FGrid.Width := FGrid.Width - 1;
  end;
end;

procedure TTMSFNCDataGridDesigner.Draw(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
begin
  inherited;
  RecreateControls;

  AGraphics.DrawBitmap(FClose.BoundsRect, FCloseBitmap);
  AGraphics.DrawBitmap(FLoadSample.BoundsRect, FLoadSampleBitmap);
  AGraphics.DrawBitmap(FSort.BoundsRect, FSortBitmap);
  AGraphics.DrawBitmap(FFilter.BoundsRect, FFilterBitmap);
  AGraphics.DrawBitmap(FEdit.BoundsRect, FEditBitmap);

  if ControlAlignment in [caLeft, caRight] then
    AGraphics.DrawLine(PointF(5, 32.5), PointF(Width - 10, 32.5));
end;

function TTMSFNCDataGridDesigner.GetControlBitmap(AControl: TControl): TTMSFNCBitmap;
begin
  Result := nil;

  if Assigned(AControl) then
  begin
    {$IFDEF FMXLIB}
    if AControl is TStyledControl then
    begin
      TStyledControl(AControl).NeedStyleLookup;
      TStyledControl(AControl).ApplyStyleLookup;
    end;
    {$ENDIF}

    Result := TTMSFNCBitmap(AControl.MakeScreenShot);
  end;
end;

function TTMSFNCDataGridDesigner.HandleDesignHitTest(
  const APoint: TPoint): Boolean;
begin
  Result := True;
end;

procedure TTMSFNCDataGridDesigner.HandleMouseDown(Button: TTMSFNCMouseButton;
  Shift: TShiftState; X, Y: Single);
var
  pt: TPointF;
begin
  inherited;
  CaptureEx;

  {$IFDEF FMXLIB}
  if not FFirstMouseDown then
  begin
    FFirstMouseDown := True;
    Exit;
  end;
  {$ENDIF}

  pt := PointF(X, Y);

  if PtInRectEx(ConvertToRectF(FLoadSample.BoundsRect), pt) then
  begin
    {$IFDEF FMXLIB}
    FLoadSample.IsPressed := True;
    {$ELSE}
    {$ENDIF}
  end;

  if PtInRectEx(ConvertToRectF(FClose.BoundsRect), pt) then
  begin
    {$IFDEF FMXLIB}
    FClose.IsPressed := True;
    {$ELSE}
    {$ENDIF}
  end;

  FRecreate := True;
  Invalidate;

  {$IFDEF FMXLIB}
  FFirstMouseDown := False;
  {$ENDIF}
end;

procedure TTMSFNCDataGridDesigner.HandleMouseUp(Button: TTMSFNCMouseButton;
  Shift: TShiftState; X, Y: Single);
var
  pt: TPointF;
begin
  inherited;

  ReleaseCaptureEx;

  {$IFDEF FMXLIB}
  if not FFirstMouseUp and FFirstMouseDown then
  begin
    FFirstMouseUp := True;
    Exit;
  end;
  {$ENDIF}

  pt := PointF(X, Y);

  {$IFDEF FMXLIB}
  FLoadSample.IsPressed := False;
  FClose.IsPressed := False;
  {$ENDIF}

  if PtInRectEx(ConvertToRectF(FLoadSample.BoundsRect), pt) then
    TTMSFNCDataGridDesignerControlOpen(FLoadSample).Click;

  if PtInRectEx(ConvertToRectF(FClose.BoundsRect), pt) then
    TTMSFNCDataGridDesignerControlOpen(FClose).Click;

  if PtInRectEx(ConvertToRectF(FSort.BoundsRect), pt) then
  begin
    {$IFDEF FMXLIB}
    FSort.IsChecked := not FSort.IsChecked;
    {$ELSE}
    FSort.Checked := not FSort.Checked;
    {$ENDIF}
    TTMSFNCDataGridDesignerControlOpen(FSort).Click;
  end;

  if PtInRectEx(ConvertToRectF(FEdit.BoundsRect), pt) then
  begin
    {$IFDEF FMXLIB}
    FEdit.IsChecked := not FEdit.IsChecked;
    {$ELSE}
    FEdit.Checked := not FEdit.Checked;
    {$ENDIF}
    TTMSFNCDataGridDesignerControlOpen(FEdit).Click;
  end;

  if PtInRectEx(ConvertToRectF(FFilter.BoundsRect), pt) then
  begin
    {$IFDEF FMXLIB}
    FFilter.IsChecked := not FFilter.IsChecked;
    {$ELSE}
    FFilter.Checked := not FFilter.Checked;
    {$ENDIF}
    TTMSFNCDataGridDesignerControlOpen(FFilter).Click;
  end;

  FRecreate := True;
  Invalidate;

  {$IFDEF FMXLIB}
  FFirstMouseUp := False;
  {$ENDIF}
end;

procedure TTMSFNCDataGridDesigner.RecreateControls;
begin
  if not FRecreate then
    Exit;

  FRecreate := False;

  AddControls;

  FreeAndNil(FLoadSampleBitmap);
  FLoadSampleBitmap := GetControlBitmap(FLoadSample);

  FreeAndNil(FCloseBitmap);
  FCloseBitmap := GetControlBitmap(FClose);

  FreeAndNil(FSortBitmap);
  FSortBitmap := GetControlBitmap(FSort);

  FreeAndNil(FEditBitmap);
  FEditBitmap := GetControlBitmap(FEdit);

  FreeAndNil(FFilterBitmap);
  FFilterBitmap := GetControlBitmap(FFilter);

  RemoveControls;
end;

procedure TTMSFNCDataGridDesigner.RemoveControls;
begin
  FClose.Parent := nil;
  FLoadSample.Parent := nil;
  FSort.Parent := nil;
  FEdit.Parent := nil;
  FFilter.Parent := nil;
end;

procedure TTMSFNCDataGridDesigner.ResetToDefaultStyle;
begin
  inherited;
  FRecreate := True;
  Invalidate;
end;

procedure TTMSFNCDataGridDesigner.UpdateControlAfterResize;
begin
  inherited;
  FRecreate := True;
  Invalidate;
end;

{$ENDIF}

{ TTMSFNCDataGridHeaderVisualGrouping }

procedure TTMSFNCDataGridHeaderVisualGrouping.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridHeaderVisualGrouping then
  begin
    FSize := (Source as TTMSFNCDataGridHeaderVisualGrouping).Size;
    FAutoSize := (Source as TTMSFNCDataGridHeaderVisualGrouping).AutoSize;
    FVisible := (Source as TTMSFNCDataGridHeaderVisualGrouping).Visible;
    FSortOnClick := (Source as TTMSFNCDataGridHeaderVisualGrouping).SortOnClick;
    FCloseIndicator := (Source as TTMSFNCDataGridHeaderVisualGrouping).CloseIndicator;
    FConnectionLines := (Source as TTMSFNCDataGridHeaderVisualGrouping).ConnectionLines;
    FConnectionStroke.Assign((Source as TTMSFNCDataGridHeaderVisualGrouping).ConnectionStroke);
    Layout.Assign((Source as TTMSFNCDataGridHeaderVisualGrouping).Layout);
    FDragging := (Source as TTMSFNCDataGridHeaderVisualGrouping).Dragging;
    FLevelIndicationFill.Assign((Source as TTMSFNCDataGridHeaderVisualGrouping).LevelIndicationFill);
    FLevelIndicationStroke.Assign((Source as TTMSFNCDataGridHeaderVisualGrouping).LevelIndicationStroke);
    FLevelActiveIndicationFill.Assign((Source as TTMSFNCDataGridHeaderVisualGrouping).LevelActiveIndicationFill);
    FLevelActiveIndicationStroke.Assign((Source as TTMSFNCDataGridHeaderVisualGrouping).LevelActiveIndicationStroke);
  end;
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.DoConnectionStrokeChanged(
  Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.DoFillChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

constructor TTMSFNCDataGridHeaderVisualGrouping.Create;
begin
  FVisible := True;
  FDragging := True;
  FSize := 30;
  FAutoSize := True;
  FSortOnClick := True;
  FCloseIndicator := True;
  FConnectionLines := True;
  FLayout := TTMSFNCDataGridHeaderVisualGroupingLayout.Create(gcWhitesmoke);
  FLayout.OnChange := @DoLayoutChanged;
  FConnectionStroke := TTMSFNCGraphicsStroke.Create;
  FConnectionStroke.OnChanged := @DoConnectionStrokeChanged;

  FLevelIndicationFill := TTMSFNCGraphicsFill.Create(gfkNone);
  FLevelIndicationFill.OnChanged := @DoFillChanged;
  FLevelIndicationStroke := TTMSFNCGraphicsStroke.Create(gskDash, gcLightgray);
  FLevelIndicationStroke.OnChanged := @DoStrokeChanged;

  FLevelActiveIndicationFill := TTMSFNCGraphicsFill.Create(gfkSolid, Lighter(gcWhitesmoke, 20));
  FLevelActiveIndicationFill.OnChanged := @DoFillChanged;
  FLevelActiveIndicationStroke := TTMSFNCGraphicsStroke.Create(gskSolid, Lighter(gcWhitesmoke, 20));
  FLevelActiveIndicationStroke.OnChanged := @DoStrokeChanged;
end;

destructor TTMSFNCDataGridHeaderVisualGrouping.Destroy;
begin
  FreeAndNil(FLevelIndicationFill);
  FreeAndNil(FLevelIndicationStroke);
  FreeAndNil(FLevelActiveIndicationFill);
  FreeAndNil(FLevelActiveIndicationStroke);
  FreeAndNil(FLayout);
  FreeAndNil(FConnectionStroke);
  inherited;
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.DoLayoutChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.DoStrokeChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.SetAutoSize(const Value: Boolean);
begin
  if FAutoSize <> Value then
  begin
    FAutoSize := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.SetCloseIndicator(const Value: Boolean);
begin
  if FCloseIndicator <> Value then
  begin
    FCloseIndicator := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.SetConnectionLines(
  const Value: Boolean);
begin
  if FConnectionLines <> Value then
  begin
    FConnectionLines := Value;
    if Assigned(OnChange) then
      OnChange(Self);
  end;
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.SetConnectionStroke(
  const Value: TTMSFNCGraphicsStroke);
begin
  if FConnectionStroke <> Value then
    FConnectionStroke.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.SetLayout(
  const Value: TTMSFNCDataGridHeaderVisualGroupingLayout);
begin
  if FLayout <> Value then
    FLayout.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.SetLevelActiveIndicationFill(
  const Value: TTMSFNCGraphicsFill);
begin
  if FLevelActiveIndicationFill <> Value then
    FLevelActiveIndicationFill.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.SetLevelActiveIndicationStroke(
  const Value: TTMSFNCGraphicsStroke);
begin
  if FLevelActiveIndicationStroke <> Value then
    FLevelActiveIndicationStroke.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.SetLevelIndicationFill(
  const Value: TTMSFNCGraphicsFill);
begin
  if FLevelIndicationFill <> Value then
    FLevelIndicationFill.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.SetLevelIndicationStroke(
  const Value: TTMSFNCGraphicsStroke);
begin
  if FLevelIndicationStroke <> Value then
    FLevelIndicationStroke.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.SetSize(const Value: Integer);
begin
  if FSize <> Value then
  begin
    FSize := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

procedure TTMSFNCDataGridHeaderVisualGrouping.SetVisible(const Value: Boolean);
begin
  if FVisible <> Value then
  begin
    FVisible := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

{ TTMSFNCDataGridHeaderBar }

procedure TTMSFNCDataGridHeaderBar.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridHeaderBar then
  begin
    FVisible := (Source as TTMSFNCDataGridHeaderBar).Visible;
    FFill.Assign((Source as TTMSFNCDataGridHeaderBar).Fill);
    FStroke.Assign((Source as TTMSFNCDataGridHeaderBar).Stroke);
    FSize := (Source as TTMSFNCDataGridHeaderBar).Size;
    FButtons.Assign((Source as TTMSFNCDataGridHeaderBar).Buttons);
  end;
end;

constructor TTMSFNCDataGridHeaderBar.Create(AOwner: TPersistent);
begin
  FOwner := AOwner;
  FButtons := TTMSFNCDataGridButtons.Create(Self, TTMSFNCDataGridButton);
  FButtons.OnChanged := @DoButtonsChanged;
  FVisible := False;
  FSize := 26;
  FFill := TTMSFNCGraphicsFill.Create(gfkSolid, gcWhitesmoke);
  FStroke := TTMSFNCGraphicsStroke.Create;
  FFill.OnChanged := @DoFillChanged;
  FStroke.OnChanged := @DoStrokeChanged;
end;

destructor TTMSFNCDataGridHeaderBar.Destroy;
begin
  FButtons.Free;
  FStroke.Free;
  FFill.Free;
  inherited;
end;

procedure TTMSFNCDataGridHeaderBar.DoButtonsChanged(Sender: TObject);
begin
  if Assigned(OnChangeRebuild) then
    OnChangeRebuild(Self);
end;

procedure TTMSFNCDataGridHeaderBar.DoFillChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridHeaderBar.DoStrokeChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

function TTMSFNCDataGridHeaderBar.GetOwner: TPersistent;
begin
  Result := FOwner;
end;

procedure TTMSFNCDataGridHeaderBar.SetButtons(
  const Value: TTMSFNCDataGridButtons);
begin
  if FButtons <> Value then
    FButtons.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderBar.SetFill(
  const Value: TTMSFNCGraphicsFill);
begin
  if FFill <> Value then
    FFill.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderBar.SetSize(const Value: Integer);
begin
  if FSize <> Value then
  begin
    FSize := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

procedure TTMSFNCDataGridHeaderBar.SetStroke(
  const Value: TTMSFNCGraphicsStroke);
begin
  if FStroke <> Value then
    FStroke.Assign(Value);
end;

procedure TTMSFNCDataGridHeaderBar.SetVisible(const Value: Boolean);
begin
  if FVisible <> Value then
  begin
    FVisible := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

{ TTMSFNCDataGridHeader }

procedure TTMSFNCDataGridHeader.Assign(Source: TPersistent);
begin
  inherited;
  if Source is TTMSFNCDataGridHeader then
    FVisualGrouping.Assign((Source as TTMSFNCDataGridHeader).VisualGrouping);
end;

constructor TTMSFNCDataGridHeader.Create(AOwner: TPersistent);
begin
  inherited Create(AOwner);

  FVisualGrouping := TTMSFNCDataGridHeaderVisualGrouping.Create;
  FVisualGrouping.OnChange := @DoVisualGroupingChanged;
  FVisualGrouping.OnChangeRebuild := DoVisualGroupingChangedRebuild;

  FBar := TTMSFNCDataGridHeaderBar.Create(AOwner);
  FBar.OnChange := @DoBarChanged;
  FBar.OnChangeRebuild := DoBarChangedRebuild;
end;

destructor TTMSFNCDataGridHeader.Destroy;
begin
  FVisualGrouping.Free;
  FBar.Free;
  inherited;
end;

procedure TTMSFNCDataGridHeader.DoBarChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridHeader.DoBarChangedRebuild(Sender: TObject);
begin
  if Assigned(OnChangeRebuild) then
    OnChangeRebuild(Self);
end;

procedure TTMSFNCDataGridHeader.DoVisualGroupingChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridHeader.DoVisualGroupingChangedRebuild(Sender: TObject);
begin
  if Assigned(OnChangeRebuild) then
    OnChangeRebuild(Self);
end;

procedure TTMSFNCDataGridHeader.SetVisualGrouping(
  const Value: TTMSFNCDataGridHeaderVisualGrouping);
begin
  FVisualGrouping.Assign(Value);
end;

procedure TTMSFNCDataGridHeader.SetBar(
  const Value: TTMSFNCDataGridHeaderBar);
begin
  FBar.Assign(Value);
end;

{ TTMSFNCDataGridHeaderGroup }

constructor TTMSFNCDataGridHeaderGroup.Create;
begin
  FNew := False;
  FScale := 1.0;
  FActive := False;
  FSortIndicator := gsiNone;
  FCloseIndicator := True;
end;

procedure TTMSFNCDataGridHeaderGroup.DrawBackground(AGraphics: TTMSFNCGraphics);
begin
  AGraphics.DrawRoundRectangle(Rect, 6);
end;

destructor TTMSFNCDataGridHeaderGroup.Destroy;
begin
  FreeAndNil(FLayout);
  FSortDescendingIcon := nil;
  FSortAscendingIcon := nil;
  FCloseIcon := nil;
  inherited;
end;

procedure TTMSFNCDataGridHeaderGroup.Draw(AGraphics: TTMSFNCGraphics);
var
  b: Boolean;
begin
  AGraphics.SetFill(Layout.Fill);
  AGraphics.SetStroke(Layout.Stroke);
  AGraphics.SetFont(Layout.Font);

  if Active then
  begin
    AGraphics.Fill.Color := Lighter(AGraphics.Fill.Color, 50);
    AGraphics.Fill.ColorTo := Lighter(AGraphics.Fill.Color, 50);
    AGraphics.Font.Color := Lighter(AGraphics.Font.Color, 50);
    AGraphics.Stroke.Color := Lighter(AGraphics.Stroke.Color, 50);
  end;

  b := True;
  DoBeforeDraw(AGraphics, b);
  if b then
  begin
    DrawBackground(AGraphics);
    DrawContent(AGraphics);
    DoAfterDraw(AGraphics);
  end;
end;

procedure TTMSFNCDataGridHeaderGroup.DrawContent(AGraphics: TTMSFNCGraphics);
begin
  DrawText(AGraphics);

  if (SortIndicator <> gsiNone) then
    DrawSortIndicator(AGraphics);

  if CloseIndicator then
    DrawCloseIndicator(AGraphics);
end;

procedure TTMSFNCDataGridHeaderGroup.DrawSortIndicator(AGraphics: TTMSFNCGraphics);
var
  bmp: TTMSFNCBitmap;
  bmpr: TRectF;
  st: TTMSFNCGraphicsSaveState;
begin
  inherited;

  bmp := nil;
  case SortIndicator of
    gsiAscending: bmp := SortAscendingIcon;
    gsiDescending: bmp := SortDescendingIcon;
  end;

  bmpr := GetSortIndicatorRect;
  if Assigned(bmp) then
  begin
    st := AGraphics.SaveState;
    try
      AGraphics.DrawBitmap(bmpr, bmp);
    finally
      AGraphics.RestoreState(st);
    end;
  end;
end;

procedure TTMSFNCDataGridHeaderGroup.DrawCloseIndicator(AGraphics: TTMSFNCGraphics);
var
  bmp: TTMSFNCBitmap;
  bmpr: TRectF;
  st: TTMSFNCGraphicsSaveState;
begin
  inherited;

  bmp := CloseIcon;
  bmpr := GetCloseIndicatorRect;
  if Assigned(bmp) then
  begin
    st := AGraphics.SaveState;
    try
      AGraphics.ClipRect(bmpr);
      AGraphics.DrawBitmap(bmpr, bmp);
    finally
      AGraphics.RestoreState(st);
    end;
  end;
end;

procedure TTMSFNCDataGridHeaderGroup.DoAfterDraw(AGraphics: TTMSFNCGraphics);
begin
  if Assigned(OnAfterDraw) then
    OnAfterDraw(Self, AGraphics);
end;

procedure TTMSFNCDataGridHeaderGroup.DoBeforeDraw(AGraphics: TTMSFNCGraphics;
  var ACanDraw: Boolean);
begin
  if Assigned(OnBeforeDraw) then
    OnBeforeDraw(Self, AGraphics, ACanDraw);
end;

procedure TTMSFNCDataGridHeaderGroup.DrawText(AGraphics: TTMSFNCGraphics);
var
  dr: TRectF;
  s: string;
  st: TTMSFNCGraphicsSaveState;
begin
  dr := GetTextRect;
  s := Text;
  if s <> '' then
  begin
    st := AGraphics.SaveState;
    try
      AGraphics.ClipRect(dr);
      AGraphics.DrawText(dr, s, Layout.WordWrapping, Layout.TextAlign, Layout.VerticalTextAlign,
        Layout.Trimming, Layout.TextAngle{, -1, -1, not DisplayHTMLFormatting});
    finally
      AGraphics.RestoreState(st);
    end;
  end;
end;

function TTMSFNCDataGridHeaderGroup.GetCloseIndicatorRect: TRectF;
var
  sz: Single;
begin
  Result := RectF(Rect.Left, Rect.Top, Rect.Left, Rect.Bottom);
  if CloseIndicator then
  begin
    sz := GetCloseIndicatorSize;
    Result := RectF(Result.Left + (2 * FScale), Result.Top + (Result.Bottom - Result.Top) / 2 - sz / 2, Result.Left + sz + ( 4 * FScale), Result.Top + (Result.Bottom - Result.Top) / 2 + sz / 2);
  end
  else
    Result := RectF(Result.Left, Result.Top, Result.Left, Result.Bottom);
end;

function TTMSFNCDataGridHeaderGroup.GetCloseIndicatorSize: Single;
begin
  Result := 16 * FScale;
end;

function TTMSFNCDataGridHeaderGroup.GetLayout: TTMSFNCDataGridCellLayout;
begin
  if not Assigned(FLayout) then
    FLayout := TTMSFNCDataGridCellLayout.Create(gcWhitesmoke);

  Result := FLayout;
end;

function TTMSFNCDataGridHeaderGroup.GetRect: TRectF;
begin
  if Active then
    Result := FOrigRect
  else
    Result := FRect;
end;

function TTMSFNCDataGridHeaderGroup.GetSortIndicatorRect: TRectF;
var
  sz: Single;
begin
  Result := RectF(Rect.Right, Rect.Top, Rect.Right, Rect.Bottom);
  if (SortIndicator <> gsiNone) then
  begin
    sz := GetSortIndicatorSize;
    Result := RectF(Result.Left - sz -( 4 * FScale), Result.Top + (Result.Bottom - Result.Top) / 2 - sz / 2, Result.Left - (2 * FScale), Result.Top + (Result.Bottom - Result.Top) / 2 + sz / 2);
  end
  else
    Result := RectF(Result.Left, Result.Top, Result.Left, Result.Bottom);
end;

function TTMSFNCDataGridHeaderGroup.GetSortIndicatorSize: Single;
begin
  Result := 16 * FScale;
end;

function TTMSFNCDataGridHeaderGroup.GetTextRect: TRectF;
var
  r, sr, cr, res: TRectF;
begin
  sr := GetSortIndicatorRect;
  cr := GetCloseIndicatorRect;
  r := RectF(cr.Right, Rect.Top, sr.Left, Rect.Bottom);
  res := r;
  InflateRectEx(res, -2 * FScale, -2 * FScale);
  Result := res;
end;

procedure TTMSFNCDataGridHeaderGroup.Reset;
begin
  FRect := FOrigRect;
end;

function TTMSFNCDataGridHeaderGroup.XYToCloseIndicator(X, Y: Single): Boolean;
begin
  Result := False;
  if CloseIndicator then
    Result := PtInRectEx(CloseIndicatorRect, PointF(X, Y));
end;

{ TTMSFNCDataGridHeaderGroups }

function TTMSFNCDataGridHeaderGroups.Find(AColumn: Integer;
  var AHeaderGroup: TTMSFNCDataGridHeaderGroup): Boolean;
var
  I: Integer;
begin
  Result := False;
  AHeaderGroup := nil;
  for I := 0 to Count - 1 do
  begin
    if Items[I].Column = AColumn then
    begin
      AHeaderGroup := Items[I];
      Result := True;
      Break;
    end;
  end;
end;

{ TTMSFNCDataGridHeaderVisualGroupingLayout }

constructor TTMSFNCDataGridHeaderVisualGroupingLayout.Create(
  ADefaultFillColor: TTMSFNCGraphicsColor);
begin
  inherited Create(ADefaultFillColor);
  inherited TextAlign := gtaCenter;
end;

function TTMSFNCDataGridHeaderVisualGroupingLayout.GetTextAlign: TTMSFNCGraphicsTextAlign;
begin
  Result := inherited TextAlign;
end;

procedure TTMSFNCDataGridHeaderVisualGroupingLayout.SetTextAlign(
  const Value: TTMSFNCGraphicsTextAlign);
begin
  inherited TextAlign := Value;
end;

{ TTMSFNCDataGridButton }

procedure TTMSFNCDataGridButton.DoBitmapChanged(Sender: TObject);
begin
  DoChanged;
end;

procedure TTMSFNCDataGridButton.DoChanged;
begin
  if (Collection is TTMSFNCDataGridButtons) then
    (Collection as TTMSFNCDataGridButtons).DoChanged;
end;

procedure TTMSFNCDataGridButton.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridButton then
  begin
    FName := (Source as TTMSFNCDataGridButton).Name;
    FHint := (Source as TTMSFNCDataGridButton).Hint;
    FText := (Source as TTMSFNCDataGridButton).Text;
    FBitmap.Assign((Source as TTMSFNCDataGridButton).Bitmap);
    FWidth := (Source as TTMSFNCDataGridButton).Width;
    FEnabled := (Source as TTMSFNCDataGridButton).Enabled;
    FVisible := (Source as TTMSFNCDataGridButton).Visible;
  end;
end;

constructor TTMSFNCDataGridButton.Create(ACollection: TCollection);
begin
  inherited;
  FName := 'Button' + IntToStr(Collection.Count);
  FBitmap := TTMSFNCBitmap.Create;
  FBitmap.OnChange := @DoBitmapChanged;

  FHint := '';
  FText := '';
  FEnabled := True;
  FVisible := True;
  FWidth := 30;
end;

destructor TTMSFNCDataGridButton.Destroy;
begin
  FControl := nil;
  FreeAndNil(FBitmap);
  FreeAndNil(FButton);
  inherited;
end;

function TTMSFNCDataGridButton.GetButton: TTMSFNCToolBarButton;
begin
  if not Assigned(FButton) then
  begin
    FButton := TTMSFNCToolBarButton.Create(nil);
    FButton.Stored := False;
    FButton.Appearance.FlatStyle := True;
    FButton.Text := '';
    FButton.Appearance.NormalFill.Color := gcWhite;
    FButton.Appearance.NormalStroke.Color := gcWhite;
    FButton.ShowHint := True;
  end;

  Result := FButton;
end;

function TTMSFNCDataGridButton.GetDisplayName: string;
begin
  if FName <> '' then
    Result := FName
  {$IFDEF WEBLIB}
  else
    Result := '';
  {$ENDIF}
  {$IFNDEF WEBLIB}
  else
    Result := inherited;
  {$ENDIF}
end;

function TTMSFNCDataGridButton.IsHintStored: Boolean;
begin
  Result := Hint <> '';
end;

function TTMSFNCDataGridButton.IsTextStored: Boolean;
begin
  Result := Text <> '';
end;

function TTMSFNCDataGridButton.IsWidthStored: Boolean;
begin
  Result := Width <> 30;
end;

procedure TTMSFNCDataGridButton.SetBitmap(const Value: TTMSFNCBitmap);
begin
  if FBitmap <> Value then
    FBitmap.Assign(Value);
end;

procedure TTMSFNCDataGridButton.SetControl(
  const Value: TTMSFNCDataGridCellControl);
begin
  if Value = nil then
  begin
    if Assigned(FControl) then
      FControl.Visible := True;
  end;

  FControl := Value;
  if Assigned(FControl) then
  begin
    FreeAndNil(FButton);
    FControl.Visible := False;
    Width := FControl.Width;
  end;
end;

procedure TTMSFNCDataGridButton.SetEnabled(const Value: Boolean);
begin
  if FEnabled <> Value then
  begin
    FEnabled := Value;
    DoChanged;
  end;
end;

procedure TTMSFNCDataGridButton.SetHint(const Value: string);
begin
  if FHint <> Value then
  begin
    FHint := Value;
    DoChanged;
  end;
end;

procedure TTMSFNCDataGridButton.SetText(const Value: string);
begin
  if FText <> Value then
  begin
    FText := Value;
    DoChanged;
  end;
end;

procedure TTMSFNCDataGridButton.SetVisible(const Value: Boolean);
begin
  if FVisible <> Value then
  begin
    FVisible := Value;
    DoChanged;
  end;
end;

procedure TTMSFNCDataGridButton.SetWidth(const Value: Single);
begin
  if FWidth <> Value then
  begin
    FWidth := Value;
    DoChanged;
  end;
end;

{ TTMSFNCDataGridButtons }

function TTMSFNCDataGridButtons.Add: TTMSFNCDataGridButton;
begin
  Result := TTMSFNCDataGridButton(inherited Add);
end;

procedure TTMSFNCDataGridButtons.DoChanged;
begin
  if Assigned(OnChanged) then
    OnChanged(Self);
end;

function TTMSFNCDataGridButtons.GetItemEx(Index: Integer): TTMSFNCDataGridButton;
begin
  Result := TTMSFNCDataGridButton(inherited Items[Index]);
end;

function TTMSFNCDataGridButtons.Insert(index: Integer): TTMSFNCDataGridButton;
begin
  Result := TTMSFNCDataGridButton(inherited Insert(Index));
end;

procedure TTMSFNCDataGridButtons.SetItemEx(Index: Integer; const Value: TTMSFNCDataGridButton);
begin
  inherited SetItem(Index, Value);
end;

initialization
begin
  {$IFDEF WEBLIB}
  TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDCLOSE);
  {$ENDIF}
end;

end.
